import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { isCN } from './lib/debug-mode';

// Knox i18n resources for the 'knox-aiops' namespace.
const KNOX_RESOURCES_EN: Record<string, Record<string, unknown>> = {
  'knox-aiops': {
    nav: {
      brand: '{{brand}} AI-Native AIOps',
      triageInbox: 'Triage Inbox',
      members: 'Members',
      selectRoom: 'Select a room to start',
      settings: 'Settings',
      signOut: 'Sign Out',
      support: 'Support',
      lightMode: 'Light Mode',
      darkMode: 'Dark Mode',
      expandSidebar: 'Expand sidebar',
      collapseSidebar: 'Collapse sidebar',
      newRoom: 'New Room',
      inbox: 'Inbox',
      inboxPending: 'pending items',
      knowledge: 'Knowledge',
      searchRooms: 'Search rooms',
      searchRoomsAria: 'Search rooms',
      clearSearch: 'Clear search',
      rooms: 'Rooms',
      backToInbox: 'Back to Inbox',
      noRoomsFound: 'No rooms found',
      noRoomsYet: 'No rooms yet',
      createFirstRoom: 'Create your first room',
      showingRoomsOf: 'Showing {{shown}} of {{total}} rooms — use search to find more',
      agents: '{{brand}} Daemon',
    },
    common: {
      yes: 'Yes',
      no: 'No',
      edit: 'Edit',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving...',
    },
    legal: {
      termsTitle: 'Terms of Service',
      privacyTitle: 'Privacy Policy',
      refundTitle: 'Refund Policy',
      back: '← Back',
      copyright: '© {{year}} {{company}}. All rights reserved.',
    },
    semanticPanel: {
      viewpoint: { business: '应用系统', infra: '基础设施', events: '事件与变更', query: '查询' },
      breadcrumb: { appSystems: '应用系统' },
      close: '关闭网络视图',
      error: { parseViewSpec: '无法解析视图参数', loadFailed: '加载失败', unsupportedLayout: '不支持的视图布局' },
      retry: '重试',
      tab: { OS: 'OS', K8S: 'K8S', storage: '存储', middleware: '中间件', config: '配置', network: '网络', ingress: '入口', incident: '事件', problem: '问题', serviceRequest: '服务请求', changeRequest: '变更' },
      entityType: { OS: '操作系统', CloudVM: '云主机', PhysicalServer: '物理机', K8sWorkload: '工作负载', CloudDisk: '云盘', StorageBucket: '存储桶', CloudRDS: '云数据库', CloudCache: '云缓存', ArtifactInst: '自建中间件', ConfigFile: '配置文件', Secret: '密钥', CloudVPC: 'VPC', CloudSubnet: '子网', CloudSecurityGroup: '安全组', CloudWAF: 'WAF', CloudDDS: '文档数据库', CloudDWS: '数据仓库', CloudCSS: '云搜索', Domain: '域名', LoadBalancer: '负载均衡', K8sCluster: 'K8s集群', K8sNamespace: '命名空间', CronJob: '定时任务', InspectionTask: '巡检任务', Incident: '事件', Problem: '问题', ServiceRequest: '服务请求', ChangeRequest: '变更请求' },
      subtypeLabel: '子类型：',
      noTabs: '无可用 tab',
      filterBy: '已按 {{name}} 过滤',
      clearFilter: '× 清除过滤',
      table: { search: '搜索...', searchAria: '搜索', loading: '加载中...', noData: '暂无数据', unknownType: '未知资源类型', totalCount: '共 {{count}} 条', pagination: { prev: '上一页', next: '下一页', range: '第 {{start}}-{{end}} 条 / 共 {{total}} 条' } },
      cards: { noIslands: 'No Applications', emptyBusinessTitle: 'No applications found', emptyBusinessHint: 'Click the button below to start an infrastructure discovery', emptyBusinessCommand: 'Run Discovery', emptyBusinessSendCommand: 'Run a discovery report on my infrastructure' },
      criticality: { critical: '严重', high: '高', medium: '中', low: '低' },
      toolbar: { envLabel: '环境', allEnv: '所有环境', domainLabel: '域名' },
      detail: { title: '实体详情', properties: '属性', relationships: '关联关系', noRelationships: '无关联关系', loading: '加载关系中...', loadError: '加载关系失败', retry: '重试', source: '源', target: '目标', clickToExplore: '点击查看详情', home: '首页', breadcrumbLabel: '面包屑导航', back: '返回上一级', close: '关闭详情', expandAll: 'Expand all ({{count}})', collapse: 'Collapse' },
      columns: { name: '名称', instanceName: '实例名', hostname: '主机名', osType: '操作系统', kernelVersion: '内核版本', cpuCores: 'CPU核数', memoryGb: '内存(GB)', provider: '云平台', dnsProvider: 'DNS服务商', instanceType: '规格', instanceClass: '实例规格', privateIp: '内网IP', status: '状态', model: '型号', workloadType: '类型', namespace: '命名空间', replicas: '副本数', cpuRequest: 'CPU请求', memoryRequest: '内存请求', clusterName: '集群', diskType: '磁盘类型', sizeGb: '大小(GB)', attachedTo: '挂载到', bucketName: 'Bucket名称', region: '区域', engine: '引擎', engineVersion: '引擎版本', endpoint: '端点', nodeType: '节点类型', path: 'Path', filePath: '路径', fileName: '文件名', format: '格式', sizeBytes: '大小(字节)', vpcId: 'VPC ID', cidr: 'CIDR', subnetId: '子网ID', zone: '可用区', sgId: '安全组ID', rulesCount: '规则数', fqdn: 'FQDN', lbType: 'LB类型', dnsName: 'DNS名称', deploymentType: '部署方式', version: '版本', installPath: '安装路径', softwareType: '引擎', serviceCategory: '服务类型', listenPorts: '监听端口', osVersion: '系统版本', processSignature: '进程标识', createdAt: '创建时间', updatedAt: '更新时间', processes: '进程列表', podName: 'Pod名称', secretType: '类型', title: '标题', severity: '严重度', priority: '优先级', impact: '影响', urgency: '紧急度', rootCauseSummary: '根因摘要', workaround: '临时方案', requestType: '请求类型', changeType: '变更类型', riskLevel: '风险等级', rollbackPlan: '回滚方案', storageGb: '存储(GB)' },
    },
    login: {
      signIn: 'Sign in to {{brand}}',
      subtitle: 'Your AI-powered IT crew is ready',
      invitationCode: 'Have an invitation code?',
      invitationCodeLabel: 'Invitation Code',
      invitationCodePlaceholder: 'Enter your code',
      go: 'Go',
      copyright: '© 2026 {{brand}}',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      or: 'or',
      refundPolicy: 'Refund Policy',
      oauthError: {
        KNOX_AUTH_OAUTH_NO_ORG: 'Your account is not associated with any organization. Please register with an invitation code first.',
        KNOX_AUTH_OAUTH_NO_ELEVO_USER: 'Please sign in with your email and password once before using OAuth login.',
        KNOX_AUTH_OAUTH_FAILED: 'OAuth sign-in failed. Please try again.',
        KNOX_BAD_REQUEST: 'Unsupported OAuth provider.',
        KNOX_INTERNAL_ERROR: 'Server error. Please try again later.',
        unknown: 'Sign-in failed. Please try again.',
      },
    },
    loginForm: {
      email: 'Email Address',
      emailPlaceholder: 'name@company.com',
      change: 'Change',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      continue: 'Continue',
      signingIn: 'Signing in...',
      signIn: 'Sign in',
      checkingEmail: 'Checking...',
      setPasswordTitle: 'Set Your Password',
      setPasswordSubtitle: 'Add a password to enable email login.',
      registerTitle: 'Create your account',
      registerSubtitle: 'Set up your name and password to get started.',
      name: 'Full Name',
      namePlaceholder: 'Your full name',
      createPassword: 'Create Password',
      passwordRules: '12+ characters, at least 2 of: letters, numbers & symbols',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Re-enter your password',
      signingUp: 'Creating account...',
      signUp: 'Create Account',
      settingPassword: 'Setting password...',
      setPassword: 'Set Password',
      forgotPassword: 'Forgot password?',
      verifyEmailTitle: 'Check Your Email',
      verifyEmailSentTo: "We've sent a verification code to",
      verifyEmailSubtitle: "We've sent a 6-digit verification code to your email address.",
      verifying: 'Verifying...',
      resendCode: 'Resend code',
      resendIn: 'Resend in {{seconds}}s',
    },
    forgotPassword: {
      title: 'Forgot Password',
      subtitle: 'Enter your email address and we\'ll send you a password reset link.',
      send: 'Send Reset Link',
      sending: 'Sending...',
      checkEmail: 'Check Your Email',
      checkEmailDescription: 'If that email is registered, you\'ll receive a password reset email.',
      backToLogin: 'Back to Login',
      resendIn: 'Resend in {{seconds}}s',
      resend: 'Resend email',
    },
    resetPassword: {
      title: 'Set New Password',
      verifying: 'Verifying link...',
      linkExpired: 'Link Expired',
      linkExpiredDescription: 'This password reset link has expired or been used. Please request a new one.',
      requestNewLink: 'Request a new reset link',
      for: 'Set new password for {{email}}',
      passwordMismatch: 'Passwords do not match',
      resetting: 'Resetting...',
      reset: 'Reset Password',
    },
    oauth: {
      continueWithGoogle: 'Sign in with Google',
      continueWithGitHub: 'Sign in with GitHub',
      continueWithDingTalk: 'Sign in with DingTalk',
      continueWithWeChat: 'Sign in with WeChat',
      continueWithWeCom: 'Sign in with WeCom',
      continueWithFeishu: 'Sign in with Feishu',
      // continueWithMicrosoft: 'Sign in with Microsoft',  // SSO 未测试，暂不启用
    },
    register: {
      noToken: 'No invitation token provided',
      expired: 'This invitation has expired',
      invalid: 'Invalid or expired invitation',
      failed: 'Registration failed',
      loginFailed: 'Login failed',
      invitationNotFound: 'Invitation Not Found',
      goToLogin: 'Go to Login',
      createYourTeam: 'Create Your Team',
      setupOrg: 'Set up your organization to get started',
      daysLicense: '{{days}} days license',
      upToMembers: 'Up to {{count}} members',
      checking: 'Checking...',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Your full name',
      createPassword: 'Create a password',
      passwordRules: '12+ characters, at least 2 of: letters, numbers & symbols',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Re-enter your password',
      next: 'Next',
      orgName: 'Organization Name',
      orgNamePlaceholder: 'Your company name',
      orgSlug: 'Organization Slug',
      orgSlugPlaceholder: 'your-company',
      orgSlugHint: 'Used in your organization URL',
      back: 'Back',
      creating: 'Creating...',
      createOrg: 'Create Organization',
    },
    createRoom: {
      title: 'Create Room',
      description: 'Create a room to collaborate with AI on specific tasks.',
      nameLabel: 'Room name',
      namePlaceholder: 'e.g. prod-outage',
      descriptionLabel: 'Description',
      descriptionPlaceholder: "What's this room about?",
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Room',
      failed: 'Failed to create room. Please try again.',
    },
    room: {
      backToInbox: 'Back to Inbox',
      backToRoomList: 'Back to room list',
      commands: {
        stopDescription: 'Stop all running AI agents',
        clearDescription: 'Clear AI session — /clear @bot or /clear (all)',
        updateMemoryDescription: 'Update Room Memory',
      },
      roomDesk: 'Knowledge',
      semanticNetwork: 'Semantic Network',
      jumpToTime: 'Jump to Time',
      jumpToTimeTime: 'Time',
      jumpToTimeDate: 'Date',
      jumpToTimePreset: 'Preset',
      jumpToTimeToday: 'Today',
      jumpToTimeYesterday: 'Yesterday',
      jumpToTimeBeginning: 'Beginning',
      jumpToTimeGo: 'Go',
      jumpToTimeError: 'Failed to resolve date',
      leaveRoom: 'Leave Room',
      pinnedMessages: 'Pinned Messages',
      noPinnedMessages: 'No pinned messages',
      confirmLeave: 'Are you sure you want to leave this room?',
      leaveFailed: 'Failed to leave room!',
      leaving: 'Leaving...',
      leave: 'Leave',
      deleteRoom: 'Delete Room',
      confirmDeleteRoom: 'Deleting this room removes it for all members and cannot be undone.',
      deleteRoomFailed: 'Failed to delete room!',
      deletingRoom: 'Deleting...',
      deleteRoomConfirm: 'Delete Room',
      members: {
        title: 'Members',
        searchPlaceholder: 'Search members',
        clearSearch: 'Clear search',
        typing: 'typing',
      },
      inviteUsers: 'Invite Users',
      searchUsers: 'Search by name or ID...',
      noUsersFound: 'No users found',
      joined: 'Joined',
      messageDeleted: 'This message has been deleted',
      markAsRead: 'Mark as Read',
      invite: 'Invite',
      hideMembers: 'Hide Members',
      showMembers: 'Show Members',
      hideMembersTasks: 'Collapse members and tasks sidebar',
      showMembersTasks: 'Expand members and tasks sidebar',
      moreOptions: 'More Options',
      dateToday: 'Today',
      dateYesterday: 'Yesterday',
      stateEvent: {
        joined: '{{name}} joined the room',
        left: '{{name}} left the room',
        removed: '{{sender}} removed {{name}}',
        invited: '{{sender}} invited {{name}}',
        banned: '{{sender}} banned {{name}}',
        knock: '{{name}} requested to join',
        updatedMembership: '{{name}} updated membership',
        roomNameChanged: '{{sender}} changed the room name to "{{name}}"',
        roomNameRemoved: '{{sender}} removed the room name',
        topicChanged: '{{sender}} changed the topic',
        topicRemoved: '{{sender}} removed the topic',
        avatarChanged: '{{sender}} changed the room avatar',
      },
      thread: {
        title: 'Thread',
        reply: 'reply',
        replies: 'replies',
        noRepliesYet: 'No replies yet',
        backToChat: 'Back to chat',
        close: 'Close',
      },
      input: {
        placeholder: 'Type a message...',
        aiGeneratedNotice: 'Content is generated by AI. Please use it with discretion.',
        stopGenerating: 'Stop generating',
        sendMessage: 'Send message',
      },
      attachment: {
        open: 'Attach file',
        dropHint: 'Drop files here, or click to browse',
        supportedTypes: 'Upload files · Max 10 MB each · Same-name files are kept separately',
        unsupportedType: 'Unsupported file type',
        fileTooLarge: 'File too large (max 10 MB)',
        uploading: 'Uploading',
        ready: 'Ready',
        failed: 'Upload failed',
      },
      knowledgeReference: {
        remove: 'Remove knowledge reference',
      },
      readiness: {
        setupFailed: 'Room setup failed',
        setupDegraded: 'Room setup degraded',
        unsupported: 'Room unsupported',
        bindingPlugin: 'Binding plugin',
        waitingForBots: 'Waiting for bots',
        initializing: 'Room initializing',
        ready: 'Room ready',
        chatUnavailable: 'Chat unavailable',
        chatUnavailableHint: 'Wait until room setup finishes before chatting.',
      },
      readinessBadge: {
        binding: 'Binding',
        waiting: 'Waiting',
        failed: 'Failed',
        degraded: 'Degraded',
        unsupported: 'Unsupported',
        initializing: 'Initializing',
      },
    },
    error: {
      loginFailed: 'Login failed',
      registrationFailed: 'Registration failed',
      retrySetupFailed: 'Retry setup failed. Please try again.',
    },
    metrics: {
      title: '{{brand}} Canvas',
    },
    inbox: {
      title: 'Inbox',
      closeAriaLabel: 'Close inbox',
      openAiChat: 'Open AI chat',
      loading: 'Loading Inbox',
      error: 'Unable to load Inbox',
      filter: {
        open: 'Pending',
        resolved: 'Resolved',
        all: 'All',
      },
      empty: {
        openTitle: 'All Clear',
        openDescription: 'No pending items',
        otherTitle: 'No Records',
        otherDescription: 'No resolved items yet',
      },
      card: {
        resolved: 'Resolved',
        dismissed: 'Dismissed',
        details: 'Details',
        creatingRoom: 'Creating...',
        processing: 'Processing...',
      },
      action: {
        createRoom: 'Create Room',
        dismiss: 'Dismiss',
        accept: 'Accept',
        reject: 'Reject',
        markRead: 'Mark as Read',
        startOnboarding: 'Get Started',
        dismissOnboarding: 'Skip',
        connectors: 'Add Connectors',
        inviteMembers: 'Invite Members',
        viewTask: 'View execution',
      },
      toast: {
        accepted: 'Accepted',
        dismissed: 'Dismissed',
        ignored: 'Ignored',
        rejected: 'Invitation declined',
        roomCreated: 'Room created',
        executionRoomUnavailable: 'The execution room was deleted or is no longer available',
      },
      onboarding: {
        addConnectors: 'Add Connectors',
        addConnectorsSummary: 'Connect your first server or cloud account so {{brand}} can manage your infrastructure',
        runAudit: 'Run Baseline Audit',
        runAuditSummary: 'Perform a first audit on connected resources to understand your infrastructure',
        inviteMembers: 'Invite Team Members',
        inviteMembersSummary: 'Invite colleagues to join your organization and collaborate',
        createRoom: 'Create Collaboration Room',
        createRoomSummary: 'Create your first team room to start collaborating',
      },
      scheduledTask: {
        completed: 'Scheduled task completed',
        failed: 'Scheduled task failed',
        timeout: 'Scheduled task timed out',
        titleWaitingHuman: 'Scheduled task needs confirmation',
        titleWaitingApproval: 'Scheduled task needs approval',
        summarySuccess: 'Scheduled task “{{name}}” completed successfully',
        summaryFailed: 'Scheduled task “{{name}}” failed: {{error}}',
        summaryTimeout: 'Scheduled task “{{name}}” timed out: {{error}}',
        summaryWaitingHuman: 'Scheduled task “{{name}}” is waiting for your confirmation',
        summaryWaitingApproval: 'Scheduled task “{{name}}” is waiting for approval',
      },
    },
    scheduledTasks: {
      title: 'Scheduled tasks', subtitle: 'Run prompts automatically and record execution results', create: 'New task', createSubmit: 'Create task', edit: 'Edit task', search: 'Search tasks', searchPlaceholder: 'Search task name or prompt...', loading: 'Loading...', empty: 'No matching tasks', save: 'Save', cancel: 'Cancel', name: 'Task name', prompt: 'Prompt', expandPrompt: 'Expand', collapsePrompt: 'Collapse', scheduleType: 'Execution plan', timeout: 'Timeout', timeoutMinutes: 'Timeout (minutes)', notifyOnDone: 'Receive task notifications', runNow: 'Run now', pause: 'Pause', resume: 'Enable', delete: 'Delete', running: 'Enabled', paused: 'Paused', frequency: 'Frequency', nextRun: 'Next run', nextRunShort: 'Next', dueSoon: 'Due shortly', detail: 'Task details', minutes: 'minutes', hours: 'hours', intervalHours: 'Every {{count}} hours', intervalMinutes: 'Every {{count}} minutes', deleteTitle: 'Delete scheduled task', deleteDescription: 'Delete “{{name}}”? The task stops running and disappears from the list, while its room and history remain.', confirmDeleteButton: 'Delete',
      createTitle: 'New scheduled task',
      scheduleLabel: 'Scheduled task',
      pickerConfirm: { title: 'Overwrite current prompt', description: 'This will overwrite the current prompt. Continue?', confirm: 'Overwrite', cancel: 'Cancel', detail: 'Details' },
      toolPickerMeta: { userHint: 'Scheduled tasks run unattended. Besides the fields above, include connectors, target resources, and business parameters in the prompt; do not rely on questions during execution.', noParameters: 'This tool has no input parameters.' },
      creator: { me: 'Me', member: 'Organization member' },
      filter: { allStatus: 'All statuses', status: 'Status filter', creator: 'Creator filter', allCreators: 'All creators', searchCreator: 'Search creators' },
      error: { load: 'Unable to load tasks', create: 'Unable to create task', update: 'Unable to update task', run: 'Unable to run task', delete: 'Unable to delete task' },
      drawer: { title: 'Scheduled tasks', close: 'Close scheduled tasks', boundTask: 'Task in this room', moreActions: 'More actions', allTasks: 'All scheduled tasks', nextRun: 'Next run', timeout: 'Timeout', history: 'Execution history', deleteTitle: 'Delete scheduled task?', deleteDescription: 'Delete “{{name}}”? Its room and execution history will remain.', confirmDelete: 'Delete', emptyTitle: 'No scheduled task for this room', emptyDescription: 'The task will bind to this room and run here on schedule.', create: 'New task' },
      form: { name: 'Task name', namePlaceholder: 'For example: daily standup reminder', prompt: 'Prompt', promptPlaceholder: 'Describe what AI should do in natural language.', schedule: 'Execution plan', insertSkill: 'Insert Skill', insertTool: 'Insert tool', promptHint: 'Scheduled tasks trigger on schedule, but some Skills or high-impact tools may still require human confirmation. Specify the Skill/tool, connector, target resource, and business parameters in the prompt to reduce follow-up questions during execution.', weekday: 'Day of week', weeklyPrefix: 'Weekly ', monthDay: 'Day of month', daySuffix: '', time: 'Execution time', onceAt: 'Execution time', selectTime: 'Select execution time', am: 'AM', pm: 'PM', hour: 'Hour', minute: 'Minute', done: 'Done', every: 'Every', intervalValue: 'Interval value', intervalUnit: 'Interval unit', localTimezone: 'Local timezone', advancedOptions: 'Advanced options', timeoutHint: 'Execution stops automatically after this duration (1-30 minutes).', notifyTitle: 'Execution result notifications', notifyHint: 'After a task runs, send an Inbox reminder for the statuses you select. Scheduled runs notify the creator; manual runs notify the person who started the run.', notifySuccess: 'Execution succeeded', notifyFailed: 'Execution failed', notifyTimeout: 'Execution timed out', notifyWaitingHuman: 'Waiting for your confirmation' },
      schedule: { once: 'No repeat', daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', interval: 'Interval' },
      weekday: { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' },
      picker: { overwritePrompt: 'This will overwrite the current prompt. Continue?' },
      skillPicker: { title: 'Select Skill', search: 'Search Skills...', loadError: 'Unable to load available skills', empty: 'No matching skills' },
      toolPicker: { title: 'Select tools', search: 'Search tools...', loadError: 'Unable to load tools', empty: 'No matching tools', parameters: 'Parameters', chooseHint: '← Select a tool on the left first', select: 'Select', insert: 'Insert into prompt', required: 'Enter required parameters', invalidInteger: 'Enter a valid integer', invalidNumber: 'Enter a valid number', invalidBoolean: 'Enter a valid boolean', invalidArray: 'Enter a valid JSON array', invalidObject: 'Enter a valid JSON object', invalidValue: 'Invalid parameter value' },
      history: { empty: 'No executions yet', viewDetails: 'View details' }, paginationSummary: '{{total}} total · page {{page}} of {{pages}}', pageSizeLabel: 'Rows per page', pageSize: '{{count}} per page', page: 'Page', jumpToPage: 'Jump to page', previous: 'Previous page', next: 'Next page',
      status: { success: 'Success', failed: 'Failed', timeout: 'Timed out', running: 'Running', waiting_human: 'Waiting for confirmation', waiting_approval: 'Waiting for approval' },
    },
    workspaceSettings: {
      title: 'Manage workspace', close: 'Close', name: 'Name', nameAria: 'Workspace name', associatedSystem: 'Associated system', description: 'Description', descriptionAria: 'Workspace description',
      loadingSystem: 'Loading associated system...', systemDeleted: 'Application system deleted', systemUnavailable: 'Application system temporarily unavailable', searchSystem: 'Search application systems', selected: 'Selected', systemWorkspaceDisabled: 'Built-in workspace: deletion and application system binding are not allowed.',
      members: 'Members', loadingMembers: 'Loading members...', noMembers: 'No members', removeMember: 'Remove {{name}}', remove: 'Remove', addMember: 'Add member', searchMember: 'Search members', memberPlaceholder: 'Enter a name or email', searchingMembers: 'Searching members...', addNamedMember: 'Add {{name}}', add: 'Add',
      delete: 'Delete workspace', save: 'Save', confirmDeleteTitle: 'Delete workspace?', confirmDeleteDescription: 'Deleting {{name}} cannot be undone.', cancel: 'Cancel', confirmDelete: 'Delete',
      error: { loadDetails: 'Failed to load workspace details. Try again.', loadMembers: 'Failed to load members. Try again.', loadSystems: 'Failed to load application systems. Try again.', searchMembers: 'Failed to search members. Try again.', save: 'Failed to save workspace. Try again.', missingMembership: 'This member has no membership record and cannot be removed.', addMember: 'Failed to add member. Try again.', removeMember: 'Failed to remove member. Try again.', delete: 'Failed to delete workspace. Try again.' },
    },
    workspaceHome: {
      label: 'Workspace', noDescription: 'No description', manage: 'Manage workspace', rooms: 'Rooms', roomCount_one: '{{count}} room', roomCount_other: '{{count}} rooms', addRoom: 'Add room', openRoomHint: 'Open the room to continue collaborating', noRooms: 'No rooms',
    },
    workspaceNav: {
      workspaces: 'Workspaces', rooms: 'Rooms', expandWorkspace: 'Expand {{name}}', collapseWorkspace: 'Collapse {{name}}', expandWorkspacesGroup: 'Expand workspaces group', collapseWorkspacesGroup: 'Collapse workspaces group', expandRoomsGroup: 'Expand rooms group', collapseRoomsGroup: 'Collapse rooms group', moreWorkspaces: 'More workspaces',
    },
    workspaceCreate: {
      open: 'Create workspace', title: 'Create workspace', description: 'Share files and context across rooms in this workspace.', close: 'Close', fromApplication: 'Create from application system', fromApplicationDescription: 'Start with existing system context', custom: 'Custom workspace', customDescription: 'Collaborate across systems', searchApplication: 'Search application systems', name: 'Name', descriptionLabel: 'Description', back: 'Back', cancel: 'Cancel', create: 'Create',
    },
    knowledge: {
      title: 'Knowledge',
      roomDeskTitle: 'Knowledge',
      roomDeskSubtitle: 'Files and memory gathered in this Room, automatically referenced by AI in chat',
      subtitle: 'Approved knowledge and drafts visible across the org',
      closeAriaLabel: 'Close knowledge panel',
      openChat: 'Open chat',
      searchPlaceholder: 'Search title, filename, tags, or Room...',
      tagPlaceholder: 'Add tag, press Enter',
      add: 'Add',
      created: 'Created',
      tab: {
        knowledge: 'Knowledge',
        outputs: 'Outputs',
        outputsDesc: 'Room outputs will appear here',
      },
      memory: {
        updateCommand: 'Update Room Memory',
        loadError: 'Failed to load Room Memory. Please try again later.',
        saveConflict: 'Save failed because Room Memory changed. Refresh and merge the latest content. Your draft is preserved.',
        updatedBy: 'Edited by {{user}}',
        reviewedByChronicler: 'Organized by AI',
        roomMember: 'Room member',
        notUpdated: 'Not created yet',
        reviewRunning: 'AI is organizing Room Memory. You can continue editing when it finishes.',
        empty: 'This Room has no memory yet',
        emptyHint: 'Edit it directly, or ask AI in chat to “update Room Memory” based on the current conversation.',
        sharedHint: 'Room Memory is shared in this Room and automatically provided to its AI Agents, helping everyone continue with the current context.',
        cardTitle: 'Room Memory',
        cardDesc: 'Automatically organized by AI from the conversation: current problems, key conclusions, and next steps…',
        cardBy: 'Chronicler',
        organizing: 'Organizing',
      },
      filter: {
        all: 'All',
        approved: 'Approved',
        draft: 'Drafts',
        drafts: 'Drafts',
      },
      status: {
        approved: 'Approved',
        draft: 'Draft',
        rejected: 'Rejected',
        deleted: 'Deleted',
      },
      scope: {
        label: 'Scope',
        org: 'Org',
        room: 'Room',
        organization: 'Organization',
        roomDescription: 'Visible to current Room members',
        orgDescription: 'Discoverable from all Rooms in the organization',
      },
      location: {
        cloud: 'Cloud',
        local: 'Local',
      },
      updatedAt: 'Updated',
      time: {
        today: 'Today',
        yesterday: 'Yesterday',
      },
      storageFilter: {
        all: 'All',
        cloud: 'Cloud storage',
        local: 'Local storage',
      },
      fromRoom: 'From Room: {{room}}',
      tags: {
        label: 'Custom tags',
        add: '+ Add tag',
        addLabel: 'Add tag',
      },
      sourceType: {
        user_upload: 'User Upload',
        ai_generated: '{{brand}} Generated',
        ai_organized: 'AI Organized',
      },
      author: {
        ai: '{{brand}}',
      },
      permission: {
        notRoomMember: 'Only current Room members can manage this knowledge',
        notOrgMember: 'Only organization members can manage this knowledge',
        authorOrOrgAdminRequired: 'Only the author, organization admins, or organization owners can perform this action',
        knowledgeNotReady: 'This action is available after the knowledge content is ready',
        roomCloudOnly: 'Only ready Room cloud knowledge can be published to the organization',
        alreadyOrg: 'This knowledge is already organization knowledge',
        operationInProgress: 'This operation is in progress',
        deleted: 'This knowledge has been deleted',
      },
      contentStatus: {
        processing: 'Processing',
        ready: 'Ready',
        failed: 'Processing failed',
      },
      empty: {
        approvedTitle: 'No knowledge yet',
        draftTitle: 'No drafts',
        searchTitle: 'No results found',
        defaultDesc: 'Knowledge will appear here once approved',
        searchDesc: 'Try a different keyword',
        filterTitle: 'No knowledge matches these filters',
        filterDesc: 'Adjust the filters and try again',
      },
      pagination: {
        previous: 'Previous page',
        next: 'Next page',
        page: 'Page {{current}} of {{total}}',
        total: 'Total: {{total}}',
        pageLabel: 'Page',
        pageNumber: 'Page number',
        pageOf: 'of {{total}}',
        pageSize: '{{size}} per page',
        pageSizeLabel: 'Items per page',
      },
      upload: 'Add',
      uploadAriaLabel: 'Add Knowledge',
      'upload.dropActive': 'Drop to upload',
      'upload.dropHint': 'Drag files here, or click to browse',
      'upload.addMore': 'Add more files',
      'upload.removeFile': 'Remove {{name}}',
      'upload.progress': 'Uploading {{current}} / {{total}}',
      'upload.dropOrClick': 'or click to browse',
      'upload.supportedFormats': 'Supports PDF, DOCX, PPTX, XLSX, CSV, and common text, code, and configuration files',
      'upload.error.fileTooLarge': 'File too large (max {{size}})',
      'upload.error.unsupportedType': 'Unsupported file type',
      'upload.maxSize': 'Max {{size}}',
      'upload.error.failed': 'Upload failed',
      'upload.error.pdfNoText': 'No extractable text was found in this PDF. It may be a scanned document.',
      'upload.error.tooManyFiles': 'Supports at most {{count}} files',
      'upload.error.batchTooLarge': 'Total size exceeds {{size}}',
      'upload.completed': '{{count}} knowledge file(s) created or updated',
      'upload.cloudCompleted': '{{created}} knowledge file(s) created, {{updated}} updated',
      'upload.localCompleted': '{{created}} knowledge file(s) created, {{updated}} updated',
      'upload.localProcessing': 'Files were sent to {{daemon}} and are still being processed. Check the Knowledge list later.',
      'upload.submit': 'Upload',
      'upload.uploading': 'Uploading...',
      'upload.preparing': 'Preparing...',
      'upload.storage': 'Storage location',
      'upload.cloudStorage': 'Cloud storage',
      'upload.localStorage': 'Local storage',
      'upload.cloudDescription': 'Upload directly to the platform. No {{daemon}} installation is required',
      'upload.localDescription': 'Save files on the selected {{daemon}} node',
      'upload.chooseFiles': 'Choose files',
      'upload.singleFileLimit': 'Max {{size}} per file',
      'upload.batchLimit': 'Up to {{count}} files / {{size}} total',
      'upload.fileCountLimit': 'Up to {{count}} files',
      'upload.step.label': 'Step {{current}} of {{total}}',
      'upload.step.storageTitle': 'Choose where to save Knowledge',
      'upload.step.localTitle': 'Choose how to add Local Knowledge',
      'upload.step.cloudFilesTitle': 'Upload files to Cloud',
      'upload.step.localFilesTitle': 'Upload files to your local device',
      'upload.step.guideTitle': 'Sync a Knowledge folder',
      'upload.local.targetAgent': 'Bind {{daemon}}',
      'upload.local.selectAgent': 'Select a {{daemon}}',
      'upload.local.searchAgent': 'Search agents...',
      'upload.local.noAgentMatch': 'No matching agents',
      'upload.local.delivery': 'How to add files',
      'upload.local.managed': 'Upload files',
      'upload.local.managedDescription': 'Upload files from the browser to the selected {{daemon}} node',
      'upload.local.managedScenario': 'Best for daily additions, single-file updates, or small additions. The same file name updates existing Knowledge; a different name creates new Knowledge',
      'upload.local.manual': 'Folder sync',
      'upload.local.manualDescription': 'Maintain the entire Knowledge folder on the selected {{daemon}} node, then run sync',
      'upload.local.manualScenario': 'Best for initial setup, bulk migration, or full updates. Added, changed, deleted, and renamed files are reflected after sync',
      'upload.local.scope': 'Target: {{scope}} (automatic)',
      'upload.local.orgScope': 'Organization knowledge base',
      'upload.local.roomScope': 'Current Room',
      'upload.local.noAgent': 'No {{daemon}} is available',
      'upload.local.noAgentHint': 'Install, start, or restore the latest {{daemon}}.',
      'upload.guide.ready': 'Sync a Knowledge folder from the local device',
      'upload.guide.step1Title': '1. Open the Knowledge folder',
      'upload.guide.step1Description': 'Sign in to the device running the selected {{daemon}} and open the folder below. Copy the path to locate it in a terminal or file manager.',
      'upload.guide.step2Title': '2. Add files to synchronize',
      'upload.guide.step2Description': 'Copy one or more supported files directly into this folder. Files inside subfolders are not read yet.',
      'upload.guide.step3Title': '3. Sync to Knowledge',
      'upload.guide.step3Description': 'Run the command below in a terminal on the same device. It can be run from any directory. After it succeeds, the Knowledge list reflects added, updated, deleted, and renamed files.',
      'upload.guide.directory': 'Knowledge folder',
      'upload.guide.command': 'Sync command',
      'upload.guide.copy': 'Copy',
      'upload.guide.rules': 'A new file creates Knowledge; replacing content at the same path updates the existing Knowledge; deleting a file removes it; renaming deletes the old Knowledge and creates a new one. Until the sync command succeeds, the last successful version remains in use.',
      'upload.dialog.title': 'Add Knowledge',
      'upload.dialog.scopeOrg': 'Save as organization Knowledge',
      'upload.dialog.scopeRoom': 'Save as Knowledge for this Room',
      'upload.dialog.cancel': 'Cancel',
      'upload.dialog.back': 'Back',
      'upload.dialog.continue': 'Continue',
      'upload.dialog.done': 'Done',
      'upload.dialog.closeAriaLabel': 'Close',
      action: {
        approve: 'Approve',
        reject: 'Reject',
        delete: 'Delete',
        confirmDelete: 'Delete this knowledge? This cannot be undone.',
        reference: 'Reference in Chat',
        referenceHint: 'Reference knowledge in the chat input',
        promote: 'Publish to Organization',
        viewDetails: 'View details',
        hideDetails: 'Hide details',
      },
      detail: {
        loading: 'Loading...',
        noContent: 'No content available',
        loadError: 'Failed to load content',
        processing: 'Content is being processed and will be available for preview and reference when ready',
        failed: 'Content processing failed. Check the file and upload it again.',
      },
      local: {
        daemonLabel: 'Daemon: {{name}}',
        daemonOffline: 'Daemon offline',
        updateFailed: 'The latest update failed. The previous successful version is still available.',
        contentUnavailable: 'Local content is unavailable. Check the Daemon and source file, then retry.',
      },
      promote: {
        title: 'Publish to organization knowledge?',
        description: 'After publishing, all organization members can view it. Only you, organization admins, and organization owners can edit tags or delete it. Moving it back to the Room is not currently supported.',
        processing: 'Publishing…',
        converging: 'Finishing the publish operation. Do not submit it again',
        success: 'Published to organization knowledge',
        toastSuccess: '“{{title}}” has been published to organization knowledge',
        toastPending: '“{{title}}” is being published. The list will update automatically',
        failed: 'Publishing failed. The knowledge remains in the current Room',
      },
      delete: {
        title: 'Delete knowledge?',
        description: 'After deletion, this Knowledge is removed from the Knowledge list and can no longer be referenced in chat. This action cannot be undone.',
        localDescription: 'This also deletes the source file and parsed content from the local device. If the Daemon is offline or deletion fails, no data is removed and the Knowledge remains available. Restore the Daemon and try again.',
        confirm: 'Delete',
        failed: 'Deletion failed. Please try again',
      },
    },
    settings: {
      title: 'Settings Hub',
      description: 'Manage your workspace and preferences.',
      backToListAriaLabel: 'Back to settings list',
      closeAriaLabel: 'Close settings',
      nav: {
        account: 'Account',
        usage: 'Usage',
        appearance: 'Appearance',
        organization: 'Organization',
        members: 'Members',
        connectors: 'Connectors',
        webhooks: 'Webhooks',
        agents: '{{brand}} Daemon',        security: 'Security',
        activity: 'Activity',
      },
      webhooks: {
        title: 'Webhooks',
        subtitle: 'Manage automated alert ingestion and inspect entity matching results',
        add: 'Add webhook',
        catalogSubtitle: 'Choose an alert source to configure',
        searchSources: 'Search alert sources...',
        noMatchingSources: 'No matching alert sources',
        category: { all: 'All', alerting: 'Alerting' },
        loading: 'Loading webhooks...',
        empty: 'No webhooks yet',
        loadError: 'Failed to load webhooks',
        name: 'Name',
        namePlaceholder: 'For example: Production alerts',
        nameRequired: 'Name is required',
        description: 'Description',
        connection: 'Connector',
        selectConnection: 'Select a connector',
        searchConnections: 'Search connectors',
        noMatchingConnections: 'No matching connectors',
        connectionRequired: 'Select an active connector',
        connectionHint: 'Choose the connector that can access this alert source.',
        addConnector: 'No connector yet? Add one in Connectors',
        connectorEnabled: 'Enabled',
        connectorDisabled: 'Disabled',
        connectorOnline: 'Online',
        connectorUnavailable: 'Unavailable',
        createSubtitle: 'Configure a Prometheus alert source',
        backToList: 'Back to list',
        prometheusAlert: 'Prometheus alerts',
        prometheusDescription: 'Receive Alertmanager events and match them to entities',
        webhookDescription: 'Receive alerts and automatically match them to semantic entities.',
        templateDetails: 'Webhook template details',
        alertIngress: 'Alert ingestion',
        templateCapability: 'Receive Prometheus Alertmanager events, automatically match entities in the Logos semantic network, and retain the results for execution history and further analysis.',
        configureAndCreate: 'Configure and create',
        configureTitle: 'Configure Prometheus webhook',
        close: 'Close',
        createWebhook: 'Create and continue',
        descriptionPlaceholder: 'Optional notes about this webhook',
        alertmanagerSetupTitle: 'Next, configure Alertmanager',
        alertmanagerStepOne: 'Add this receiver to alertmanager.yml.',
        receiverCopyIncludesToken: 'The copied receiver configuration includes the full ingress token.',
        alertmanagerStepTwo: 'Route the alerts you want to send to this receiver.',
        alertmanagerStepThree: 'Reload or restart Alertmanager using the method appropriate for its deployment.',
        copyReceiverConfig: 'Copy receiver configuration',
        copyRouteConfig: 'Copy route configuration',
        prometheusDocs: 'Prometheus configuration documentation',
        create: 'Create',
        cancel: 'Cancel',
        createError: 'Failed to create webhook',
        updateError: 'Failed to update webhook. Try again.',
        saveAndContinue: 'Save and continue',
        editName: 'Edit webhook name',
        editDescription: 'Edit webhook description',
        emptyDescription: 'No description',
        editConnector: 'Edit connector',
        updateConfiguration: 'Update configuration',
        connectionUnavailableError: 'This connector is unavailable or you no longer have access. Select another connector and try again.',
        connectionInactiveError: 'This connector is disabled. Enable it in Connectors or select another connector.',
        connectionTypeMismatchError: 'This connector is not compatible with the selected webhook type. Select a Prometheus connector.',
        created: 'Webhook created',
        ingressUrl: '{{brand}} ingress URL',
        token: '{{brand}} ingress token',
        copyUrl: 'Copy URL',
        copyToken: 'Copy token',
        copyValue: 'Copy value',
        requestHeaders: 'Request headers',
        requestHeaderName: 'Header',
        requestHeaderValue: 'Value',
        addAlertmanager: 'Let Captain configure it automatically',
        alertmanagerGuide: 'If you prefer not to edit the configuration manually, ask Captain to use a connector that can reach Alertmanager to write the receiver and route, reload or verify Alertmanager. Risky operations require your confirmation.',
        autoConfigure: 'Configure & verify automatically',
        autoConfigureHint: 'Backs up Alertmanager config, merges this webhook receiver and route, validates, reloads, then reads the applied config to confirm it matches this Webhook.',
        manualConfigure: 'I will configure manually',
        verifyNow: 'Verify configuration',
        verifyNowHint: 'Reads the current Alertmanager configuration for the bound connector and checks whether this Webhook receiver and route are present. The Alertmanager configuration is not modified.',
        configurationChoiceTitle: 'Choose what to do next',
        configurationChoiceHintCreate: 'Choose automatic configuration, configure manually, or finish now.',
        configurationChoiceHintUpdate: 'Choose automatic configuration, verify the current setup, configure manually, or finish now.',
        configurationTabsLabel: 'Webhook configuration method',
        configurationTabAutomatic: 'Automatic configuration',
        configurationTabManual: 'Manual configuration',
        manualConfigurationTitle: 'Manual Alertmanager configuration required',
        configureAndVerify: 'Configure & verify',
        verifyOnly: 'Verify only',
        loadingRoom: 'Loading room',
        verifyConfiguration: 'Verify configuration',
        verificationTimeoutGuidance: 'This Webhook could not confirm its receiver in the current Alertmanager configuration. Run Configure & verify, or correct the manual configuration and verify again.',
        automaticRoutingScopeLabel: 'Alert scope:',
        automaticRoutingScope: 'All alerts from the bound Alertmanager; existing notification routes are preserved.',
        automaticVerificationMethodLabel: 'Verification:',
        automaticVerificationMethod: 'Reads the applied Alertmanager config and checks that this Webhook receiver and route are present.',
        agentConfigurationScopeLabel: 'Alert coverage:',
        agentConfigurationScope: 'Connect all alerts, or connect only the alerts you need.',
        agentConfigurationMethodLabel: 'Setup:',
        agentConfigurationMethod: 'Sets up alert ingestion and notifications, then checks that messages are delivered successfully.',
        configurePlanTitle: 'Before you start',
        inspectingTarget: 'Inspecting the bound Alertmanager target...',
        planField: { target: 'Target', write_mode: 'Configuration method', config_source: 'Configuration source', config_file: 'Configuration file', k8s_namespace: 'Kubernetes namespace', k8s_secret_name: 'Kubernetes Secret' },
        writeModeLocalFile: 'Configuration file on the Alertmanager host',
        writeModeK8s: 'Kubernetes Secret',
        writeModeManual: 'Automatic write unavailable — manual configuration required',
        manualReasonFallback: 'No writable Alertmanager configuration entry was detected for the selected connection.',
        manualConfigurationGuidance: 'Follow the manual configuration guide to update Alertmanager, then run verification.',
        openManualConfiguration: 'View manual configuration',
        configurationFailureNoReason: 'The server did not return a specific failure reason.',
        configurePlanIntro: 'Confirm the target and actions below. No change is made until you start the task.',
        planStepInspect: 'Confirm the bound Alertmanager and its configuration location',
        planStepBackup: 'Save the current configuration for recovery',
        planStepConfigure: 'Add this Webhook receiver and route without replacing existing receivers',
        planStepValidate: 'Check that the updated configuration is valid',
        planStepReload: 'Make the updated configuration take effect',
        planStepVerify: 'Read the applied configuration and confirm this Webhook is wired in',
        confirmAndStart: 'Confirm & start',
        configurationProgress: 'Configuration progress',
        verificationConfirmationTitle: 'Before verification',
        verificationConfirmationIntro: 'Review the bound Alertmanager and verification scope. Verification only reads the current configuration and makes no changes.',
        verificationProgress: 'Verification progress',
        startVerification: 'Start verification',
        closePanel: 'Close',
        manualConfigurationReason: 'The system found the Alertmanager service address, but could not find a configuration file or Kubernetes Secret that it can safely modify. This may be caused by insufficient ontoxd connection permissions, an unsupported Alertmanager deployment mode, or the current connection not being the node that runs Alertmanager. Check the connection permissions and deployment mode, or follow the manual configuration guide and verify again.',
        manualConfigurationReasonIntro: 'The system found the Alertmanager service address, but could not find a configuration file or Kubernetes Secret that it can safely modify. Possible causes:',
        manualConfigurationCausePermission: 'Insufficient ontoxd connection permissions',
        manualConfigurationCauseMode: 'Unsupported Alertmanager deployment mode',
        manualConfigurationCauseNode: 'The current connection is not the node running Alertmanager',
        manualConfigurationRawTitle: 'Probe output',
        taskStatusStarting: 'Starting the verification task...',
        taskStatusInspecting: 'Checking the bound Alertmanager target...',
        taskStatusConfiguring: 'Applying the Alertmanager configuration...',
        taskStatusValidating: 'Validating the updated configuration...',
        taskStatusReloading: 'Reloading Alertmanager...',
        taskStatusVerifying: 'Reading the applied configuration...',
        stepLabelInspect: 'Check the Alertmanager target',
        stepLabelBackup: 'Back up the current configuration',
        stepLabelConfigure: 'Write the Webhook receiver and route',
        stepLabelValidate: 'Validate the updated configuration',
        stepLabelReload: 'Reload Alertmanager',
        stepLabelVerify: 'Read the applied configuration and confirm {{brand}} wiring',
        configurationCompleted: 'Webhook configuration verified',
        configurationCompletedEvidence: 'The current Alertmanager configuration contains this Webhook receiver and route.',
        configurationAppliedVerificationFailed: 'Configuration was applied, but delivery verification failed',
        configurationFailed: 'Configuration failed',
        configurationAwaitingWrite: 'No write access detected — configure manually, then run verification',
        statusRefreshFailed: 'Unable to refresh the latest task status. Retrying automatically...',
        stepStatusRunning: 'Running',
        stepStatusSucceeded: 'Done',
        stepStatusFailed: 'Failed',
        stepStatusPending: 'Pending',
        saveAndVerify: 'Save & verify',
        saveOnly: 'Save only',
        connectionChangedHint: 'The bound connector changed.',
        reconfigure: 'Re-configure',
        done: 'Done',
        enabled: 'Enabled',
        disabled: 'Disabled',
        showMoreLabels: 'Show more labels',
        showFewerLabels: 'Show fewer labels',
        collapseLabels: 'Collapse',
        alertContentLabel: 'Alert content',
        alertInformationLabel: 'Alert',
        expandAlertContent: 'Show more',
        collapseAlertContent: 'Show less',
        expandAnnotationValue: 'Show more',
        collapseAnnotationValue: 'Show less',
        toggleError: 'Failed to update webhook status',
        back: 'Back',
        edit: 'Edit',
        editTitle: 'Update webhook configuration',
        save: 'Save',
        delete: 'Delete',
        deleteTitle: 'Delete webhook',
        moreActions: 'More actions',
        deleteDescription: 'New alerts will stop and execution history will no longer be available in {{brand}}.',
        confirmDelete: 'Delete webhook',
        deleteError: 'Failed to delete webhook. It remains active and can be retried.',
        showToken: 'Show token',
        hideToken: 'Hide token',
        test: 'Run internal test',
        verifyIngress: 'Verify real ingress',
        ingressVerified: 'Ingress token verified',
        justNow: 'just now',
        createdAt: 'Created',
        rateLimit: 'Ingest limit',
        rateLimitTooltip: 'Maximum pushes your alert source can send to Ontox per minute. A push counts once regardless of how many alerts it contains. Pushes over the limit are rejected.',
        rateLimitValue: '{{count}} requests/min',
        executions: 'Alert history',
        executionCount: '{{count}} total',
        executionHistoryHelp: 'Each row is one received alert group. Severity comes from the alert severity label; processing status shows whether {{brand}} handled the payload successfully.',
        severityHelp: 'Severity reported by the alert source',
        severity: {
          critical: 'Critical',
          warning: 'Warning',
          info: 'Info',
          other: 'Other',
          unknown: 'Not reported',
        },
        executionStatus: {
          success: 'Success',
          completed: 'Success',
          failed: 'Failed',
          timeout: 'Timed out',
          pending: 'Processing',
          running: 'Processing',
        },
        alertStatus: { firing: 'Firing', resolved: 'Resolved', grouped: 'Grouped' },
        alertStatusFilter: 'Alert status',
        allAlertStatuses: 'All statuses',
        unnamedAlert: 'Unnamed alert',
        firstTriggered: 'First triggered',
        recovered: 'Recovered',
        summaryLabel: 'Summary',
        descriptionLabel: 'Description',
        labelsLabel: 'Labels',
        annotationsLabel: 'Annotations',
        linkedEntity: 'Entity linked',
        candidateEntity: 'Candidate entity',
        unmatchedEntity: 'No entity matched',
        noEntityMatchDescription: 'No matching entity was found in the current semantic network.',
        alertCount: '{{count}} alerts',
        alertGroup: 'Alerts in this group ({{count}}, matched individually)',
        noExecutions: 'No alert records',
        viewAll: 'View all',
        previous: 'Previous',
        next: 'Next',
        page: 'Page',
        jumpToPage: 'Jump to page',
        pageSizeLabel: 'Rows per page',
        pageSize: '{{count}} per page',
        paginationSummary: '{{total}} total · page {{page}} of {{pages}}',
        matched: 'Matched',
        uncertain: 'Uncertain',
        unmatched: 'Unmatched',
        candidateMatches: '{{count}} possible entities; more evidence is required',
        confirmedMatches: '{{count}} confirmed entities',
        candidate: 'Candidate',
        confirmed: 'Confirmed',
        exactEvidence: 'Evidence: {{field}} = {{value}}',
        ruleConfidence: 'rule confidence {{confidence}}%',
        rawPayload: 'Raw payload',
        copyRawPayload: 'Copy raw payload',
        keywordPlaceholder: 'Search alert name, labels, annotations or content',
        clearSearch: 'Clear search',
        quickRange: 'Quick range',
        allTime: 'All time',
        today: 'Today',
        last1Hour: 'Last 1 hour',
        last24Hours: 'Last 24 hours',
        last7Days: 'Last 7 days',
        last30Days: 'Last 30 days',
        customRange: 'Custom range',
        now: 'Now',
        startTime: 'Start time',
        endTime: 'End time',
        endBeforeStart: 'End time must be later than the start time.',
        to: 'to',
        clear: 'Clear',
        apply: 'Apply',
        filter: 'Filter',
        reset: 'Reset',
        unlimited: 'Unlimited',
        lastTriggered: 'Last triggered {{time}}',
        lastTriggeredLabel: 'Last triggered',
        lastTriggeredRelative: 'Last triggered {{time}}',
        neverTriggered: 'Never triggered',
      },
      agents: {
        title: '{{brand}} Daemon',
        description: 'Monitor and manage deployed {{brand}} Daemons',
        install: 'Install {{brand}} Daemon',
        installTitle: 'Install {{brand}} Daemon',
        installBack: 'Back',
        choosePlatform: 'Choose the deployment platform',
        copyCommand: 'Copy',
        copyTooltip: 'Copy to clipboard',
        copied: 'Copied!',
        cancel: 'Cancel',
        verify: 'Verify installation',
        noAgents: 'No {{brand}} Daemons registered yet',
        loading: 'Loading...',
        installInfoLoadFailed: 'Failed to load installation information.',
        manifestLoadFailed: 'Failed to generate the Kubernetes installation command.',
        stepInstall: 'Install {{brand}} Daemon',
        clusterName: 'Cluster name',
        clusterNameHint: 'Give the cluster an easy-to-recognize name, such as prod-cn.',
        namespace: 'Namespace',
        namespaceHint: 'Kubernetes namespace used to install the daemon.',
        namespaceInvalid: 'Use 1–63 lowercase letters, numbers, or hyphens. Start and end with a letter or number.',
        generateInstallCommand: 'Generate install command',
        deleteTitle: 'Delete {{brand}} Daemon',
        deleteConfirm: 'This will permanently remove "{{name}}" from the system. The daemon will also be disconnected if currently online.',
        deleteInputLabel: 'Type the display name to confirm',
        deleteAction: 'Delete',
        deleteFailed: 'Failed to delete daemon',
        deleteSuccess: 'Daemon "{{name}}" deleted',
        deleting: 'Deleting...',
        selectAll: 'Select all offline daemons',
        selectRow: 'Select {{name}}',
        onlineNotSelectable: 'Online {{brand}} Daemon cannot be batch deleted',
        selectedCount: '{{count}} selected',
        batchDelete: 'Delete selected',
        clearSelection: 'Clear selection',
        batchDeleteTitle: 'Delete {{brand}} Daemons',
        batchDeleteConfirm: 'The following {{count}} daemon(s) will be permanently removed:',
        batchDeleteAction: 'Delete',
        batchDeleteSuccess: 'Deleted {{count}} daemon(s)',
        batchDeletePartialTitle: 'Some daemons could not be deleted',
        batchDeleteFailedTitle: 'Failed to delete daemons',
        columns: {
          name: 'Display Name',
          uuid: 'UUID',
          type: 'Type',
          networks: 'Networks',
          status: 'Status',
          version: 'Version',
          lastSeen: 'Last Seen',
          actions: 'Actions',
        },
        status: {
          online: 'Online',
          offline: 'Offline',
          pending: 'Pending',
        },
        confirmStop: 'Stop {{brand}} Daemon "{{name}}"? It will go offline.',
        confirmRestart: 'Restart {{brand}} Daemon "{{name}}"? It will briefly go offline.',
        confirmUninstall: 'Uninstall {{brand}} Daemon "{{name}}"? Type the display name to confirm.',
        platformLinux: {
          title: 'Linux Server',
          desc: 'CentOS / Ubuntu / Debian. Supports x86_64 and ARM64.',
        },
        platformK8s: {
          title: 'Kubernetes',
          desc: 'StatefulSet deployment, auto RBAC.',
        },
        commandsTitle: '{{brand}} Daemon Commands',
        commandsDesc: 'Copy these commands and run them on the target machine.',
        cmdVerify: 'Verify',
        cmdLogs: 'Logs',
        cmdRestart: 'Restart',
        cmdStop: 'Stop',
        cmdUpgrade: 'Upgrade',
        cmdUninstall: 'Uninstall',
        operations: {
          open: 'Operations commands',
          close: 'Close operations commands',
          title: 'Operations commands · {{name}}',
          k8sContextHint: 'Before running a command, confirm that kubectl is connected to the cluster for {{cluster}}.',
          versionUpgrade: 'Version upgrade',
          upgradeUnavailable: 'Upgrade information is temporarily unavailable. Other commands can still be used.',
        },
        accessMode: 'Access Mode',
        readWrite: 'Read Write',
        readOnly: 'Read Only',
        readWriteDesc: 'Full control: create, update, delete workloads (Deployments, StatefulSets, CronJobs, etc.), exec into Pods, scale services, and manage Prometheus alert rules.',
        readOnlyDesc: 'Observability only: view workloads, logs, and events. No modifications allowed.',
      },
      profile: {
        title: 'Account',
        description: 'Manage your personal information.',
        displayName: 'Display Name',
        avatar: 'Avatar',
        uploadAvatar: 'Upload Avatar',
        changeAvatar: 'Change Avatar',
        removeAvatar: 'Remove Avatar',
        email: 'Email',
        saveChanges: 'Save Changes',
        saving: 'Saving...',
        loading: 'Loading...',
        loadError: 'Failed to load profile',
      },
      appearance: {
        title: 'Appearance',
        description: 'Customize how {{brand}} looks and feels.',
        modeTitle: 'Appearance Mode',
        light: 'Light',
        dark: 'Dark',
        system: 'System',
        languageTitle: 'Interface Language',
        debugMode: 'Debug Mode',
        debugModeDescription: 'Show all agent work steps inline instead of hiding thread messages.',
      },
      organization: {
        title: 'Organization',
        description: 'View your organization details.',
        logo: 'Logo',
        name: 'Name',
        slug: 'Slug',
        slugCopy: 'Copy slug',
        id: 'Organization ID',
        role: 'Your Role',
        switchOrg: 'Switch Organization',
        loading: 'Loading...',
        loadError: 'Failed to load organization',
        notFound: 'No organization found.',
      },
      usage: {
        title: 'Usage',
        description: 'Monitor your credit balance, recharge history, and usage details.',
        creditsLow: 'Credits running low',
        creditsExhausted: 'Credits Exhausted',
        creditsExhaustedDesc: 'Your credit balance has been depleted. Recharge to continue.',
        requestRecharge: '+ Request Recharge',
        rechargeDialogTitle: 'Recharge Request',
        rechargeDialogDesc: 'Your credit balance is insufficient. Please provide a reason for the recharge request. An administrator will review and process it.',
        rechargeDialogReason: 'Reason',
        rechargeDialogPlaceholder: 'Describe why you need a recharge...',
        rechargeDialogCancel: 'Cancel',
        rechargeDialogSubmit: 'Submit',
        rechargeDialogSubmitting: 'Submitting...',
        rechargeDialogSuccess: 'Request submitted. An administrator will review it shortly.',
        viewDetails: 'View usage details →',
        currentBalance: 'Current Balance',
        credits: 'credits',
        upgrade: {
          title: 'Upgrade Your Plan',
          free: 'Free',
          pro: 'Pro',
          freePrice: '$0',
          freeCredits: '{{count}} free credits on signup',
          freeDesc: 'Need more? Buy credits anytime to unlock Pro.',
          currentPlan: 'Current Plan',
          proDesc: 'Credits never expire. Choose the pack that fits your needs',
          proDiscount: 'Save {{discount}}%',
          upgradeBtn: 'Upgrade',
          addCreditsBtn: 'Add Credits',
          buyFor: 'Buy for {{currency}}{{price}}',
          quantity: 'Quantity',
          total: 'Total',
          payNow: 'Pay Now',
          processing: 'Redirecting...',
          questions: 'Questions?',
          freeFeatures: ['All AI features', 'Unlimited connectors', 'Team (up to 3)', 'Community support'],
          proFeatures: ['All AI features', 'Unlimited connectors', 'Team (up to 50)', 'Priority support'],
        },
        rechargeHistory: 'Recharge History',
        records: '{{count}} records',
        noRecords: 'No records',
        pageSize: '{{count}} / page',
        recharge: 'Recharge',
        detailsTitle: 'Usage Details',
        detailsDesc: 'Credit consumption log.',
        startDate: 'Start date',
        endDate: 'End date',
        to: 'to',
        totalUsed: 'Total {{credits}} credits used',
        loading: 'Loading...',
        columns: {
          user: 'User',
          time: 'Time',
          quota: 'Credit',
        },
        checkout: {
          confirming: 'Confirming payment...',
          success: 'Credits added successfully!',
          failed: 'Recharge failed: {{error}}',
          timeout: "Payment is taking longer to confirm. Your credits will appear automatically — if not received within a few minutes, please contact support",
          purchaseSuccess: 'Credits purchased successfully!',
          purchaseCancelled: 'Purchase cancelled.',
          pendingOrderTitle: 'You have an unpaid order',
          pendingOrderHint: 'Complete payment in Alipay, or cancel to place a new order. Unpaid orders auto-close after 30 minutes.',
          cancelOrder: 'Cancel Order',
          cancelling: 'Cancelling...',
          channelAlipay: 'Alipay',
          unitPrice: 'Unit Price',
          unitCredits: 'Credits per Pack',
          totalCredits: 'Total Credits',
          scrollHint: 'Scroll for more',
        },
      },
      members: {
        title: 'Members',
        count: '{{count}} member',
        countPlural: '{{count}} members',
        inOrg: 'in this organization.',
        loading: 'Loading...',
        loadError: 'Failed to load members',
        empty: 'No members found',
        invite: 'Invite',
        searchPlaceholder: 'Search by name or email…',
        inviteTitle: 'Invite Team Members',
        inviteDescription: 'Share this code or link to invite others to join your organization.',
        joinCodeLabel: 'Join Code',
        inviteLinkLabel: 'Invitation Link',
        inviteCopy: 'Copy',
        inviteCopied: 'Copied!',
        inviteExpiryNote: 'This link expires in 7 days.',
        inviteExpired: 'This invite has expired.',
        inviteExpiresIn1Day: 'Expires in 1 day.',
        inviteExpiresInDays: 'Expires in {{days}} days.',
        inviteRegenerate: 'Regenerate',
        inviteToRoom: {
          title: 'Invite to Room',
          userLabel: 'User',
          searchPlaceholder: 'Search by name or email',
          noUsersFound: 'No users found for "{{query}}"',
          reasonLabel: 'Reason',
          reasonOptional: 'optional',
          reasonPlaceholder: 'Why are you inviting these users?',
          selectedCount: '{{count}} selected',
          inviteButton: 'Invite',
          inviteButtonWithCount: 'Invite ({{count}})',
          cancelButton: 'Cancel',
          inviteFailed: 'Failed to invite one or more users.',
        },
        regenerateConfirmTitle: 'Regenerate Invite Code?',
        regenerateConfirmDescription: 'The current invite code will be invalidated. Anyone who has this code will no longer be able to use it to join.',
        regenerateCancel: 'Cancel',
        regenerateConfirm: 'Regenerate',
        inviteGenerateError: 'Failed to generate invite link.',
        memberLimitExceeded: 'Organization member limit reached.',
        quotaHint: 'Remove existing members or upgrade your plan.',
        you: '(you)',
        role: {
          owner: 'Owner',
          admin: 'Admin',
          member: 'Member',
          change: 'Change role',
        },
        status: {
          active: 'Active',
          disabled: 'Disabled',
        },
        pagination: {
          showing: '{{from}}-{{to}} of {{total}}',
          previous: 'Previous page',
          next: 'Next page',
        },
        actions: {
          disable: 'Disable',
          enable: 'Enable',
          remove: 'Remove',
        },
      },
      connectors: {
        title: 'Connections',
        loading: 'Loading...',
        emptyTitle: 'No connectors configured',
        emptyDescription: 'Connect external services to empower AI agents.',
        addConnector: 'Add connector',
        manageConnectors: 'Manage connectors',
        catalogTitle: 'Available Connectors',
        backToList: 'Back to list',
        searchPlaceholder: 'Search connectors...', pageSearchLabel: 'Search configured connectors', pageSearchPlaceholder: 'Search name, type, owner, or status', statusFilter: 'Availability filter', statusAll: 'All', statusActive: 'Available', statusInactive: 'Disabled', pageNoResults: 'No connectors match the current filters.', toggleConfirmTitle: 'Change {{name}} status?', toggleConfirmDescription: 'This changes whether the connector can be used. Existing executions are not affected.', toggleConfirm: 'Confirm {{name}} status change', toggleCancel: 'Cancel', togglePending: 'Updating…', toggleSuccess: '{{name}} status updated', builtInManaged: 'System managed',
        categoryAll: 'All',
        categoryInfra: 'Infrastructure',
        categoryCollaboration: 'Collaboration',
        noResults: 'No connectors found',
        retry: 'Retry',
        bundle: 'bundle',
        team: 'team',
        personal: 'personal',
        knoxdPending: 'Pending setup',
        knoxdConfigured: 'Configured',
        knoxdOffline: 'Agent offline',
        configModal: {
          title: 'Add {{name}}',
          connectionName: 'Connection Name',
          connectionNamePlaceholder: 'e.g., Production cloud account',
          shareWithTeam: 'Share with team',
          teamDescription: 'All organization members can use this connection',
          personalDescription: 'Personal: only you can use this connection',
          create: 'Create Connection',
          creating: 'Creating...',
          oauthHint: 'Click Authorize to grant {{brand}} access to your account via the third-party service.',
          oauthAuthorize: 'Authorize',
          authorizing: 'Redirecting...',
          credentialStorageMode: 'Credential location',
          cloudStorage: 'Platform stores Role ARN',
          cloudStorageDesc: 'Ontox stores the Role ARN and obtains temporary credentials when it runs.',
          knoxdLocal: 'Local Storage',
          knoxdLocalDesc: 'Only the role reference is stored on the selected {{daemon}} node. Temporary STS credentials remain in memory.',
          noOnlineAgentGuide: 'Install or start a {{daemon}} Agent, then select an online Agent to continue.',
          agentLoadError: 'Unable to load {{daemon}} Agents. Check the connection and try again.',
          retryAgents: 'Retry',
          knoxdSuccessTitle: 'Connection Created',
          createKnoxdSuccess: 'Cloud connector created. Run this command on the selected {{daemon}} node; the first run initializes the credential and running it again updates it:',
          close: 'Close',
        },
        cloudAuthorization: {
          title: 'Secure cloud authorization',
          subtitle: 'Grant short-lived access through a dedicated role — no access keys required.',
          accountId: 'Cloud account ID',
          accountIdPlaceholder: 'Enter the cloud account ID',
          accountIdLabel: { aliyun: 'Alibaba Cloud account ID', tencent: 'Tencent Cloud root account UIN', volcengine: 'Volcengine Account ID' },
          accountIdPlaceholderByProvider: { aliyun: 'Enter your Alibaba Cloud account ID', tencent: 'Enter your Tencent Cloud root account UIN', volcengine: 'Enter your Volcengine Account ID' },
          accountIdInvalid: 'Cloud account IDs contain digits only.',
          accountIdHelp: {
            aliyun: 'Open the account menu in the upper-right corner of the Alibaba Cloud console, then go to Security Settings to find the account ID.',
            tencent: 'Open the account menu in the upper-right corner of the Tencent Cloud console, then go to Account Information to find the root account UIN.',
            volcengine: 'Open the account menu in the upper-right corner of the Volcengine console, then go to Account Management > Basic Information to find the Account ID.',
          },
          providers: { aliyun: 'Alibaba Cloud RAM', tencent: 'Tencent Cloud CAM', volcengine: 'Volcengine IAM' },
          steps: { 0: 'Create cloud role', 1: 'Verify role and enable connection' },
          account: 'Cloud account',
          editAccount: 'Change account',
          regenerateSession: 'Restart authorization',
          cancel: 'Cancel',
          role: 'Role to create',
          permissionsTitle: 'Permission packages',
          permissionsHint: 'Purpose-built policies keep the role auditable and easy to revoke.',
          leastPrivilege: 'Least privilege',
          resourceDiscovery: 'Resource discovery',
          resourceDiscoveryDesc: 'Read cloud inventory and topology. Always enabled.',
          operationsExecution: 'Operations execution',
          operationsExecutionDesc: 'Run approved commands through the cloud agent service.',
          observabilityConfig: 'Observability configuration',
          observabilityConfigDesc: 'Read and configure supported monitoring integrations.',
          securityNotice: 'Ontox stores only the role reference on your selected daemon. Temporary STS credentials stay in memory and are never persisted.',
          cloudSecurityNotice: 'The platform stores only the role reference. Temporary STS credentials remain only in execution memory and are never persisted.',
          localSecurityNotice: 'Ontox stores only the role reference on the selected Ontoxd. Temporary STS credentials remain in memory and are never persisted.',
		  status: { pending: 'Ready for authorization', authorizing: 'Waiting for cloud setup', verifying: 'Verifying cloud role', succeeded: 'Configured', failed: 'Authorization needs attention', connectionCreateFailed: 'Connection was not created', cancelled: 'Authorization cancelled' },
		  guidance: { pending: 'Open the cloud role guide, complete the steps, then return here to verify.', authorizing: 'Complete the guided setup in the cloud console, then return here to verify.', verifying: 'We are assuming the role, checking identity, and saving the role reference.', succeeded: 'The connector is configured and ready to use.', failed: 'Fix the issue shown here, then retry from the failed step.', cancelled: 'Start a new authorization session when you are ready.' },
          viewConnectionDetails: 'View connector details',
          openCloud: 'Open cloud setup',
          cloudShellCommand: 'CloudShell command',
          rosTemplate: 'ROS role template',
          rosUpdateTemplate: 'ROS permission update template',
          tencentCamTitle: 'Create a Tencent Cloud CAM role',
          tencentRoleName: 'Role name',
          tencentTrustedUin: 'Trusted Ontox UIN',
          tencentPoliciesTitle: 'Select both preset policies',
          tencentPolicyReadOnlyDesc: 'Read-only access to all Tencent Cloud resources, including resources supported in the future.',
          tencentPolicyTatDesc: 'Manage TAT for remote commands, execution results, and file transfer.',
          tencentCamStep1: 'Open Tencent Cloud, click Create role, and choose Tencent Cloud account as the role carrier.',
          tencentCamStep2: 'Choose Other root account, enter the account ID below, and keep All identities selected. Leave console access and external ID off.',
          tencentCamStep3: 'In Configure role policy, search for and select both preset policies below.',
          tencentCamStep4: 'Role tags are optional. Leave them blank and continue.',
          tencentCamStep5: 'On Review, enter the role name below and finish. Then return to Ontox to verify.',
          openTencentCam: 'View CAM setup steps',
          createTencentRole: 'Go to Tencent Cloud and start',
          manualRole: {
            trustedAccountId: 'Trusted Ontox account ID',
            roleName: 'Role name',
            policyDescriptions: {
              ReadOnlyAccess: 'Read-only access to cloud resources, including resources supported in the future.',
              AliyunECSAssistantFullAccess: 'Manage Alibaba Cloud ECS Cloud Assistant for remote commands, execution results, and file transfer.',
              AliyunARMSFullAccess: 'Manage ARMS alert integrations, webhook contacts, and notification policies.',
              ECSFullAccess: 'Manage Volcengine ECS remote commands and related ECS operations.',
            },
            aliyun: {
              title: 'Create an Alibaba Cloud RAM role',
              step1: 'Open RAM Roles, click Create Role, and choose Cloud Account as the trusted entity type.',
              step2: 'Choose Other Cloud Account, enter the trusted Ontox account ID below, leave external ID disabled or blank, then confirm.',
              step3: 'In the Create Role dialog, enter the role name below and confirm.',
              step4: 'Open the created role and stay on the Permissions tab.',
              step5: 'Click Grant Permission, then search for and attach the three recommended system policies below.',
              step6: 'After authorization completes, return to Ontox and start verification.',
              openConsole: 'Go to Alibaba Cloud and start',
            },
            volcengine: {
              title: 'Create a Volcengine IAM role',
              step1: 'Open IAM Roles, click Create Role, and choose Account as the trusted identity type.',
              step2: 'On Configure role information, enter the role name below, choose Other Account, and enter the trusted Ontox account ID. Leave external ID blank.',
              step3: 'Continue to Add permissions, then search for and attach both preset policies below.',
              step4: 'Finish creating the role, then return to Ontox and start verification.',
              openConsole: 'Go to Volcengine and start',
            },
          },
          setupArtifactHint: 'Generated for this session. It contains no access keys or temporary credentials.',
          copyCommand: 'Copy',
          copied: 'Copied',
          verify: 'I completed setup — verify',
          verifying: 'Verifying…',
          connectionReady: 'Connector configured and ready to use.',
          retry: 'Retry',
          useExistingRole: 'Use an existing role instead',
          useGuidedAuthorization: 'Use guided secure authorization',
          verifyExistingRole: 'Verify and connect',
          existingRoleTitle: 'Verify an existing cloud role',
          existingRoleDesc: 'Paste the existing role reference. Ontox will verify its trust relationship and required permissions before enabling the connection.',
          existingRoleSwitch: { aliyun: 'Already have a RAM Role ARN? Verify it directly', tencent: 'Already have a CAM Role QCS? Verify it directly', volcengine: 'Already have an IAM Role TRN? Verify it directly' },
          backToRoleGuide: 'Back to role creation guide',
          existingRoleLabel: { aliyun: 'RAM Role ARN', tencent: 'CAM Role QCS', volcengine: 'IAM Role TRN' },
          existingRolePlaceholder: { aliyun: 'acs:ram::123456789:role/example', tencent: 'qcs::cam::uin/123456789:roleName/example', volcengine: 'trn:iam::123456789:role/example' },
          errors: {
            CLOUD_ROLE_REF_INVALID: 'Enter a complete role reference for this cloud account.',
            ASSUME_ROLE_FAILED: 'The role could not be verified. Confirm that cloud-side setup completed successfully, then reopen the setup and try again.',
            STS_ROLE_NOT_FOUND: 'The expected role was not found. Ask a cloud administrator to complete the role setup, or use an existing role.',
            STS_DENIED: 'The role exists, but its trust policy does not allow the platform to assume it. Ask a cloud administrator to update the trust policy, then retry.',
            STS_SERVICE_UNAVAILABLE: 'The role could not be verified yet. Complete the role setup below first; if it is already complete, wait briefly and retry.',
            STS_THROTTLED: 'The cloud provider is temporarily rate limiting STS requests. Wait briefly and retry.',
            STS_NETWORK_ERROR: 'The platform could not reach the cloud STS endpoint. Keep this authorization open and retry after network recovery.',
            PERMISSION_PROBE_FAILED: 'The role trust is valid, but the required permissions could not be verified. Confirm that both policies in the guide are attached, then retry.',
            TENCENT_TAT_POLICY_MISSING: 'QcloudTATFullAccess is not attached to this role. Attach it in CAM, then retry verification.',
            CONNECTION_NAME_ALREADY_EXISTS: 'Cloud authorization succeeded, but this connection name is already in use. Return and choose a different name.',
            AGENT_VERSION_UNSUPPORTED: 'This Ontoxd version does not support automatic cloud role binding. Upgrade Ontoxd to v1.0.137 or newer, then retry.',
            generic: 'Authorization verification failed. Reopen the cloud setup, confirm the role and trust policy, then try again.',
          },
          changeConnectionName: 'Change connection name',
          continue: 'Continue authorization',
          close: 'Close',
          done: 'Done',
          openAgentSettings: 'Open Ontoxd settings',
        },
        deviceAuth: {
          connecting: 'Connecting...',
          waiting: 'Waiting for authorization...',
          expiresIn: 'Expires in {{minutes}} min',
          userCode: 'Authorization Code',
          copy: 'Copy',
          copied: 'Copied',
          openVerifyUrl: 'Open Authorization Page',
          success: 'Successfully Connected!',
          successDesc: 'You can now close this dialog.',
          noConnections: 'No available connections',
          selectConnection: 'Select a connection',
          continue: 'Continue',
          cancel: 'Cancel',
          done: 'Done',
          expired: 'Authorization expired. Please try again.',
          rejected: 'Authorization rejected.',
        },
        oauthCallback: {
          loading: 'Completing authorization...',
          success: 'Authorization successful! Redirecting...',
          error: 'Authorization failed. Please try again.',
          back: 'Go Back',
        },
        detailModal: {
          credentials: 'Required credentials',
          credentialsHint: 'This connector requires authentication. Configuration fields will be shown during setup.',
          capabilities: 'Capabilities',
          configure: 'Configure Connection',
          secureCloudAuthorization: 'Secure cloud authorization',
          cloudStorage: 'Cloud Storage',
          cloudStorageDesc: 'Credentials are encrypted and stored on the platform. No agent required.',
          knoxdLocal: 'Local Storage',
          knoxdLocalDesc: 'Only the role reference is stored on the selected {{daemon}} node. Temporary STS credentials remain in memory.',
          aliyun: {
            resourceDiscovery: 'Resource Discovery',
            resourceDiscoveryDesc: 'Query ECS instances, disks, networks and other resources',
            remoteExecution: 'Remote Execution',
            remoteExecutionDesc: 'Execute commands on ECS instances via Cloud Assistant',
          },
          aws: {
            resourceDiscovery: 'Resource Discovery',
            resourceDiscoveryDesc: 'Query EC2 instances, S3, RDS and other AWS resources',
            remoteExecution: 'Remote Execution',
            remoteExecutionDesc: 'Execute commands on EC2 instances via SSM Run Command',
          },
          tencent: {
            resourceDiscovery: 'Resource Discovery',
            resourceDiscoveryDesc: 'Query CVM instances, VPC, COS and other Tencent Cloud resources',
            remoteExecution: 'Remote Execution',
            remoteExecutionDesc: 'Execute commands on CVM instances via TAT',
          },
          volcengine: {
            resourceDiscovery: 'Resource Discovery',
            resourceDiscoveryDesc: 'Query ECS instances, VPC and other Volcengine resources',
            remoteExecution: 'Remote Execution',
            remoteExecutionDesc: 'Execute commands on ECS instances via Cloud Assistant',
          },
        },
        editModal: {
          type: 'Type',
          status: 'Status',
          created: 'Created',
          lastUsed: 'Last used',
          connectionName: 'Connection Name',
          credentials: 'Credentials',
          credentialMode: 'Credential Mode',
          knoxdLocal: 'Local Storage',
          credentialModeReadonly: 'Credential mode cannot be changed after creation',
          cloudAuthorizationTitle: 'Cloud account authorization',
          cloudAuthorizationDescription: 'Run the guided role verification again to update this connection without creating a duplicate.',
          reauthorizeCloudAccount: 'Reauthorize and verify',
          reauthorizingCloudAccount: 'Preparing authorization...',
          reauthorizeLoading: 'Loading the current connection details…',
          reauthorizeNeedsAccountId: 'Enter the cloud account ID before reauthorizing.',
          reauthorizeNeedsOnlineAgent: 'Select an online Agent before reauthorizing.',
		  replaceCloudRole: 'Configure a different role',
		  replacingCloudRole: 'Preparing role setup…',
          completeCloudAccount: 'Complete account setup and authorize',
          agentUpgradeRequired: 'Your Ontoxd version does not support automatic cloud authorization. Upgrade to v1.0.137 or later.',
          openAgentList: 'Go to Ontoxd list',
		  completingCloudAccount: 'Preparing account setup…',
          cliBindCommand: 'CLI Bind Command',
          credentialCommandHint: 'Run this command on the bound {{daemon}} node. The first run initializes the credential; running it again updates it.',
          bindAgent: 'Bind Agent',
          noAgent: 'Not bound',
          agentOfflineUnavailable: 'Offline, unavailable for selection',
          loadingCredentials: 'Loading credentials...',
          shareWithTeam: 'Share with team',
          teamDescription: 'All organization members can use this connection',
          personalDescription: 'Personal: only you can use this connection',
          save: 'Save Changes',
          saving: 'Saving...',
          delete: 'Delete Connection',
          deleting: 'Deleting...',
          confirmTitle: 'Delete this connection?',
          confirmMessage: 'Are you sure you want to delete "{{name}}"?',
          localRoleCleanupWarning: 'Also remove this connection\'s local RoleArn or RoleTrn binding from the bound {{daemon}} node. Deleting the cloud connection does not remove the local Role binding.',
          bundleWarning: 'This is a bundle connection — all related connections (discovery + execution) will be deleted.',
          cancel: 'Cancel',
          confirmDelete: 'Delete',
        },
        toggleStatus: '{{name}} — toggle status',
      },
      security: {
        title: 'Command Policy',
        description: 'Manage allowed and blocked commands. Unlisted commands require approval.',
        nav: 'Security',
        orgTab: 'Organization',
        orgTabDesc: 'Org-level command allow/block rules. All members can view, admins can edit.',
        myTab: 'My Authorized',
        myTabDesc: 'Commands you have permanently authorized. Removing means future executions require approval.',
        readonlyNotice: 'Only admins can edit organization policies.',
        columnCommand: 'Command',
        columnPolicy: 'Policy',
        columnAddedBy: 'Added by',
        columnAuthorized: 'Authorized',
        allow: 'Allow',
        block: 'Block',
        addPlaceholder: 'e.g. docker rm, systemctl stop',
        addCommand: '+ Add Command',
        empty: 'No policies configured.',
        searchPlaceholder: 'Filter commands...',
        noResults: 'No matching commands.',
        tip: '`{cmd}` matches all commands. `{sub}` matches only that subcommand. Subcommand rules take priority over base command rules.',
        myTip: 'Adding a command here skips future approval prompts. Removing it means the next execution will require approval. Org-level policies always take priority.',
        addSuccess: 'Command added',
        addError: 'Failed to add command',
        removeSuccess: 'Command removed',
        removeError: 'Failed to remove command',
      },
      activity: {
        title: 'Activity Log',
        subtitle: 'Command executions and file operations across your organization',
        empty: 'No activity yet',
        emptyHint: 'Activity will appear here when commands are executed via connectors',
        error: 'Failed to load activity log',
        retry: 'Retry',
        filter: {
          all: 'All',
          tool: 'Tool',
          status: 'Status',
          allTools: 'All Tools',
          allStatuses: 'All Statuses',
          allTime: 'All Time',
          lastHour: 'Last Hour',
          last24h: 'Last 24h',
          last7d: 'Last 7 Days',
          last30d: 'Last 30 Days',
          custom: 'Custom Range',
          searchPlaceholder: 'Search commands, hosts...',
        },
        col: {
          time: 'Time',
          action: 'Action',
          host: 'Host',
          channel: 'Channel',
          status: 'Status',
          duration: 'Duration',
        },
        detail: {
          user: 'User',
          tool: 'Tool',
          channel: 'Channel',
          host: 'Host',
          exitCode: 'Exit Code',
          duration: 'Duration',
          command: 'Command',
          filePath: 'File Path',
          output: 'Output',
          outputTruncated: '(truncated)',
          error: 'Error',
        },
        batch: {
          tooltipSingle: 'Single execution',
          tooltipBatch: 'Batch operation',
          filterLabel: 'Batch',
        },
        pagination: {
          showing: 'Showing {{from}}-{{to}} of {{total}} records',
          total: '{{total}} records',
          prev: 'Previous',
          next: 'Next',
        },
      },
    },
    orgBinding: {
      title: 'Set Up Your Organization',
      subtitle: 'Create a new organization or join an existing one to get started.',
      createLabel: 'Create Organization',
      joinLabel: 'Join Organization',
    },
    joinCode: {
      title: 'Join an Organization',
      subtitle: 'Enter the invitation code shared by your team admin.',
      codeLabel: 'Invitation Code',
      placeholder: 'Enter your code',
      submit: 'Continue',
      invalidOrExpired: 'This code is invalid or has expired.',
      memberLimitReached: 'This organization has reached its member limit.',
      back: '← Back to options',
      noCode: "Don't have a code?",
    },
    inviteCode: {
      title: 'Create Your Organization',
      subtitle: 'Enter the invitation code and set up your organization.',
      codeLabel: 'Invitation Code',
      placeholder: 'Enter your code',
      orgNameLabel: 'Organization Name',
      orgNamePlaceholder: 'e.g. Acme Corp',
      submit: 'Create Organization',
      invalidOrExpired: 'This code is invalid or has expired.',
      memberLimitReached: 'This organization has reached its member limit.',
      back: '← Back to options',
      noCode: "Don't have a code?",
    },
    accessApplication: {
      trigger: 'Request Access',
      title: 'Request Access',
      subtitle: "Fill in your details and we'll review your application.",
      workEmail: 'Work Email',
      workEmailPlaceholder: 'you@company.com',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Your name',
      companyName: 'Company or Team',
      companyNamePlaceholder: 'Your company or team name',
      role: 'Role',
      rolePlaceholder: 'Select your role',
      roleOptions: {
        itOperations: 'IT Operations',
        platformEngineering: 'Platform Engineering',
        sre: 'SRE',
        infrastructure: 'Infrastructure',
        engineeringManagement: 'Engineering Management',
        other: 'Other',
      },
      problem: 'What are you trying to solve with {{brand}}?',
      problemPlaceholder: 'Tell us about your infrastructure challenges or operational problems.',
      teamSize: 'Team Size (optional)',
      teamSizePlaceholder: 'Select team size',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      successTitle: 'Application Submitted',
      successMessage: "We'll review your request and email you an invite code if approved.",
      checkInbox: 'Check your inbox within 24 hours.',
      rateLimited: 'Too many requests. Please wait a moment and try again.',
      genericError: 'Something went wrong. Please try again.',
      required: 'This field is required.',
      invalidEmail: 'Enter a valid work email.',
      problemMinLength: 'Must be at least 20 characters.',
      close: 'Close',
    },
    invite: {
      loading: 'Loading invitation...',
      joining: 'Joining organization...',
      joinOrg: 'Join {{orgName}}',
      invitedBy: '{{name}} has invited you to join',
      invitedToJoin: "You've been invited to join",
      asRole: 'You will join as {{role}}',
      expired: 'This invitation has expired.',
      invalid: 'This invitation link is invalid.',
      used: 'This invitation has already been used.',
      error: 'Failed to load invitation details.',
      acceptError: 'Failed to accept invitation. Please try again.',
      loginToJoin: 'Log in to accept this invitation',
      email: 'Email',
      password: 'Password',
      name: 'Full Name',
      login: 'Log In & Join',
      noAccount: "Don't have an account?",
      createAccount: 'Create Account',
      haveAccount: 'Already have an account?',
      signIn: 'Sign In',
      register: 'Create Account & Join',
      registering: 'Creating account...',
      loggingIn: 'Logging in...',
      confirmJoin: 'Join Organization',
      backToLogin: 'Back to login',
      passwordRules: '12+ characters, at least 2 of: letters, numbers & symbols',
      confirmPassword: 'Confirm Password',
    },
    onboarding: {
      welcomeTitle: 'Welcome to {{brand}}',
      welcomeSubtitle: 'Your AI-native infrastructure operations team is ready.',
      ctaButton: 'Get Started',
      ctaLoading: 'Setting up...',
      progressTitle: 'Onboarding',
      agentCaptain: 'Orchestrates tasks and coordinates the team',
      agentCollector: 'Gathers infrastructure data from your servers',
      agentSupervisor: 'Validates data completeness and consistency',
      agentArchitect: 'Analyzes architecture and traces dependencies',
      agentChronicler: 'Records findings to the knowledge graph',
      agentCoordinator: 'Manages workflows and schedules',
      step1Title: 'Meet Your AI Team',
      step2Title: 'Connect a Server',
      step2Desc: 'Tell Captain about your server in the chat panel.',
      step3Title: 'Generate Discovery Report',
      step3Desc: 'Your AI team will analyze your infrastructure.',
      step3Analyzing: 'AI team is analyzing your infrastructure...',
      step3Complete: 'Report ready',
      viewReport: 'View Full Report',
      completed: 'Onboarding Complete!',
      skip: 'Skip onboarding',
      continueGuide: 'Continue',
    },
    structured: {
      taskRun: {
        backgroundExecution: 'Background execution',
        loadingLatest: 'Loading latest status',
      },
      tool: {
        submitted: 'Submitted',
        running: 'Running...',
        debugRunning: 'Running',
        completed: 'Completed',
        error: 'Error',
        showOutput: 'Show output',
        hideOutput: 'Hide output',
        loadingOutput: 'Loading output...',
        retry: 'Retry',
        noOutput: 'No output',
        noResults: 'No results',
        emptyResult: 'Empty result',
        outputLargeSlow: 'Output is large, loading may be slow',
      },
      toolLibrary: {
        common: { yes: 'Yes', no: 'No' },
        status: {
          submitted: 'Submitted', running: 'Running', cancelling: 'Cancelling', terminating: 'Confirming termination',
          succeeded: 'Succeeded', partial: 'Partially succeeded', failed: 'Failed', cancelled: 'Cancelled', timedOut: 'Timed out', unknown: 'Unknown status',
        },
        connector: {
          ssh: 'SSH', teleport: 'Teleport', aliyun: 'Alibaba Cloud Assistant', aws_ssm: 'AWS SSM', tencent: 'Tencent Cloud Assistant', volcengine: 'Volcengine',
        },
        action: { read: 'Read-only', change: 'Change', delete: 'Delete' },
        target: { default: 'Target', numbered: 'Target {{number}}', title: 'Targets' },
        duration: {
          label: 'Duration {{duration}}', milliseconds: '{{count}} ms', seconds: '{{count}} sec', minutes: '{{count}} min', minutesSeconds: '{{minutes}} min {{seconds}} sec',
        },
        array: { showRemaining: 'Show {{count}} more' },
        arguments: { title: 'Input parameters', provided: 'Provided' },
        logs: { title: 'Runtime logs', stdout: 'Standard output', stderr: 'Standard error', errorCode: 'Error code', errorStage: 'Error stage', diagnostic: 'Diagnostic', retryable: 'Retry', retryableYes: 'Retryable', retryableNo: 'Not retryable', truncated: 'Logs truncated' },
        schema: { input: 'Input parameters', output: 'Output parameters', required: 'Required' },
        search: { title: 'Available tools', empty: 'No matching tools' },
        describe: { title: 'Tool details', connectors: 'Compatible connectors' },
        tool: { defaultTitle: 'Operational tool' },
        prepare: { title: 'Confirm execution' },
        execution: {
          running: 'Tool running', submitted: 'Tool execution submitted', title: 'Tool execution',
          counts: '{{targetCount}} targets · {{successCount}} succeeded · {{failedCount}} failed',
          cancel: 'Cancel execution', confirmCancel: 'Confirm cancellation', keepRunning: 'Keep running',
          cancelConfirmation: 'Successful targets keep their results; running targets are stopped on a best-effort basis; queued targets are cancelled.',
          prepareRetry: 'Retry failed targets', confirmPrepareRetry: 'Confirm retry', cancelPrepareRetry: 'Not now',
          retryConfirmation: 'Only failed targets are prepared for retry; targets awaiting termination confirmation are not retried.',
          retryPrepared: 'Retry submitted.',
          terminatingHint: 'Waiting for running targets to stop',
        },
        output: { title: 'Output' },
        error: { title: 'Tool execution failed' },
        maintenance: {
          actionTitles: { createDraft: 'Tool draft created', findDraft: 'Tool draft found', getDraft: 'Tool draft', updateDraft: 'Tool draft updated', deleteDraft: 'Tool draft deleted', publish: 'Tool published', enable: 'Tool enabled', disable: 'Tool disabled', listVersions: 'Version history' },
          runningActions: { createDraft: 'Creating tool draft', findDraft: 'Finding tool draft', getDraft: 'Loading tool draft', updateDraft: 'Updating tool draft', deleteDraft: 'Deleting tool draft', publish: 'Publishing tool', enable: 'Enabling tool', disable: 'Disabling tool', listVersions: 'Loading version history', debug: 'Debugging tool', default: 'Processing tool' },
          debug: { submitted: 'Debug submitted', running: 'Debug running', succeeded: 'Debug succeeded', failed: 'Debug failed', default: 'Tool debug' },
          schema: { parameter: 'Parameter', type: 'Type', required: 'Required', defaultValue: 'Default', description: 'Description' },
          runtime: { title: 'Runtime', language: 'Language', defaultInterpreter: 'Default interpreter', allowedInterpreters: 'Allowed interpreters', timeout: 'Timeout', seconds: '{{count}} sec' },
          errorTitle: 'Tool operation failed', completedTitle: 'Tool operation completed', revision: 'Revision {{revision}}', executionId: 'Execution ID', status: 'Status', viewPublishedDetails: 'View published details', viewVersionHistory: 'View version history',
        },
      },
      error: {
        title: 'Error',
        insufficientCredits: {
          title: 'Insufficient credits',
          adminDescription: 'Your organization has insufficient credits. Add credits to continue using AI.',
          memberDescription: 'Your organization has insufficient credits. Contact an administrator to add credits.',
          recharge: 'Add credits',
        },
      },
      askHuman: {
        waitingForInput: 'Waiting for input...',
        submitted: 'Response submitted',
        expired: 'Timed out',
        answer: 'Answer',
        question: 'Question',
        other: 'Other',
        typeYourAnswer: 'Type your answer...',
        submitting: 'Submitting...',
        submit: 'Submit',
        selectPlaceholder: 'Select an option...',
        previous: 'Previous',
        next: 'Next',
      },
      approval: {
        approved: 'Approved',
        rejected: 'Rejected',
        expired: 'Approval timed out',
        expiredHint: 'Command was not executed',
        alwaysAllow: 'Always allow',
        needsYourAuth: 'Needs your authorization',
        waitingFor: 'Waiting for {{name}} to approve',
        processing: 'Processing...',
        reject: 'Reject',
        allowOnce: 'Allow',
        approvalFailed: 'Approval failed',
        rejectionFailed: 'Rejection failed',
        waitingForApproval: 'Waiting for approval...',
        alwaysAllowScope: 'Always allow will authorize',
        complexCommandNoAlwaysAllow: 'This command uses complex shell features and cannot be auto-authorized. Each execution requires manual approval.',
      },
      dispatch: {
        dispatchedTo: 'Dispatched to',
      },
      threadAck: {
        processing: 'Processing...',
        completed: 'Completed',
        error: 'Error occurred',
      },
      systemNotice: {
        sessionCleared: 'Session cleared. Starting fresh.',
        sessionStopped: 'Processing stopped.',
        sessionStoppedIdle: 'Nothing is currently running.',
        processingInterrupted: 'Processing interrupted.',
        allStopped: 'Stopped: {{names}}',
        allStoppedIdle: 'Nothing is currently running.',
        allCleared: 'All sessions cleared.',
        personaCleared: '{{name}} session cleared.',
      },
      botControl: {
        stopTooltip: 'Stop all running tasks',
        clearSession: 'Clear Session',
        clearAll: 'Clear All',
      },
      nameSeparator: ', ',
      search: {
        scopeThisRoom: 'This Room',
        scopeAllRooms: 'All Rooms',
        searchMessages: 'Search Messages',
        searchInRoom: 'Search in this room',
        searchAllRooms: 'Search across all rooms',
        noResults: 'No results for <b>{{term}}</b>',
        noResultsHint: 'Try different keywords or check the spelling',
        thread: 'Thread',
        replyTo: 'Reply',
      },
      profile: {
        roleAdmin: 'Admin',
        roleModerator: 'Moderator',
        roleMember: 'Member',
        mention: 'Mention',
        stop: 'Stop',
        clear: 'Clear',
        aiBadge: 'AI',
        confirmClear: 'Reset {{name}}\'s context? All conversation history will be lost.',
        confirmStop: 'Stop {{name}}? It will be cancelled if currently running.',
        confirmBtn: 'Confirm',
        cancelBtn: 'Cancel',
      },
      typing: {
        thinking: 'Thinking...',
        oneTyping: '<bold>{{name}}</bold> is typing...',
        twoTyping: '<bold>{{name1}}</bold> and <bold>{{name2}}</bold> are typing...',
        manyTyping: '<bold>{{name}}</bold> and {{count}} others are typing...',
      },
      timeline: {
        newMessages: 'New messages',
        jumpToLatest: 'Jump to Latest',
        noMessagesYet: 'No messages yet',
        loadingMessages: 'Loading messages',
	        unreadSeparator: 'New',
        relativeTime: {
          justNow: 'just now',
          minutesAgo: '{{n}}m ago',
          hoursAgo: '{{n}}h ago',
          daysAgo: '{{n}}d ago',
        },
        agentToolGroup: {
          done: 'Done',
          tool: 'tool',
          tools: 'tools',
          agentPrompt: 'Agent Prompt',
          close: 'Close',
        },
        suggestions: {
          title: '{{brand}} Team Ready',
          subtitle: 'Select a quick start, or type your own command',
          baseline: {
            title: 'Baseline Report',
            desc: 'Get a full picture of the current system',
            prompt: 'Run a discovery report on my infrastructure',
          },
          troubleshoot: {
            title: 'Troubleshoot an Issue',
            desc: 'Investigate and resolve production problems',
            prompt: 'Help me troubleshoot an issue:',
          },
          change: {
            title: 'Execute a Change',
            desc: 'Run operations and configuration updates',
            prompt: 'Help me execute a change:',
          },
          capabilities: {
            title: 'What Can You Do?',
            desc: 'Learn about the team\'s capabilities',
            prompt: 'Introduce the team\'s capabilities. What can you help me with?',
          },
        },
        inboxWelcome: {
          greetingMorning: 'Good morning, {{name}}',
          greetingAfternoon: 'Good afternoon, {{name}}',
          greetingEvening: 'Good evening, {{name}}',
          subtitle: 'Your AI SRE Team — discovering, monitoring, troubleshooting, and operations',
          alertSummary: {
            label: 'Alert Summary',
            prompt: 'Summarize current alerts',
          },
          healthCheck: {
            label: 'Health Check',
            prompt: 'Run a system health check',
          },
          troubleshoot: {
            label: 'Troubleshoot',
            prompt: 'Help me troubleshoot an issue:',
          },
          capabilities: {
            label: 'What Can You Do?',
            prompt: 'What can you help me with?',
          },
        },
        knowledgeWelcome: {
          greetingMorning: 'Good morning',
          greetingAfternoon: 'Good afternoon',
          greetingEvening: 'Good evening',
          subtitle: 'Find, browse, and manage the Knowledge you can access',
          searchKnowledge: {
            label: 'Search Knowledge',
            prompt: 'Search the knowledge base for relevant information',
          },
          uploadKnowledge: {
            label: 'Add Knowledge',
            prompt: 'Explain how to add Knowledge, including the available storage methods and the basic steps for each.',
          },
          recentChanges: {
            label: 'Recent Updates',
            prompt: 'What Knowledge was recently added or updated?',
          },
          capabilities: {
            label: 'About Knowledge',
            prompt: 'How can I use Knowledge?',
          },
        },
      },
    },
    messageActions: {
      reply: 'Reply',
      copy: 'Copy',
      copied: 'Copied',
      pin: 'Pin',
      unpin: 'Unpin',
      cancelReply: 'Cancel reply',
      cancelReplyAria: 'Cancel reply',
    },
    plugins: {
      title: 'Plugins', description: 'Discover Skills, tools, and connectors available in this workspace.',
      skills: 'Skills', tools: { title: 'Tools', description: 'Browse published tools.', searchPlaceholder: 'Search name, purpose, or maintainer', scopeLabel: 'Scope', statusLabel: 'Status', viewDetail: 'View details', continueDraft: 'Continue editing', enableWithAi: 'Enable with AI', disableWithAi: 'Disable with AI', createWithAi: 'Create with AI', creatingWithAi: 'Creating…', launchingAi: 'Opening…', draftVersion: 'Draft', loadMore: 'Load more tools', empty: 'No tools match the current filters.', searchEmpty: 'No matching tools', maintainedEmpty: 'You do not maintain any published tools yet', draftEmpty: 'No tool drafts', disabledEmpty: 'No disabled tools', clearSearch: 'Clear search', viewAllTools: 'View all tools', viewEnabledTools: 'View available tools', column: { tool: 'Tool', source: 'Source', version: 'Version', action: 'Action', status: 'Status', operation: 'Operation' }, scope: { all: 'All tools', maintained: 'Maintained by me', draft: 'Drafts' }, status: { all: 'All statuses', enabled: 'Available', disabled: 'Disabled' } },
      connectors: 'Connectors', connectorsDescription: 'Review connection status and open connector management.',
      searchPlaceholder: 'Search Skills, tools, or connectors', searchLabel: 'Search Skills, tools, or connectors', viewAll: 'View all', viewSkill: 'View details', skillVersion: 'Version {{version}}', viewConnector: 'View connector', clearFilters: 'Clear filters', retry: 'Retry', back: 'Back', backToWorkspace: 'Back to workspace', skillsLoading: 'Loading Skills', toolsLoading: 'Loading tools', connectorsLoading: 'Loading connectors', skillsLoadError: 'Unable to load Skills', toolsLoadError: 'Unable to load tools', connectorsLoadError: 'Unable to load connectors', skillsEmpty: 'No Skills available', toolsEmpty: 'No tools available', connectorsEmpty: 'No connectors available', createSkill: 'Create Skill', createTool: 'Create tool', configureConnector: 'Configure connector', creatingSkill: 'Creating…', creatingTool: 'Creating…', configuringConnector: 'Opening…',
      skillsSummary: 'Reusable AI operation workflow.', skillsUnavailable: 'Skill management is not available yet', skillsUnavailableDescription: 'This entry will appear after the protected Skill management route is delivered.', connectorSummary: 'Environment access capability.', connectorStatus: { active: 'Available', inactive: 'Disabled', disabled: 'Disabled' }, searchEmpty: 'No matching plugins found.', errorTitle: 'Unable to load this page', loading: 'Loading', enable: 'Enable', disable: 'Disable', maintainers: 'Maintainers', maintainerSummary: '{{name}} and {{count}} maintainers', maintainerSeparator: ', ', closePanel: 'Close plugins panel', workspaceUnavailable: 'Workspace unavailable', workspaceUnavailableDescription: 'This workspace is not available in your current organization context.',
      toggle: { confirmTitle: 'Confirm status change', confirmDescription: 'Change the availability of {{name}}?', confirm: 'Confirm', cancel: 'Cancel' },
      status: { enabled: 'Available', disabled: 'Disabled' }, officialTeam: 'Ontox official team',
      source: { official: 'Official', organization: 'Organization' },
      action: { read: 'Read-only check', write: 'Change operation', change: 'Change operation', execute: 'Execute command', delete: 'Delete operation' },
      impact: { read: 'Does not change target configuration.', write: 'May change the target environment.', change: 'May change the target environment.', execute: 'Runs commands on the selected targets.', delete: 'May remove data from the selected targets.' },
      detail: { useInAi: 'Use in AI', enableInAi: 'Enable with AI', toolInformation: 'Tool information', purpose: 'Purpose and use cases', inputSchema: 'Input parameters', outputSchema: 'Output parameters', implementation: 'Implementation', copyScript: 'Copy script', scriptCopied: 'Script copied', recentExecutions: 'Recent executions', viewAllExecutions: 'View all execution history', emptyExecutions: 'No recent executions', versionHistory: 'Version history', emptyVersions: 'No published versions', noReleaseNotes: 'No release notes', source: 'Source', version: 'Version', status: 'Status', action: 'Action', impact: 'Impact', scenarios: 'Use cases', scenarioDescription: 'Use when you need to {{summary}}; this is a {{action}}.' },
      aiContext: { ready: 'Tool added. Send the message to start.', createTitle: 'Create tool', dismiss: 'Dismiss', backToDetail: 'Back to tool details', backToList: 'Back to tool list', prompt: { create: 'Help me create an operations tool.', use: 'Use tool “{{title}}” (tool ID: {{id}}). Confirm targets and parameters before generating the execution plan.', enable: 'Enable tool “{{title}}” (tool ID: {{id}}). Explain the impact and wait for my confirmation.', disable: 'Disable tool “{{title}}” (tool ID: {{id}}). Explain the impact and wait for my confirmation.', maintain: 'Continue maintaining the draft of tool “{{title}}” (tool ID: {{id}}). Read the current draft and explain what can be changed.' } },
      schema: { empty: 'No parameters', name: 'Name', type: 'Type', requirement: 'Required', description: 'Description', default: 'Default', required: 'Required', optional: 'Optional', enumSingle: 'Single select', enumMultiple: 'Multiple select' },
      history: { title: 'My execution history', description: 'Only executions started by you are shown.', toolDetail: 'Tool details', succeeded: 'Succeeded', partially_succeeded: 'Partially succeeded', failed: 'Failed', running: 'Running', queued: 'Queued', submitted: 'Submitted', cancelling: 'Cancelling', terminating: 'Cancelling', cancelled: 'Cancelled', timed_out: 'Timed out', unknown: 'Status unknown', empty: 'No execution history yet.', time: 'Time', version: 'Version', targets: 'Targets', result: 'Result', duration: 'Duration', action: 'Action', viewResult: 'View result', resultDetail: 'Execution result', closeResult: 'Close result', executionStatus: 'Status', loadMore: 'Load more', cancelAction: 'Cancel execution', cancellingAction: 'Cancelling', compactCounts: '{{successCount}} succeeded · {{failedCount}} failed', successCount: 'Succeeded {{count}}', failedCount: 'Failed {{count}}', retryFailed: 'Prepare retry', retryPending: 'Preparing', retryPrepared: 'Retry confirmation plan created for {{count}} failed targets.', targetResults: 'Target results', target: 'Target', targetDuration: 'Target duration', stdoutPreview: 'Standard output', stderrPreview: 'Standard error', errorCode: 'Error code', hiddenValue: 'Hidden' },
    },
    alerts: {
      rulesNewTitle: 'New filter rule', rulesEditTitle: 'Edit filter rule', rulesCreateDescription: 'Create an RCA automation filter rule', rulesEditDescription: 'Edit the filter conditions for this rule', rulesConditionSummary: '{{count}} conditions (all)', rulesExtraConditions: '+{{count}} conditions',
      title: 'Alerts', subtitle: 'Alert center', alertCount: '{{count}} alert', alertCount_other: '{{count}} alerts', paginationSummary: '{{total}} total · page {{page}} of {{pages}}', pageSizeLabel: 'Rows per page', pageSize: '{{count}} per page', page: 'Page', jumpToPage: 'Jump to page', loading: 'Loading alerts', empty: 'No alerts yet', filteredEmpty: 'No alerts match the current filters', error: 'Unable to load alerts', unauthorized: 'Your sign-in has expired. Please sign in again.', forbidden: 'You do not have permission to view alerts.', retry: 'Retry', previous: 'Previous page', next: 'Next page', unnamed: 'Unnamed alert',
      filters: { keyword: 'Search alert name, labels or annotations', status: 'Alert status', webhook: 'Webhook', searchSource: 'Search Webhook sources', clearSource: 'Clear Webhook source', rcaStatus: 'RCA status', startsAfter: 'Start', startsBefore: 'End', quickRange: 'Quick range', allTime: 'All time', last1Hour: 'Last hour', last24Hours: 'Last 24 hours', today: 'Today', last7Days: 'Last 7 days', last30Days: 'Last 30 days', apply: 'Filter', clear: 'Clear filters', clearKeyword: 'Clear search', expand: 'Show filters', collapse: 'Hide filters' },
      columns: { name: 'Alert name', status: 'Status', source: 'Source', time: 'Time', rca: 'RCA', actions: 'Actions' },
      status: { all: 'All statuses', firing: 'Firing', resolved: 'Resolved' },
      rcaStatus: { all: 'All RCA statuses', unevaluated: 'Not triggered', unmatched: 'Not triggered', triggering: 'Triggering', failed: 'Trigger failed', triggered: 'Triggered' },
      webhooks: { all: 'All sources', deleted: 'Deleted Webhook' },
      actions: { startRca: 'Start RCA', retryRca: 'Retry RCA', triggering: 'Triggering', viewConversation: 'View conversation', openingConversation: 'Opening conversation', failed: 'Unable to start RCA', unauthorized: 'Your sign-in has expired. Please sign in again.', forbidden: 'You do not have permission to start RCA.' },
      details: { loading: 'Loading alert details', loadError: 'Unable to load alert details', retry: 'Retry alert details' },
      rules: { title: 'Filter rules', description: 'RCA starts when any enabled rule matches', loading: 'Loading rules', empty: 'No filter rules yet', filteredEmpty: 'No filter rules match the search', loadError: 'Unable to load filter rules', unauthorized: 'Your sign-in has expired. Please sign in again.', forbidden: 'You do not have permission to manage filter rules.', retry: 'Retry', new: 'New rule', edit: 'Edit', delete: 'Delete', confirmDelete: 'Confirm delete', cancel: 'Cancel', close: 'Close filter rules', name: 'Rule name', enabled: 'Enable rule', allConditions: 'Match all conditions', conditionHint: 'All conditions in one rule must match; any matching rule triggers RCA', and: 'AND', addCondition: 'Add condition', removeCondition: 'Remove condition', conditionPrefix: 'Condition {{index}}', fieldLabel: 'Field', labelKey: 'Label key', operatorLabel: 'Operator', value: 'Value', save: 'Save rule', saveError: 'Unable to save rule', updateError: 'Unable to update rule', deleteError: 'Unable to delete rule', nameRequired: 'Enter a rule name', labelKeyRequired: 'Enter a label key', valueRequired: 'Enter a condition value', invalidRegex: 'Enter a valid regular expression', conditionSummary: '{{count}} conditions', searchLabel: 'Search rule names', statusFilterLabel: 'Enabled status', statusFilter: { all: 'All statuses', enabled: 'Enabled', disabled: 'Disabled' }, paginationSummary: '{{total}} total · page {{page}} of {{pages}}', pageSizeLabel: 'Rows per page', pageSize: '{{count}} per page', page: 'Page', jumpToPage: 'Jump to page', previous: 'Previous page', next: 'Next page', fields: { alertname: 'Alert name', webhook_id: 'Source / Webhook', status: 'Normalized status', starts_at: 'Time', label: 'Label' }, operators: { equals: 'Equals', not_equals: 'Does not equal', contains: 'Contains', matches: 'Regular expression', exists: 'Exists', not_exists: 'Does not exist' } },
    },
    aria: {
      loading: 'Loading',
      connectors: 'Connectors',
      settings: 'Settings',
      helpCenter: 'Help Center',
      copy: 'Copy',
      close: 'Close',
      cancelReply: 'Cancel reply',
      backToChat: 'Back to chat',
      threadMessage: 'Thread message',
    },
  },
};

const KNOX_RESOURCES_ZH: Record<string, Record<string, unknown>> = {
  'knox-aiops': {
    nav: {
      brand: '{{brand}} AI-Native AIOps',
      triageInbox: '分诊收件箱',
      members: '成员',
      selectRoom: '选择一个房间开始',
      settings: '设置',
      signOut: '退出登录',
      support: '帮助与支持',
      lightMode: '浅色模式',
      darkMode: '深色模式',
      expandSidebar: '展开侧边栏',
      collapseSidebar: '收起侧边栏',
      newRoom: '新建房间',
      inbox: '收件箱',
      inboxPending: '待处理事项',
      knowledge: '知识库',
      searchRooms: '搜索房间',
      searchRoomsAria: '搜索房间',
      clearSearch: '清除搜索',
      rooms: '房间',
      backToInbox: '返回 Inbox',
      noRoomsFound: '未找到房间',
      noRoomsYet: '暂无房间',
      createFirstRoom: '创建你的第一个房间',
      showingRoomsOf: '显示 {{shown}} / 共 {{total}} 个房间 — 使用搜索查找更多',
      agents: '{{brand}} Daemon',
    },
    common: {
      yes: '是',
      no: '否',
      edit: '编辑',
      cancel: '取消',
      save: '保存',
      saving: '保存中...',
    },
    legal: {
      termsTitle: '服务条款',
      privacyTitle: '隐私政策',
      refundTitle: '退款政策',
      back: '← 返回',
      copyright: '© {{year}} {{company}} 保留所有权利。',
    },
    semanticPanel: {
      viewpoint: { business: '应用系统', infra: '基础设施', events: '事件与变更', query: 'Query' },
      breadcrumb: { appSystems: '应用系统' },
      close: '关闭网络视图',
      error: { parseViewSpec: '无法解析视图参数', loadFailed: '加载失败', unsupportedLayout: '不支持的视图布局' },
      retry: '重试',
      tab: { OS: 'OS', K8S: 'K8S', storage: '存储', middleware: '中间件', config: '配置', network: '网络', ingress: '入口', incident: '事件', problem: '问题', serviceRequest: '服务请求', changeRequest: '变更' },
      entityType: { OS: '操作系统', CloudVM: '云主机', PhysicalServer: '物理机', K8sWorkload: '工作负载', CloudDisk: '云盘', StorageBucket: '存储桶', CloudRDS: '云数据库', CloudCache: '云缓存', ArtifactInst: '自建中间件', ConfigFile: '配置文件', Secret: '密钥', CloudVPC: 'VPC', CloudSubnet: '子网', CloudSecurityGroup: '安全组', CloudWAF: 'WAF', CloudDDS: '文档数据库', CloudDWS: '数据仓库', CloudCSS: '云搜索', Domain: '域名', LoadBalancer: '负载均衡', K8sCluster: 'K8s集群', K8sNamespace: '命名空间', CronJob: '定时任务', InspectionTask: '巡检任务', Incident: '事件', Problem: '问题', ServiceRequest: '服务请求', ChangeRequest: '变更请求' },
      subtypeLabel: '子类型：',
      noTabs: '无可用 tab',
      filterBy: '已按 {{name}} 过滤',
      clearFilter: '× 清除过滤',
      table: { search: '搜索...', searchAria: '搜索', loading: '加载中...', noData: '暂无数据', unknownType: '未知资源类型', totalCount: '共 {{count}} 条', pagination: { prev: '上一页', next: '下一页', range: '第 {{start}}-{{end}} 条 / 共 {{total}} 条' } },
      cards: { noIslands: '暂无应用系统', emptyBusinessTitle: '没有发现应用系统', emptyBusinessHint: '点击下方按钮，发起一次基础设施发现', emptyBusinessCommand: '发起一次基础设施发现', emptyBusinessSendCommand: '对我的基础设施运行一次发现报告' },
      criticality: { critical: '严重', high: '高', medium: '中', low: '低' },
      toolbar: { envLabel: '环境', allEnv: '所有环境', domainLabel: '域名' },
      detail: { title: '实体详情', properties: '属性', relationships: '关联关系', noRelationships: '无关联关系', loading: '加载关系中...', loadError: '加载关系失败', retry: '重试', source: '源', target: '目标', clickToExplore: '点击查看详情', home: '首页', breadcrumbLabel: '面包屑导航', back: '返回上一级', close: '关闭详情', expandAll: '展开全部（{{count}}）', collapse: '收起' },
      columns: { name: '名称', instanceName: '实例名', hostname: '主机名', osType: '操作系统', kernelVersion: '内核版本', cpuCores: 'CPU核数', memoryGb: '内存(GB)', provider: '云平台', dnsProvider: 'DNS服务商', instanceType: '规格', instanceClass: '实例规格', privateIp: '内网IP', status: '状态', model: '型号', workloadType: '类型', namespace: '命名空间', replicas: '副本数', cpuRequest: 'CPU请求', memoryRequest: '内存请求', clusterName: '集群', diskType: '磁盘类型', sizeGb: '大小(GB)', attachedTo: '挂载到', bucketName: 'Bucket名称', region: '区域', engine: '引擎', engineVersion: '引擎版本', endpoint: '端点', nodeType: '节点类型', path: '路径', filePath: '路径', fileName: '文件名', format: '格式', sizeBytes: '大小(字节)', vpcId: 'VPC ID', cidr: 'CIDR', subnetId: '子网ID', zone: '可用区', sgId: '安全组ID', rulesCount: '规则数', fqdn: 'FQDN', lbType: 'LB类型', dnsName: 'DNS名称', deploymentType: '部署方式', version: '版本', installPath: '安装路径', softwareType: '引擎', serviceCategory: '服务类型', listenPorts: '监听端口', osVersion: '系统版本', processSignature: '进程标识', createdAt: '创建时间', updatedAt: '更新时间', processes: '进程列表', podName: 'Pod名称', secretType: '类型', title: '标题', severity: '严重度', priority: '优先级', impact: '影响', urgency: '紧急度', rootCauseSummary: '根因摘要', workaround: '临时方案', requestType: '请求类型', changeType: '变更类型', riskLevel: '风险等级', rollbackPlan: '回滚方案', storageGb: '存储(GB)' },
    },
    login: {
      signIn: '登录 {{brand}}',
      subtitle: '和你的 7x24 On Call 的 AI SRE 团队协作',
      invitationCode: '有邀请码？',
      invitationCodeLabel: '邀请码',
      invitationCodePlaceholder: '输入邀请码',
      go: '前往',
      copyright: '© 2026 {{brand}}',
      termsOfService: '服务条款',
      privacyPolicy: '隐私政策',
      or: '或',
      refundPolicy: '退款政策',
    },
    loginForm: {
      email: '邮箱地址',
      emailPlaceholder: 'name@company.com',
      change: '更换',
      password: '密码',
      passwordPlaceholder: '输入密码',
      continue: '继续',
      signingIn: '登录中...',
      signIn: '登录',
      checkingEmail: '检查中...',
      setPasswordTitle: '设置密码',
      setPasswordSubtitle: '添加密码以启用邮箱登录。',
      registerTitle: '创建你的账号',
      registerSubtitle: '设置你的姓名和密码以开始使用。',
      name: '姓名',
      namePlaceholder: '你的姓名',
      createPassword: '创建密码',
      passwordRules: '至少12个字符，字母、数字、符号至少包含两种',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      signingUp: '创建中...',
      signUp: '创建账号',
      settingPassword: '设置密码中...',
      setPassword: '设置密码',
      forgotPassword: '忘记密码？',
      verifyEmailTitle: '请查看您的邮箱',
      verifyEmailSentTo: '我们已向以下邮箱发送验证码',
      verifyEmailSubtitle: '我们已向您的邮箱发送了 6 位验证码。',
      verifying: '正在验证...',
      resendCode: '重新发送验证码',
      resendIn: '{{seconds}}秒后可重新发送',
    },
    forgotPassword: {
      title: '忘记密码',
      subtitle: '输入您的邮箱地址，我们将发送密码重置链接。',
      send: '发送重置链接',
      sending: '发送中...',
      checkEmail: '请查看您的邮箱',
      checkEmailDescription: '如果该邮箱已注册，您将收到重置密码的邮件。',
      backToLogin: '返回登录',
      resendIn: '{{seconds}}秒后可重新发送',
      resend: '重新发送邮件',
    },
    resetPassword: {
      title: '设置新密码',
      verifying: '正在验证链接...',
      linkExpired: '链接已失效',
      linkExpiredDescription: '此重置密码链接已过期或已被使用，请重新申请。',
      requestNewLink: '重新申请重置链接',
      for: '为 {{email}} 设置新密码',
      passwordMismatch: '两次输入的密码不一致',
      resetting: '重置中...',
      reset: '重置密码',
    },
    oauth: {
      continueWithGoogle: '使用 Google 登录',
      continueWithGitHub: '使用 GitHub 登录',
      continueWithDingTalk: '使用钉钉登录',
      continueWithWeChat: '使用微信登录',
      continueWithWeCom: '使用企业微信登录',
      continueWithFeishu: '使用飞书登录',
      // continueWithMicrosoft: '使用 Microsoft 登录',  // SSO 未测试，暂不启用
    },
    register: {
      noToken: '未提供邀请令牌',
      expired: '此邀请已过期',
      invalid: '邀请无效或已过期',
      failed: '注册失败',
      loginFailed: '登录失败',
      invitationNotFound: '邀请不存在',
      goToLogin: '前往登录',
      createYourTeam: '创建你的团队',
      setupOrg: '设置你的组织以开始使用',
      daysLicense: '{{days}} 天许可',
      upToMembers: '最多 {{count}} 位成员',
      checking: '检查中...',
      fullName: '姓名',
      fullNamePlaceholder: '你的姓名',
      createPassword: '创建密码',
      passwordRules: '至少12个字符，字母、数字、符号至少包含两种',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      next: '下一步',
      orgName: '组织名称',
      orgNamePlaceholder: '你的公司名称',
      orgSlug: '组织标识',
      orgSlugPlaceholder: 'your-company',
      orgSlugHint: '用于组织 URL',
      back: '返回',
      creating: '创建中...',
      createOrg: '创建组织',
    },
    createRoom: {
      title: '创建房间',
      description: '创建一个房间，用于与 AI 协作处理具体任务。',
      nameLabel: '房间名称',
      namePlaceholder: '例如 prod-outage',
      descriptionLabel: '描述',
      descriptionPlaceholder: '这个房间是做什么的？',
      cancel: '取消',
      creating: '创建中...',
      create: '创建房间',
      failed: '创建房间失败，请重试。',
    },
    room: {
      backToInbox: '返回 Inbox',
      backToRoomList: '返回房间列表',
      commands: {
        stopDescription: '停止所有正在运行的 AI Agent',
        clearDescription: '清除 AI 会话 — /clear @bot 清除指定 Bot，/clear 清除全部',
        updateMemoryDescription: '更新 Room Memory',
      },
      roomDesk: '知识',
      semanticNetwork: '语义网络',
      jumpToTime: '跳转到时间点',
      jumpToTimeTime: '时间',
      jumpToTimeDate: '日期',
      jumpToTimePreset: '快捷选择',
      jumpToTimeToday: '今天',
      jumpToTimeYesterday: '昨天',
      jumpToTimeBeginning: '最早',
      jumpToTimeGo: '跳转',
      jumpToTimeError: '无法解析日期',
      leaveRoom: '离开房间',
      pinnedMessages: '置顶消息',
      noPinnedMessages: '暂无置顶消息',
      confirmLeave: '确定要离开这个房间吗？',
      leaveFailed: '离开房间失败！',
      leaving: '离开中...',
      leave: '离开',
      deleteRoom: '删除房间',
      confirmDeleteRoom: '删除后所有成员都无法再进入该房间，且不可恢复。',
      deleteRoomFailed: '删除房间失败！',
      deletingRoom: '删除中...',
      deleteRoomConfirm: '删除房间',
      members: {
        title: '成员',
        searchPlaceholder: '搜索成员',
        clearSearch: '清除搜索',
        typing: '正在输入',
      },
      inviteUsers: '邀请用户',
      searchUsers: '按姓名或 ID 搜索...',
      noUsersFound: '未找到用户',
      joined: '已加入',
      messageDeleted: '此消息已被删除',
      markAsRead: '标记为已读',
      invite: '邀请',
      hideMembers: '隐藏成员',
      showMembers: '显示成员',
      hideMembersTasks: '收起成员与任务侧栏',
      showMembersTasks: '展开成员与任务侧栏',
      moreOptions: '更多选项',
      dateToday: '今天',
      dateYesterday: '昨天',
      stateEvent: {
        joined: '{{name}} 加入了房间',
        left: '{{name}} 离开了房间',
        removed: '{{sender}} 移除了 {{name}}',
        invited: '{{sender}} 邀请了 {{name}}',
        banned: '{{sender}} 封禁了 {{name}}',
        knock: '{{name}} 请求加入',
        updatedMembership: '{{name}} 更新了成员状态',
        roomNameChanged: '{{sender}} 将房间名改为"{{name}}"',
        roomNameRemoved: '{{sender}} 移除了房间名',
        topicChanged: '{{sender}} 更改了主题',
        topicRemoved: '{{sender}} 移除了主题',
        avatarChanged: '{{sender}} 更改了房间头像',
      },
      thread: {
        title: '话题',
        reply: '条回复',
        replies: '条回复',
        noRepliesYet: '暂无回复',
        backToChat: '返回聊天',
        close: '关闭',
      },
      input: {
        placeholder: '输入消息...',
        aiGeneratedNotice: '内容由 AI 生成，请结合实际情况使用',
        stopGenerating: '停止生成',
        sendMessage: '发送消息',
      },
      attachment: {
        open: '添加附件',
        dropHint: '拖放文件到此处，或点击选择',
        supportedTypes: '上传文件 · 单文件最大 10 MB · 同名文件不会覆盖',
        unsupportedType: '不支持的文件类型',
        fileTooLarge: '文件过大（最大 10 MB）',
        uploading: '上传中',
        ready: '已上传',
        failed: '上传失败',
      },
      knowledgeReference: {
        remove: '移除知识引用',
      },
      readiness: {
        setupFailed: '房间初始化失败',
        setupDegraded: '房间初始化降级',
        unsupported: '房间不支持',
        bindingPlugin: '正在绑定插件',
        waitingForBots: '等待机器人加入',
        initializing: '房间初始化中',
        ready: '房间就绪',
        chatUnavailable: '聊天不可用',
        chatUnavailableHint: '请等待房间初始化完成后再发起聊天。',
      },
      readinessBadge: {
        binding: '绑定中',
        waiting: '等待中',
        failed: '失败',
        degraded: '降级',
        unsupported: '不支持',
        initializing: '初始化中',
      },
    },
    error: {
      loginFailed: '登录失败',
      registrationFailed: '注册失败',
      retrySetupFailed: '重试设置失败，请重试。',
    },
    metrics: {
      title: '{{brand}} Canvas',
    },
    inbox: {
      title: '收件箱',
      closeAriaLabel: '关闭收件箱',
      openAiChat: '打开 AI 对话',
      loading: '正在加载收件箱',
      error: '无法加载收件箱',
      filter: {
        open: '待处理',
        resolved: '已处理',
        all: '全部',
      },
      empty: {
        openTitle: '一切就绪',
        openDescription: '没有待处理的事项',
        otherTitle: '暂无记录',
        otherDescription: '还没有已处理的事项',
      },
      card: {
        resolved: '已处理',
        dismissed: '已忽略',
        details: '详情',
        creatingRoom: '创建中...',
        processing: '处理中...',
      },
      action: {
        createRoom: '创建房间',
        dismiss: '忽略',
        accept: '接受',
        reject: '拒绝',
        markRead: '已读',
        startOnboarding: '开始',
        dismissOnboarding: '跳过',
        connectors: '接入连接器',
        inviteMembers: '邀请成员',
        viewTask: '查看执行详情',
      },
      toast: {
        accepted: '已接受邀请',
        dismissed: '已拒绝邀请',
        ignored: '已忽略',
        rejected: '已拒绝邀请',
        roomCreated: '房间已创建',
        executionRoomUnavailable: '该执行所在的房间已删除或不可用',
      },
      onboarding: {
        addConnectors: '添加连接器',
        addConnectorsSummary: '连接你的第一台服务器或云账号，让 {{brand}} 团队可以帮你管理基础设施',
        runAudit: '执行摸底报告',
        runAuditSummary: '对已连接的资源执行首次摸底，了解你的基础设施全貌',
        inviteMembers: '邀请团队成员',
        inviteMembersSummary: '邀请同事加入组织，一起协作管理基础设施',
        createRoom: '创建协作房间',
        createRoomSummary: '创建第一个团队协作房间，开始团队沟通',
      },
      scheduledTask: {
        completed: '定时任务已完成',
        failed: '定时任务执行失败',
        timeout: '定时任务已超时',
        titleWaitingHuman: '定时任务等待用户确认',
        titleWaitingApproval: '定时任务等待审批',
        summarySuccess: '定时任务“{{name}}”执行成功',
        summaryFailed: '定时任务“{{name}}”执行失败：{{error}}',
        summaryTimeout: '定时任务“{{name}}”已超时：{{error}}',
        summaryWaitingHuman: '定时任务“{{name}}”等待你确认',
        summaryWaitingApproval: '定时任务“{{name}}”等待审批',
      },
    },
    scheduledTasks: {
      title: '定时任务', subtitle: '按计划自动执行提示词并记录执行结果', create: '新建任务', createSubmit: '创建任务', edit: '编辑任务', search: '搜索任务', searchPlaceholder: '搜索任务名或提示词...', loading: '加载中...', empty: '没有匹配的任务', save: '保存', cancel: '取消', name: '任务名称', prompt: '提示词', expandPrompt: '展开', collapsePrompt: '收起', scheduleType: '执行计划', timeout: '超时时间', timeoutMinutes: '超时时间（分钟）', notifyOnDone: '接收任务通知', runNow: '立即执行', pause: '暂停', resume: '启用', delete: '删除', running: '已启用', paused: '已暂停', frequency: '执行频率', nextRun: '下次执行', nextRunShort: '下次', dueSoon: '即将执行', detail: '任务详情', minutes: '分钟', hours: '小时', intervalHours: '每 {{count}} 小时', intervalMinutes: '每 {{count}} 分钟', deleteTitle: '删除定时任务', deleteDescription: '确认删除任务“{{name}}”吗？删除后任务停止执行并从列表隐藏，原房间与历史对话不受影响。', confirmDeleteButton: '确认删除',
      createTitle: '新建定时任务',
      scheduleLabel: '定时任务',
      pickerConfirm: { title: '覆盖当前提示词', description: '将覆盖当前提示词，确认继续？', confirm: '确认覆盖', cancel: '取消', detail: '详情' },
      toolPickerMeta: { userHint: '定时任务会按计划自动触发，但部分 Skill 或高风险工具仍可能需要人工确认。请在提示词中写清 Skill/工具、连接器、目标资源和业务参数，减少执行时等待补充信息的情况。', noParameters: '该工具没有输入参数，无需配置。' },
      creator: { me: '我', member: '组织成员' },
      filter: { allStatus: '全部状态', status: '状态筛选', creator: '创建者筛选', allCreators: '全部创建者', searchCreator: '搜索创建者' },
      error: { load: '无法加载任务', create: '无法创建任务', update: '无法更新任务', run: '无法执行任务', delete: '无法删除任务' },
      drawer: { title: '定时任务', close: '关闭定时任务', boundTask: '本房间任务', moreActions: '更多操作', allTasks: '所有定时任务', nextRun: '下次执行', timeout: '超时时间', history: '执行历史', deleteTitle: '删除定时任务？', deleteDescription: '确认删除“{{name}}”？原房间与执行历史会保留。', confirmDelete: '确认删除', emptyTitle: '本房间还没有定时任务', emptyDescription: '任务将绑定当前房间，并按计划在这里执行。', create: '新建定时任务' },
      form: { name: '任务名称', namePlaceholder: '例如：每日站会提醒', prompt: '提示词', promptPlaceholder: '用自然语言描述要让 AI 做什么。例如：\n· 汇总我昨天的待办事项，按优先级排序\n· 检查指定主机上的 knoxd 进程状态，并在执行结果中说明异常\n· 调用 list_instances 拉取指定 ECS 实例并汇总状态', schedule: '执行计划', insertSkill: '插入 Skill', insertTool: '插入工具', promptHint: '定时任务会按计划自动触发，但部分 Skill 或高风险工具仍可能需要人工确认。请在提示词中写清 Skill/工具、连接器、目标资源和业务参数，减少执行时等待补充信息的情况。', weekday: '周几', weeklyPrefix: '每', monthDay: '每月几日', daySuffix: '日', time: '执行时间', onceAt: '执行时间', selectTime: '选择执行时间', am: '上午', pm: '下午', hour: '小时', minute: '分钟', done: '完成', every: '每', intervalValue: '间隔数值', intervalUnit: '间隔单位', localTimezone: '本地时区', advancedOptions: '高级选项', timeoutHint: '任务执行超过此时间将自动终止（1-30 分钟）。', notifyTitle: '执行结果通知', notifyHint: '任务执行后，按你勾选的状态在收件箱提醒你。定时执行通知创建者，立即执行通知发起人。', notifySuccess: '执行成功', notifyFailed: '执行失败', notifyTimeout: '执行超时', notifyWaitingHuman: '等待你确认' },
      schedule: { once: '不重复', daily: '每天', weekly: '每周', monthly: '每月', interval: '间隔' },
      weekday: { 0: '周日', 1: '周一', 2: '周二', 3: '周三', 4: '周四', 5: '周五', 6: '周六' },
      picker: { overwritePrompt: '将覆盖当前提示词，确认继续？' },
      skillPicker: { title: '选择 Skill', search: '搜索 Skill...', loadError: '无法加载可用技能', empty: '没有匹配的可用技能' },
      toolPicker: { title: '选择工具', search: '搜索工具...', loadError: '无法加载工具', empty: '没有匹配的工具', parameters: '参数配置', chooseHint: '← 先在左侧选择一个工具', select: '请选择', insert: '插入到提示词', required: '请输入必填参数', invalidInteger: '请输入有效的整数', invalidNumber: '请输入有效的数字', invalidBoolean: '请输入有效的布尔值', invalidArray: '请输入有效的 JSON 数组', invalidObject: '请输入有效的 JSON 对象', invalidValue: '参数值无效' },
      history: { empty: '暂无执行记录', viewDetails: '查看详情' }, paginationSummary: '共 {{total}} 条 · 第 {{page}} / {{pages}} 页', pageSizeLabel: '每页条数', pageSize: '每页 {{count}} 条', page: '第', jumpToPage: '跳转页码', previous: '上一页', next: '下一页',
      status: { success: '成功', failed: '失败', timeout: '已超时', running: '执行中', waiting_human: '等待用户确认', waiting_approval: '等待审批' },
    },
    workspaceSettings: {
      title: '管理工作空间', close: '关闭', name: '名称', nameAria: '工作空间名称', associatedSystem: '关联系统', description: '描述', descriptionAria: '工作空间描述',
      loadingSystem: '正在加载关联系统...', systemDeleted: '应用系统已删除', systemUnavailable: '应用系统暂时不可用', searchSystem: '搜索应用系统', selected: '已选择', systemWorkspaceDisabled: '内置工作空间，不允许删除或绑定应用系统。',
      members: '成员', loadingMembers: '正在加载成员...', noMembers: '暂无成员', removeMember: '移除{{name}}', remove: '移除', addMember: '添加成员', searchMember: '搜索成员', memberPlaceholder: '输入姓名或邮箱', searchingMembers: '正在搜索成员...', addNamedMember: '添加{{name}}', add: '添加',
      delete: '删除工作空间', save: '保存', confirmDeleteTitle: '确认删除工作空间', confirmDeleteDescription: '删除 {{name}} 后无法恢复。', cancel: '取消', confirmDelete: '确认删除',
      error: { loadDetails: '加载工作空间详情失败，请重试', loadMembers: '加载成员失败，请重试', loadSystems: '加载应用系统失败，请重试', searchMembers: '搜索成员失败，请重试', save: '保存工作空间失败，请重试', missingMembership: '成员记录缺少标识，无法移除', addMember: '添加成员失败，请重试', removeMember: '移除成员失败，请重试', delete: '删除工作空间失败，请重试' },
    },
    workspaceHome: {
      label: '工作空间', noDescription: '暂无描述', manage: '管理工作空间', rooms: '房间', roomCount: '{{count}} 个房间', addRoom: '添加房间', openRoomHint: '进入房间继续协作', noRooms: '暂无房间',
    },
    workspaceNav: {
      workspaces: '工作空间', rooms: '房间', expandWorkspace: '展开{{name}}', collapseWorkspace: '折叠{{name}}', expandWorkspacesGroup: '展开工作空间分组', collapseWorkspacesGroup: '收起工作空间分组', expandRoomsGroup: '展开房间分组', collapseRoomsGroup: '收起房间分组', moreWorkspaces: '更多工作空间',
    },
    workspaceCreate: {
      open: '新建工作空间', title: '新建工作空间', description: '让工作空间内的房间共享文件和上下文。', close: '关闭', fromApplication: '从应用系统创建', fromApplicationDescription: '带入已有系统上下文', custom: '自定义工作空间', customDescription: '用于跨系统协作', searchApplication: '搜索应用系统', name: '名称', descriptionLabel: '描述', back: '返回', cancel: '取消', create: '创建',
    },
    knowledge: {
      title: '知识库',
      roomDeskTitle: '知识',
      roomDeskSubtitle: '本房间沉淀的资料与记忆，AI 对话时自动参考',
      subtitle: '全局可见的已审核知识与草稿',
      closeAriaLabel: '关闭知识库面板',
      openChat: '打开对话',
      searchPlaceholder: '搜索标题、文件名、标签或 Room...',
      tagPlaceholder: '添加标签，回车保存',
      add: '添加',
      created: '创建于',
      tab: {
        knowledge: '知识',
        outputs: '产出物',
        outputsDesc: 'Room 产出物将在此显示',
      },
      memory: {
        updateCommand: '更新 Room Memory',
        loadError: 'Room Memory 加载失败，请稍后重试。',
        saveConflict: '保存失败，请刷新后合并最新内容。草稿已保留。',
        updatedBy: '由 {{user}} 编辑',
        reviewedByChronicler: '由 AI 整理',
        roomMember: '房间成员',
        notUpdated: '尚未创建',
        reviewRunning: 'AI 正在整理 Room Memory，完成后即可继续编辑。',
        empty: '这个 Room 还没有记忆',
        emptyHint: '你可以直接编辑，也可以在对话中告诉 AI“更新 Room Memory”，让它根据当前对话进行整理。',
        sharedHint: 'Room Memory 在本 Room 内共享，并会自动提供给本 Room 的 AI，帮助成员和 AI 快速接上当前上下文。',
        cardTitle: '房间记忆',
        cardDesc: 'AI 从对话中自动整理：正在解决的问题、关键结论、下一步计划…',
        cardBy: 'Chronicler',
        organizing: '整理中',
      },
      filter: {
        all: '全部',
        approved: '已审核',
        draft: '草稿',
        drafts: '草稿',
      },
      status: {
        approved: '已审核',
        draft: '草稿',
        rejected: '已拒绝',
        deleted: '已删除',
      },
      scope: {
        label: '范围',
        org: '全组织',
        room: '房间',
        organization: '组织',
        roomDescription: '仅当前 Room 成员可见',
        orgDescription: '组织内所有 Room 均可发现',
      },
      location: {
        cloud: '云端',
        local: '本地',
      },
      updatedAt: '更新于',
      time: {
        today: '今天',
        yesterday: '昨天',
      },
      storageFilter: {
        all: '全部',
        cloud: '云端存储',
        local: '本地存储',
      },
      fromRoom: '来自房间：{{room}}',
      tags: {
        label: '自定义标签',
        add: '+ 添加标签',
        addLabel: '添加标签',
      },
      sourceType: {
        user_upload: '用户上传',
        ai_generated: '{{brand}} 生成',
        ai_organized: 'AI 整理',
      },
      author: {
        ai: '{{brand}}',
      },
      permission: {
        notRoomMember: '仅当前 Room 成员可以操作此知识',
        notOrgMember: '仅组织成员可以操作此知识',
        authorOrOrgAdminRequired: '仅知识作者、组织管理员或组织所有者可以执行此操作',
        knowledgeNotReady: '知识正文处理完成后才能执行此操作',
        roomCloudOnly: '仅已就绪的 Room 云端知识可以发布到组织',
        alreadyOrg: '该知识已是组织知识',
        operationInProgress: '操作正在处理中，请稍候',
        deleted: '该知识已删除',
      },
      contentStatus: {
        processing: '处理中',
        ready: '已就绪',
        failed: '处理失败',
      },
      empty: {
        approvedTitle: '暂无知识',
        draftTitle: '暂无草稿',
        searchTitle: '未找到结果',
        defaultDesc: '审核通过后知识将显示在这里',
        searchDesc: '换个关键词试试',
        filterTitle: '没有符合条件的知识',
        filterDesc: '调整筛选条件后再试',
      },
      pagination: {
        previous: '上一页',
        next: '下一页',
        page: '第 {{current}} / {{total}} 页',
        total: '共 {{total}} 条',
        pageLabel: '第',
        pageNumber: '页码',
        pageOf: '/ {{total}} 页',
        pageSize: '{{size}} 条/页',
        pageSizeLabel: '每页条数',
      },
      upload: '添加',
      uploadAriaLabel: '添加知识',
      'upload.dropActive': '松开以上传文件',
      'upload.dropHint': '拖放文件到此处，或点击选择',
      'upload.addMore': '继续添加文件',
      'upload.removeFile': '移除 {{name}}',
      'upload.progress': '正在上传 {{current}} / {{total}}',
      'upload.dropOrClick': '或点击选择文件',
      'upload.supportedFormats': '支持 PDF、DOCX、PPTX、XLSX、CSV，以及常见文本、代码和配置文件',
      'upload.error.fileTooLarge': '文件过大（最大 {{size}}）',
      'upload.error.unsupportedType': '不支持的文件类型',
      'upload.maxSize': '最大 {{size}}',
      'upload.error.failed': '上传失败',
      'upload.error.pdfNoText': 'PDF 中未提取到文字，可能是扫描件。',
      'upload.error.tooManyFiles': '最多支持 {{count}} 个文件',
      'upload.error.batchTooLarge': '文件总大小超过 {{size}}',
      'upload.completed': '已新增或更新 {{count}} 个知识文件',
      'upload.cloudCompleted': '已新增 {{created}} 个知识，更新 {{updated}} 个知识',
      'upload.localCompleted': '已新增 {{created}} 个知识，更新 {{updated}} 个知识',
      'upload.localProcessing': '文件已发送到 {{daemon}}，正在处理中，可稍后在知识列表中查看。',
      'upload.submit': '上传',
      'upload.uploading': '上传中...',
      'upload.preparing': '准备中...',
      'upload.storage': '存储位置',
      'upload.cloudStorage': '云端存储',
      'upload.localStorage': '本地存储',
      'upload.cloudDescription': '直接上传到平台，无需安装 {{daemon}}',
      'upload.localDescription': '保存在所选 {{daemon}} 节点上',
      'upload.chooseFiles': '选择文件',
      'upload.singleFileLimit': '单文件最大 {{size}}',
      'upload.batchLimit': '最多 {{count}} 个 / 总计 {{size}}',
      'upload.fileCountLimit': '最多 {{count}} 个文件',
      'upload.step.label': '第 {{current}} 步，共 {{total}} 步',
      'upload.step.storageTitle': '选择知识保存位置',
      'upload.step.localTitle': '选择本地知识添加方式',
      'upload.step.cloudFilesTitle': '上传文件到云端',
      'upload.step.localFilesTitle': '上传文件到本地设备',
      'upload.step.guideTitle': '同步知识文件夹',
      'upload.local.targetAgent': '绑定 {{daemon}}',
      'upload.local.selectAgent': '选择一个 {{daemon}}',
      'upload.local.searchAgent': '搜索 Agent...',
      'upload.local.noAgentMatch': '未找到匹配的 Agent',
      'upload.local.delivery': '文件添加方式',
      'upload.local.managed': '上传文件',
      'upload.local.managedDescription': '从浏览器上传文件到所选 {{daemon}} 节点',
      'upload.local.managedScenario': '适合日常新增、单个文件更新或零散补充。同名文件更新原知识，不同名文件新增知识',
      'upload.local.manual': '文件夹同步',
      'upload.local.manualDescription': '在所选 {{daemon}} 节点维护整个知识文件夹，再执行同步',
      'upload.local.manualScenario': '适合知识库初始化、批量迁移或整体更新。新增、修改、删除和重命名会在同步后反映到知识列表',
      'upload.local.scope': '目标范围：{{scope}}（自动）',
      'upload.local.orgScope': '组织知识库',
      'upload.local.roomScope': '当前 Room',
      'upload.local.noAgent': '暂无可用的 {{daemon}}',
      'upload.local.noAgentHint': '请安装、启动或恢复最新版本的 {{daemon}}。',
      'upload.guide.ready': '从本地设备同步知识文件夹',
      'upload.guide.step1Title': '1. 打开知识文件夹',
      'upload.guide.step1Description': '登录运行所选 {{daemon}} 的设备并打开下面的文件夹。可复制路径，在终端或文件管理器中定位。',
      'upload.guide.step2Title': '2. 放入需要同步的文件',
      'upload.guide.step2Description': '将一个或多个支持的文件直接放入这个文件夹。暂不读取子文件夹中的文件。',
      'upload.guide.step3Title': '3. 同步到知识库',
      'upload.guide.step3Description': '在同一设备的终端中执行下面的命令，可在任意目录执行。命令成功后，知识列表会同步文件的新增、更新、删除和重命名。',
      'upload.guide.directory': '知识文件夹',
      'upload.guide.command': '同步命令',
      'upload.guide.copy': '复制',
      'upload.guide.rules': '新文件会创建知识；同一路径替换内容会更新原知识；删除文件会删除对应知识；重命名会删除旧知识并创建新知识。同步命令成功前，系统继续使用上一次成功版本。',
      'upload.dialog.title': '添加知识',
      'upload.dialog.scopeOrg': '保存为组织知识',
      'upload.dialog.scopeRoom': '保存为当前 Room 的知识',
      'upload.dialog.cancel': '取消',
      'upload.dialog.back': '返回',
      'upload.dialog.continue': '继续',
      'upload.dialog.done': '完成',
      'upload.dialog.closeAriaLabel': '关闭',
      action: {
        approve: '通过',
        reject: '拒绝',
        delete: '删除',
        confirmDelete: '确定删除这条知识？此操作不可撤销。',
        reference: '引用到对话',
        referenceHint: '引用知识到对话输入框',
        promote: '发布到组织',
        viewDetails: '查看详情',
        hideDetails: '收起详情',
      },
      detail: {
        loading: '加载中...',
        noContent: '暂无内容',
        loadError: '内容加载失败',
        processing: '正文正在解析，完成后即可预览和引用',
        failed: '正文处理失败，请检查文件后重新上传。',
      },
      local: {
        daemonLabel: 'Daemon：{{name}}',
        daemonOffline: 'Daemon 离线',
        updateFailed: '最新版本处理失败，当前仍使用上一次成功版本。',
        contentUnavailable: '本地正文暂不可用，请检查 Daemon 和源文件后重试。',
      },
      promote: {
        title: '发布到组织知识？',
        description: '发布后，组织内所有成员都可以查看。仅你、组织管理员和组织所有者可以修改标签或删除。此操作暂不支持撤回为 Room 知识。',
        processing: '正在发布…',
        converging: '正在完成发布，请勿重复操作',
        success: '已发布到组织知识',
        toastSuccess: '“{{title}}”已发布到组织知识',
        toastPending: '“{{title}}”正在发布，列表会自动更新',
        failed: '发布失败，知识仍保留在当前 Room',
      },
      delete: {
        title: '删除知识？',
        description: '删除后，这条知识会从知识列表中移除，也不能再在对话中引用。此操作不可撤销。',
        localDescription: '同时会删除本地设备上的源文件和解析内容。如果 Daemon 离线或删除失败，不会删除任何数据，知识仍可继续使用；请恢复 Daemon 后重试。',
        confirm: '删除',
        failed: '删除失败，请重试',
      },
    },
    settings: {
      title: '设置中心',
      description: '管理工作空间和个人偏好。',
      backToListAriaLabel: '返回设置列表',
      closeAriaLabel: '关闭设置',
      nav: {
        account: '账号',
        usage: '使用情况',
        appearance: '外观',
        organization: '组织',
        members: '成员',
        connectors: '连接器',
        webhooks: 'Webhooks',
        agents: '{{brand}} Daemon',        security: '安全策略',
        activity: '活动日志',
      },
      webhooks: {
        title: 'Webhooks',
        subtitle: '管理自动化告警接入，并查看实体匹配与处理结果',
        add: '添加 Webhook',
        catalogSubtitle: '选择要配置的告警来源',
        searchSources: '搜索告警来源...',
        noMatchingSources: '没有匹配的告警来源',
        category: { all: '全部', alerting: '告警' },
        loading: '正在加载 Webhook…',
        empty: '暂无 Webhook',
        loadError: '加载 Webhook 失败',
        name: '名称',
        namePlaceholder: '例如：生产环境告警',
        nameRequired: '请输入名称',
        description: '描述',
        connection: '连接器',
        selectConnection: '请选择连接器',
        searchConnections: '搜索连接器',
        noMatchingConnections: '没有匹配的连接器',
        connectionRequired: '请选择可用连接器',
        connectionHint: '选择能够访问当前告警来源的连接器。',
        addConnector: '还没有连接器？去「连接器」添加',
        connectorEnabled: '启用',
        connectorDisabled: '禁用',
        connectorOnline: '在线',
        connectorUnavailable: '不可用',
        createSubtitle: '配置 Prometheus 告警来源',
        backToList: '返回列表',
        prometheusAlert: 'Prometheus 告警',
        prometheusDescription: '接收 Alertmanager 告警并自动匹配实体',
        webhookDescription: '接收告警事件，并自动匹配语义网络中的实体。',
        templateDetails: 'Webhook 模板详情',
        alertIngress: '告警接入',
        templateCapability: '接收 Prometheus Alertmanager 推送的告警事件，自动匹配 Logos 语义网络中的实体并存储结果，供执行历史查看与后续分析。',
        configureAndCreate: '配置并创建',
        configureTitle: '配置 Prometheus Webhook',
        close: '关闭',
        createWebhook: '创建并继续',
        descriptionPlaceholder: '可选，简单备注一下',
        alertmanagerSetupTitle: '接下来，在 Alertmanager 里完成配置',
        alertmanagerStepOne: '把下面的 receiver 添加到 alertmanager.yml。',
        receiverCopyIncludesToken: '复制 receiver 配置时将包含完整接入令牌。',
        alertmanagerStepTwo: '把需要推送的告警路由到这个 receiver。',
        alertmanagerStepThree: '按当前部署方式 reload 或重启 Alertmanager，使配置生效。',
        copyReceiverConfig: '复制 receiver 配置',
        copyRouteConfig: '复制 route 配置',
        prometheusDocs: 'Prometheus 配置文档',
        create: '创建',
        cancel: '取消',
        createError: '创建 Webhook 失败',
        updateError: '更新 Webhook 失败，请重试。',
        saveAndContinue: '保存并继续',
        editName: '编辑 Webhook 名称',
        editDescription: '编辑 Webhook 描述',
        emptyDescription: '未设置描述',
        editConnector: '编辑连接器',
        updateConfiguration: '更新配置',
        connectionUnavailableError: '该连接器不可用或你已无权访问，请选择其他连接器后重试。',
        connectionInactiveError: '该连接器已停用，请先在「连接器」中启用，或选择其他连接器。',
        connectionTypeMismatchError: '该连接器与当前 Webhook 类型不匹配，请选择 Prometheus 连接器。',
        created: 'Webhook 已创建',
        ingressUrl: '{{brand}} 接入地址',
        token: '{{brand}} 接入令牌',
        copyUrl: '复制地址',
        copyToken: '复制令牌',
        copyValue: '复制内容',
        requestHeaders: '请求头',
        requestHeaderName: '请求头',
        requestHeaderValue: '值',
        addAlertmanager: '让 Captain 自动配置',
        alertmanagerGuide: '如果不想手动修改配置，可以让 Captain 使用可访问 Alertmanager 的连接器自动完成 receiver 和 route 写入、reload 校验及只读验证；执行有影响的操作前会先给出方案，你只需确认。',
        autoConfigure: '自动配置并验证',
        autoConfigureHint: '自动备份 Alertmanager 配置，幂等合入本 webhook 的 receiver 与 route，校验并 reload，最后读取已应用配置确认已接入。',
        manualConfigure: '我手动配置',
        verifyNow: '验证配置',
        verifyNowHint: '读取当前绑定连接器对应的 Alertmanager 配置，并检查此 Webhook 的 receiver 和 route 是否存在。不会修改 Alertmanager 配置。',
        configurationChoiceTitle: '选择下一步操作',
        configurationChoiceHintCreate: '选择自动配置、手动配置，或直接完成。',
        configurationChoiceHintUpdate: '选择自动配置、验证现有配置、手动配置，或直接完成。',
        configurationTabsLabel: 'Webhook 配置方式',
        configurationTabAutomatic: '自动配置',
        configurationTabManual: '手动配置',
        manualConfigurationTitle: '需要手动配置 Alertmanager',
        configureAndVerify: '配置并验证',
        verifyOnly: '仅验证',
        loadingRoom: '正在加载房间',
        verifyConfiguration: '验证配置',
        verificationTimeoutGuidance: '当前 Alertmanager 配置里未确认到此 Webhook。请执行“配置并验证”，或检查手动配置后再次验证。',
        automaticRoutingScopeLabel: '告警范围：',
        automaticRoutingScope: '绑定的 Alertmanager 的全部告警；现有通知路由会保留。',
        automaticVerificationMethodLabel: '验证方式：',
        automaticVerificationMethod: '读取已应用的 Alertmanager 配置，并检查此 Webhook 的 receiver 和 route 是否存在。',
        agentConfigurationScopeLabel: '接入范围：',
        agentConfigurationScope: '可以接入全部告警，也可以只选择需要的告警。',
        agentConfigurationMethodLabel: '配置内容：',
        agentConfigurationMethod: '完成告警接入和通知配置，并检查消息能否正常送达。',
        configurePlanTitle: '开始前确认',
        inspectingTarget: '正在检查绑定的 Alertmanager 目标...',
        planField: { target: '目标地址', write_mode: '配置方式', config_source: '配置来源', config_file: '配置文件', k8s_namespace: 'Kubernetes 命名空间', k8s_secret_name: 'Kubernetes Secret' },
        writeModeLocalFile: 'Alertmanager 所在主机的配置文件',
        writeModeK8s: 'Kubernetes Secret',
        writeModeManual: '无法自动写入，需要手动配置',
        manualReasonFallback: '未在当前连接对应的 Alertmanager 上检测到可写入的配置入口。',
        manualConfigurationGuidance: '请按手动配置指引修改 Alertmanager，然后运行验证。',
        openManualConfiguration: '查看手动配置',
        configurationFailureNoReason: '服务端未返回具体失败原因。',
        configurePlanIntro: '请确认目标和操作范围。点击开始前不会修改任何配置。',
        planStepInspect: '确认绑定的 Alertmanager 和配置位置',
        planStepBackup: '保存当前配置，便于异常时恢复',
        planStepConfigure: '添加此 Webhook 的接收器和路由，不替换已有接收器',
        planStepValidate: '检查更新后的配置是否有效',
        planStepReload: '让更新后的配置生效',
        planStepVerify: '读取已应用配置并确认已接入 {{brand}}',
        confirmAndStart: '确认并开始',
        configurationProgress: '配置进度',
        verificationConfirmationTitle: '开始验证前确认',
        verificationConfirmationIntro: '请确认绑定的 Alertmanager 和验证范围。验证只读取当前配置，不会修改任何内容。',
        verificationProgress: '验证进度',
        startVerification: '开始验证',
        closePanel: '关闭',
        manualConfigurationReason: '系统找到了 Alertmanager 服务地址，但没有找到可以安全自动修改的配置文件或 Kubernetes Secret。这可能是 ontoxd 连接权限不足、Alertmanager 运行方式不受支持，或当前连接不是实际运行节点。请检查连接权限和部署方式，或按手动配置指引修改后再验证。',
        manualConfigurationReasonIntro: '系统找到了 Alertmanager 服务地址，但没有找到可以安全自动修改的配置文件或 Kubernetes Secret。可能是：',
        manualConfigurationCausePermission: 'ontoxd 连接权限不足',
        manualConfigurationCauseMode: 'Alertmanager 运行方式不受支持',
        manualConfigurationCauseNode: '当前连接不是实际运行节点',
        manualConfigurationRawTitle: '探测日志输出',
        taskStatusStarting: '正在启动验证任务...',
        taskStatusInspecting: '正在检查绑定的 Alertmanager 目标...',
        taskStatusConfiguring: '正在写入 Alertmanager 配置...',
        taskStatusValidating: '正在校验更新后的配置...',
        taskStatusReloading: '正在重新加载 Alertmanager...',
        taskStatusVerifying: '正在读取已应用配置...',
        stepLabelInspect: '检查 Alertmanager 目标',
        stepLabelBackup: '备份当前配置',
        stepLabelConfigure: '写入 Webhook receiver 与路由',
        stepLabelValidate: '校验更新后的配置',
        stepLabelReload: '重新加载 Alertmanager',
        stepLabelVerify: '读取已应用配置并确认 {{brand}} 接入',
        configurationCompleted: 'Webhook 配置已验证',
        configurationCompletedEvidence: '当前 Alertmanager 配置中已包含此 Webhook 的 receiver 和 route。',
        configurationAppliedVerificationFailed: '配置已应用，但投递验证失败',
        configurationFailed: '配置失败',
        configurationAwaitingWrite: '未检测到写入能力——请手动配置，然后运行验证',
        statusRefreshFailed: '暂时无法获取最新任务状态，正在自动重试...',
        stepStatusRunning: '进行中',
        stepStatusSucceeded: '已完成',
        stepStatusFailed: '失败',
        stepStatusPending: '待执行',
        saveAndVerify: '保存并验证',
        saveOnly: '仅保存',
        connectionChangedHint: '绑定的连接器已变更',
        reconfigure: '重新配置',
        done: '完成',
        enabled: '启用',
        disabled: '禁用',
        showMoreLabels: '展开更多标签',
        showFewerLabels: '收起标签',
        collapseLabels: '收起',
        alertContentLabel: '告警内容',
        alertInformationLabel: '告警信息',
        expandAlertContent: '展开',
        collapseAlertContent: '收起',
        expandAnnotationValue: '展开',
        collapseAnnotationValue: '收起',
        toggleError: '更新 Webhook 状态失败',
        back: '返回',
        edit: '编辑',
        editTitle: '更新 Webhook 配置',
        save: '保存',
        delete: '删除',
        deleteTitle: '删除 Webhook',
        moreActions: '更多操作',
        deleteDescription: '删除后将停止接收新告警，并且无法再从 {{brand}} 查看执行历史。',
        confirmDelete: '确认删除',
        deleteError: '删除 Webhook 失败。当前 Webhook 仍保持可用，可稍后重试。',
        showToken: '显示令牌',
        hideToken: '隐藏令牌',
        test: '执行内部测试',
        verifyIngress: '验证真实入口',
        ingressVerified: '接入令牌验证通过',
        justNow: '刚刚',
        createdAt: '创建时间',
        rateLimit: '接收上限',
        rateLimitTooltip: '这是你的告警源每分钟能往 Ontox 推送的最大次数。一次推送无论带了多少条告警，都只算一次。超过上限的推送会被直接拒收。',
        rateLimitValue: '{{count}} 次/分钟',
        executions: '告警记录',
        executionCount: '共 {{count}} 条',
        executionHistoryHelp: '每行代表一次收到的告警组。严重度取自告警的 severity 标签；处理状态表示 Knox 是否成功接收并处理本次数据。',
        severityHelp: '由告警来源上报的严重度',
        severity: {
          critical: '严重',
          warning: '警告',
          info: '提示',
          other: '其他',
          unknown: '未上报',
        },
        executionStatus: {
          success: '成功',
          completed: '成功',
          failed: '失败',
          timeout: '超时',
          pending: '处理中',
          running: '处理中',
        },
        alertStatus: { firing: '告警中', resolved: '已恢复', grouped: '分组' },
        alertStatusFilter: '告警状态',
        allAlertStatuses: '全部状态',
        unnamedAlert: '未命名告警',
        firstTriggered: '首次触发',
        recovered: '恢复',
        summaryLabel: '摘要',
        descriptionLabel: '描述',
        labelsLabel: '标签',
        annotationsLabel: '注释',
        linkedEntity: '已关联实体',
        candidateEntity: '候选实体',
        unmatchedEntity: '未匹配实体',
        noEntityMatchDescription: '当前语义网络中未找到与该告警对象匹配的实体。',
        alertCount: '共 {{count}} 条告警',
        alertGroup: '本组告警（{{count}} 条 · 逐条匹配实体）',
        noExecutions: '暂无告警记录',
        viewAll: '查看全部',
        previous: '上一页',
        next: '下一页',
        page: '第',
        jumpToPage: '跳转页码',
        pageSizeLabel: '每页条数',
        pageSize: '每页 {{count}} 条',
        paginationSummary: '共 {{total}} 条 · 第 {{page}} / {{pages}} 页',
        matched: '已匹配',
        uncertain: '待确认',
        unmatched: '未匹配',
        candidateMatches: '发现 {{count}} 个候选实体，需更多证据确认',
        confirmedMatches: '已确认 {{count}} 个关联实体',
        candidate: '候选',
        confirmed: '已确认',
        exactEvidence: '匹配证据：{{field}} = {{value}}',
        ruleConfidence: '规则置信度 {{confidence}}%',
        rawPayload: '原始 Payload',
        copyRawPayload: '复制原始 Payload',
        keywordPlaceholder: '搜索告警名、标签、注释或告警内容',
        clearSearch: '清除搜索',
        quickRange: '快速选择',
        allTime: '全部时间',
        today: '今天',
        last1Hour: '近 1 小时',
        last24Hours: '近 24 小时',
        last7Days: '近 7 天',
        last30Days: '近 30 天',
        customRange: '指定范围',
        now: '现在',
        startTime: '开始时间',
        endTime: '结束时间',
        endBeforeStart: '结束时间必须晚于开始时间',
        to: '至',
        clear: '清除',
        apply: '确定',
        filter: '筛选',
        reset: '重置',
        unlimited: '不限',
        lastTriggered: '上次触发 {{time}}',
        lastTriggeredLabel: '上次触发',
        lastTriggeredRelative: '上次触发：{{time}}',
        neverTriggered: '尚未触发',
      },
      agents: {
        title: '{{brand}} Daemon 管理',
        description: '监控和管理已部署的 {{brand}} Daemon',
        install: '安装 {{brand}} Daemon',
        installTitle: '安装 {{brand}} Daemon',
        installBack: '返回',
        choosePlatform: '选择部署平台',
        copyCommand: '复制',
        copyTooltip: '复制到剪贴板',
        copied: '已复制!',
        cancel: '取消',
        verify: '验证安装',
        noAgents: '暂无已注册的 {{brand}} Daemon',
        loading: '加载中...',
        installInfoLoadFailed: '加载安装信息失败。',
        manifestLoadFailed: '生成 Kubernetes 安装命令失败。',
        stepInstall: '安装 {{brand}} Daemon',
        clusterName: '集群名称',
        clusterNameHint: '给集群设置一个便于识别的名称，例如 prod-cn。',
        namespace: 'Namespace',
        namespaceHint: '安装 Daemon 使用的 Kubernetes Namespace。',
        namespaceInvalid: '请输入 1–63 位小写字母、数字或连字符，并以字母或数字开头和结尾。',
        generateInstallCommand: '生成安装命令',
        deleteTitle: '删除 {{brand}} Daemon',
        deleteConfirm: '此操作将永久移除 "{{name}}"。如果该 Daemon 当前在线，连接也将被断开。',
        deleteInputLabel: '输入显示名称以确认',
        deleteAction: '删除',
        deleteFailed: '删除 Daemon 失败',
        deleteSuccess: '已删除 Daemon "{{name}}"',
        deleting: '删除中...',
        selectAll: '全选离线 Daemon',
        selectRow: '选择 {{name}}',
        onlineNotSelectable: '在线 {{brand}} Daemon 不可批量删除',
        selectedCount: '已选 {{count}} 项',
        batchDelete: '批量删除',
        clearSelection: '清除选择',
        batchDeleteTitle: '批量删除 {{brand}} Daemon',
        batchDeleteConfirm: '以下 {{count}} 个 Daemon 将被永久移除：',
        batchDeleteAction: '删除',
        batchDeleteSuccess: '已删除 {{count}} 个 Daemon',
        batchDeletePartialTitle: '部分 Daemon 删除失败',
        batchDeleteFailedTitle: 'Daemon 删除失败',
        columns: {
          name: '显示名称',
          uuid: 'UUID',
          type: '类型',
          networks: '网络',
          status: '状态',
          version: '版本',
          lastSeen: '最后心跳',
          actions: '操作',
        },
        status: {
          online: '在线',
          offline: '离线',
          pending: '等待中',
        },
        confirmStop: '确认停止 {{brand}} Daemon "{{name}}"？它将离线。',
        confirmRestart: '确认重启 {{brand}} Daemon "{{name}}"？它会短暂离线。',
        confirmUninstall: '确认卸载 {{brand}} Daemon "{{name}}"？请输入显示名称以确认。',
        platformLinux: {
          title: 'Linux 服务器',
          desc: 'CentOS / Ubuntu / Debian，支持 x86_64 和 ARM64。',
        },
        platformK8s: {
          title: 'Kubernetes',
          desc: 'StatefulSet 部署，自动 RBAC。',
        },
        commandsTitle: '{{brand}} Daemon 常用命令',
        commandsDesc: '复制以下命令，在目标机器上执行。',
        cmdVerify: '验证状态',
        cmdLogs: '查看日志',
        cmdRestart: '重启',
        cmdStop: '停止',
        cmdUpgrade: '升级版本',
        cmdUninstall: '卸载',
        operations: {
          open: '运维命令',
          close: '关闭运维命令',
          title: '运维命令 · {{name}}',
          k8sContextHint: '执行前请确认 kubectl 当前连接到 {{cluster}} 对应的集群。',
          versionUpgrade: '版本升级',
          upgradeUnavailable: '暂时无法获取升级信息，其他运维命令仍可使用。',
        },
        accessMode: '访问模式',
        readWrite: '读写',
        readOnly: '只读',
        readWriteDesc: '完整控制：可创建、更新、删除工作负载（Deployment、StatefulSet、CronJob 等），进入 Pod 执行命令，扩缩容服务，以及管理 Prometheus 告警规则。',
        readOnlyDesc: '仅可观测：查看工作负载、日志和事件，不允许任何修改操作。',
      },
      profile: {
        title: '账号',
        description: '管理你的个人信息。',
        displayName: '显示名称',
        avatar: '头像',
        uploadAvatar: '上传头像',
        changeAvatar: '更换头像',
        removeAvatar: '移除头像',
        email: '邮箱',
        saveChanges: '保存更改',
        saving: '保存中...',
        loading: '加载中...',
        loadError: '加载个人信息失败',
      },
      appearance: {
        title: '外观',
        description: '自定义 {{brand}} 的外观和体验。',
        modeTitle: '外观模式',
        light: '浅色',
        dark: '深色',
        system: '跟随系统',
        languageTitle: '界面语言',
        debugMode: '调试模式',
        debugModeDescription: '显示所有 Agent 工作步骤，不隐藏线程消息。',
      },
      organization: {
        title: '组织',
        description: '查看组织详情。',
        logo: 'Logo',
        name: '名称',
        slug: '标识符',
        slugCopy: '复制标识符',
        id: '组织 ID',
        role: '你的角色',
        switchOrg: '切换组织',
        loading: '加载中...',
        loadError: '加载组织信息失败',
        notFound: '未找到组织。',
      },
      usage: {
        title: '使用情况',
        description: '监控积分、充值记录和使用明细。',
        creditsLow: '积分即将不足',
        creditsExhausted: '积分已耗尽',
        creditsExhaustedDesc: '积分已耗尽，请充值后继续使用。',
        requestRecharge: '+ 申请充值',
        rechargeDialogTitle: '申请充值',
        rechargeDialogDesc: '积分不足，请填写充值申请原因，管理员将审核并处理。',
        rechargeDialogReason: '原因',
        rechargeDialogPlaceholder: '请描述您需要充值的原因...',
        rechargeDialogCancel: '取消',
        rechargeDialogSubmit: '提交',
        rechargeDialogSubmitting: '提交中...',
        rechargeDialogSuccess: '申请已提交，管理员将尽快审核。',
        viewDetails: '查看使用明细 →',
        currentBalance: '当前积分',
        credits: '积分',
        upgrade: {
          title: '升级套餐',
          free: 'Free',
          pro: 'Pro',
          freePrice: '$0',
          freeCredits: '注册即送 {{count}} 积分',
          freeDesc: '需要更多？随时购买积分升级为 Pro。',
          currentPlan: '当前套餐',
          proDesc: '积分永不过期，选择适合你的积分包',
          proDiscount: '立省 {{discount}}%',
          upgradeBtn: ' 升级 ',
          addCreditsBtn: ' 购买积分 ',
          buyFor: '支付 {{currency}}{{price}}',
          quantity: '购买份数',
          total: '合计',
          payNow: ' 立即支付 ',
          processing: '跳转中...',
          questions: '有疑问？联系我们',
          freeFeatures: ['所有 AI 功能', '无限连接器', '团队（最多 3 人）', '社区支持'],
          proFeatures: ['所有 AI 功能', '无限连接器', '团队（最多 50 人）', '优先支持'],
        },
        rechargeHistory: '充值记录',
        records: '{{count}} 条记录',
        noRecords: '暂无记录',
        pageSize: '{{count}} 条/页',
        recharge: '充值',
        detailsTitle: '使用明细',
        detailsDesc: '积分消耗记录。',
        startDate: '开始日期',
        endDate: '结束日期',
        to: '至',
        totalUsed: '共消耗 {{credits}} 积分',
        loading: '加载中...',
        columns: {
          user: '用户',
          time: '时间',
          quota: '积分',
        },
        checkout: {
          confirming: '确认支付中...',
          success: '积分已到账！',
          failed: '充值失败：{{error}}',
          timeout: '支付确认需要更长时间，积分会自动到账 — 若几分钟后仍未到账，请联系客服',
          purchaseSuccess: '积分购买成功！',
          purchaseCancelled: '购买已取消。',
          pendingOrderTitle: '有未完成的订单',
          pendingOrderHint: '请在支付宝完成支付，或取消后重新下单。未支付订单 30 分钟后自动关闭。',
          cancelOrder: '取消订单',
          cancelling: '取消中...',
          channelAlipay: '支付宝',
          unitPrice: '单包价格',
          unitCredits: '单包积分',
          totalCredits: '到账积分',
          scrollHint: '下滑查看更多',
        },
      },
      members: {
        title: '成员',
        count: '{{count}} 位成员',
        countPlural: '{{count}} 位成员',
        inOrg: '。',
        loading: '加载中...',
        loadError: '加载成员列表失败',
        empty: '暂无成员',
        invite: '邀请',
        searchPlaceholder: '按姓名或邮箱搜索…',
        inviteTitle: '邀请团队成员',
        inviteDescription: '分享此邀请码或链接以邀请他人加入你的组织。',
        joinCodeLabel: '邀请码',
        inviteLinkLabel: '邀请链接',
        inviteCopy: '复制',
        inviteCopied: '已复制！',
        inviteExpiryNote: '此链接 7 天内有效。',
        inviteExpired: '此邀请已过期。',
        inviteExpiresIn1Day: '1 天后过期。',
        inviteExpiresInDays: '{{days}} 天后过期。',
        inviteRegenerate: '重新生成',
        inviteToRoom: {
          title: '邀请加入房间',
          userLabel: '用户',
          searchPlaceholder: '按姓名或邮箱搜索',
          noUsersFound: '未找到 "{{query}}" 相关用户',
          reasonLabel: '邀请理由',
          reasonOptional: '可选',
          reasonPlaceholder: '为什么要邀请这些用户？',
          selectedCount: '已选 {{count}} 位',
          inviteButton: '邀请',
          inviteButtonWithCount: '邀请 ({{count}})',
          cancelButton: '取消',
          inviteFailed: '邀请一个或多个用户失败。',
        },
        regenerateConfirmTitle: '重新生成邀请码？',
        regenerateConfirmDescription: '当前邀请码将失效，已分享出去的链接将无法使用。',
        regenerateCancel: '取消',
        regenerateConfirm: '确认重新生成',
        inviteGenerateError: '生成邀请链接失败。',
        memberLimitExceeded: '已达到组织成员上限。',
        quotaHint: '请移除现有成员或升级套餐。',
        you: '（你）',
        role: {
          owner: '所有者',
          admin: '管理员',
          member: '成员',
          change: '更改角色',
        },
        status: {
          active: '正常',
          disabled: '已禁用',
        },
        pagination: {
          showing: '第 {{from}}-{{to}} 条，共 {{total}} 条',
          previous: '上一页',
          next: '下一页',
        },
        actions: {
          disable: '禁用',
          enable: '启用',
          remove: '移除',
        },
      },
      connectors: {
        title: '连接',
        loading: '加载中...',
        emptyTitle: '暂无连接器',
        emptyDescription: '连接外部服务，赋能 AI Agent。',
        addConnector: '添加连接器',
        manageConnectors: '管理连接器',
        catalogTitle: '可用连接器',
        backToList: '返回列表',
        searchPlaceholder: '搜索连接器...', pageSearchLabel: '搜索已配置连接器', pageSearchPlaceholder: '搜索名称、类型、所有者或状态', statusFilter: '可用状态筛选', statusAll: '全部', statusActive: '可用', statusInactive: '已停用', pageNoResults: '没有符合当前筛选的连接器。', toggleConfirmTitle: '确认变更“{{name}}”状态？', toggleConfirmDescription: '变更后会影响连接器是否可用，不影响已经开始的执行。', toggleConfirm: '确认变更 {{name}} 状态', toggleCancel: '取消', togglePending: '正在更新…', toggleSuccess: '“{{name}}”状态已更新', builtInManaged: '系统内置，不可停用',
        categoryAll: '全部',
        categoryInfra: '基础设施',
        categoryCollaboration: '协作办公',
        noResults: '未找到连接器',
        retry: '重试',
        bundle: '组合',
        team: '团队',
        personal: '个人',
        knoxdPending: '待配置',
        knoxdConfigured: '已配置',
        knoxdOffline: 'Agent 离线',
        configModal: {
          title: '添加 {{name}}',
          connectionName: '连接名称',
          connectionNamePlaceholder: '例如：生产环境云账号',
          shareWithTeam: '共享给团队',
          teamDescription: '组织内所有成员都可以使用此连接',
          personalDescription: '个人连接：仅你可以使用',
          create: '创建连接',
          creating: '创建中...',
          oauthHint: '点击授权按钮后，将跳转到第三方服务进行授权。',
          oauthAuthorize: '授权',
          authorizing: '正在跳转...',
          credentialStorageMode: '凭证使用方式',
          cloudStorage: '云端保存 Role ARN',
          cloudStorageDesc: 'Ontox 保存 Role ARN，执行时获取临时凭证。无需 Ontoxd。',
          knoxdLocal: '本地存储',
          knoxdLocalDesc: '仅在所选 {{daemon}} 节点保存角色引用；STS 临时凭证只驻留内存。',
          noOnlineAgentGuide: '请安装或启动 {{daemon}} Agent，然后选择一个在线 Agent 继续。',
          agentLoadError: '无法加载 {{daemon}} Agent，请检查连接后重试。',
          retryAgents: '重试',
          knoxdSuccessTitle: '连接已创建',
          createKnoxdSuccess: '云连接器创建成功。请在所选 {{daemon}} 节点运行以下命令；首次执行会初始化凭证，重复执行会更新凭证：',
          close: '关闭',
        },
        cloudAuthorization: {
          title: '安全授权云账号',
          subtitle: '通过专用角色授予短期访问，无需提供 AccessKey 或 SecretKey。',
          accountId: '云账号 ID',
          accountIdPlaceholder: '请输入云账号 ID',
          accountIdLabel: { aliyun: '阿里云账号 ID', tencent: '腾讯云主账号 UIN', volcengine: '火山引擎 Account ID' },
          accountIdPlaceholderByProvider: { aliyun: '请输入您的阿里云账号 ID', tencent: '请输入您的腾讯云主账号 UIN', volcengine: '请输入您的火山引擎 Account ID' },
          accountIdInvalid: '云账号 ID 只能包含数字。',
          accountIdHelp: {
            aliyun: '在阿里云控制台右上角打开账号菜单，进入“安全设置”即可查看账号 ID。',
            tencent: '在腾讯云控制台右上角打开账号菜单，进入“账号信息”即可查看主账号 UIN。',
            volcengine: '在火山引擎控制台右上角打开账号菜单，进入“账号管理 > 基本信息”即可查看 Account ID。',
          },
          providers: { aliyun: '阿里云 RAM', tencent: '腾讯云 CAM', volcengine: '火山引擎 IAM' },
          steps: { 0: '创建云角色', 1: '验证角色并启用连接' },
          account: '云账号',
          editAccount: '修改云账号',
          regenerateSession: '重新开始授权',
          cancel: '取消',
          role: '待创建角色',
          permissionsTitle: '权限包',
          permissionsHint: '按用途拆分策略，便于审计、调整和随时撤销。',
          leastPrivilege: '最小权限',
          resourceDiscovery: '资源发现',
          resourceDiscoveryDesc: '读取云资源清单与拓扑，固定开启。',
          operationsExecution: '运维执行',
          operationsExecutionDesc: '通过云助手执行经过审批的运维命令。',
          observabilityConfig: '观测配置',
          observabilityConfigDesc: '读取并配置支持的监控与可观测集成。',
          securityNotice: 'Ontox 仅在所选守护进程中保存角色引用；STS 临时凭证只存在于内存中，绝不落盘。',
          cloudSecurityNotice: '平台仅保存角色引用；STS 临时凭证只存在于执行内存中，绝不落盘。',
          localSecurityNotice: 'Ontox 仅在所选 {{daemon}} 中保存角色引用；STS 临时凭证只存在于内存中，绝不落盘。',
		  status: { pending: '已准备授权', authorizing: '等待完成云侧配置', verifying: '正在验证云角色', succeeded: '已配置', failed: '授权需要处理', connectionCreateFailed: '连接器未创建', cancelled: '授权已取消' },
		  guidance: { pending: '打开云角色配置指引，完成后回到这里验证。', authorizing: '在云控制台完成引导配置，然后回到这里发起验证。', verifying: '正在 AssumeRole、核对身份并保存角色引用。', succeeded: '连接器已配置，可以直接使用。', failed: '按错误提示修复后，可从失败步骤继续重试。', cancelled: '准备好后可以重新发起授权。' },
          viewConnectionDetails: '查看连接器详情',
          openCloud: '打开云侧配置',
          cloudShellCommand: 'CloudShell 配置命令',
          rosTemplate: 'ROS 角色模板',
          rosUpdateTemplate: 'ROS 权限更新模板',
          tencentCamTitle: '创建腾讯云 CAM 角色',
          tencentRoleName: '角色名称',
          tencentTrustedUin: '可信 Ontox UIN',
          tencentPoliciesTitle: '请同时选择以下两项预设策略',
          tencentPolicyReadOnlyDesc: '只读访问所有腾讯云资源，后续新增资源发现能力无需逐项补充权限。',
          tencentPolicyTatDesc: '管理自动化助手 TAT，用于远程执行命令、查询执行结果和文件传输。',
          tencentCamStep1: '进入腾讯云后点击“新建角色”，在“选择角色载体”中选择“腾讯云账户”。',
          tencentCamStep2: '选择“其他主账号”，账号 ID 填写下方值，主体身份保持“所有身份”；不要勾选控制台访问和外部 ID。',
          tencentCamStep3: '在“配置角色策略”中，按名称搜索并勾选下方两项腾讯云预设策略。',
          tencentCamStep4: '角色标签无需填写，直接点击“下一步”。',
          tencentCamStep5: '在审阅页填写下方角色名称并完成创建，然后回到 Ontox 验证。',
          openTencentCam: '查看 CAM 配置步骤',
          createTencentRole: '前往腾讯云，开始配置',
          manualRole: {
            trustedAccountId: '可信 Ontox 账号 ID',
            roleName: '角色名称',
            policyDescriptions: {
              ReadOnlyAccess: '只读访问云资源，后续新增资源发现能力无需逐项补充权限。',
              AliyunECSAssistantFullAccess: '管理阿里云 ECS 云助手，用于远程执行命令、查询执行结果和文件传输。',
              AliyunARMSFullAccess: '管理 ARMS 告警集成、Webhook 联系人和通知策略。',
              ECSFullAccess: '用于火山引擎 ECS 远程命令执行及相关 ECS 管理操作。',
            },
            aliyun: {
              title: '创建阿里云 RAM 角色',
              step1: '进入 RAM 角色页面，点击“创建角色”，可信实体类型选择“云账号”。',
              step2: '选择“其他云账号”，填写下方可信 Ontox 账号 ID；外部 ID 保持关闭或留空，然后点击“确定”。',
              step3: '在弹出的“创建角色”窗口中填写下方角色名称，然后点击“确定”。',
              step4: '进入刚创建的角色详情，打开“权限管理”页签。',
              step5: '点击“新增授权”，按名称搜索并添加下方三项推荐系统策略。',
              step6: '授权完成后回到 Ontox，开始验证。',
              openConsole: '前往阿里云，开始配置',
            },
            volcengine: {
              title: '创建火山引擎 IAM 角色',
              step1: '进入 IAM 角色页面，点击“新建角色”，信任身份类型选择“账号”。',
              step2: '进入“配置角色信息”，填写下方角色名称；账号范围选择“其他账号”，填写下方可信 Ontox 账号 ID，外部 ID 保持关闭或留空。',
              step3: '点击下一步进入“添加权限”，按名称搜索并添加下方两项火山引擎系统预设策略。',
              step4: '完成角色创建，然后回到 Ontox 开始验证。',
              openConsole: '前往火山引擎，开始配置',
            },
          },
          setupArtifactHint: '仅为本次会话生成，不包含 AccessKey 或 STS 临时凭证。',
          copyCommand: '复制',
          copied: '已复制',
          verify: '我已完成，开始验证',
          verifying: '正在验证…',
          connectionReady: '连接器已配置，可以使用。',
          retry: '重试',
          useExistingRole: '改用已有角色',
          useGuidedAuthorization: '使用引导式安全授权',
          verifyExistingRole: '验证并连接',
          existingRoleTitle: '验证已有云角色',
          existingRoleDesc: '粘贴已有角色引用。连接启用前，Ontox 会验证角色信任关系和所需权限。',
          existingRoleSwitch: { aliyun: '已经有 RAM 角色 ARN？直接验证', tencent: '已经有 CAM 角色 QCS？直接验证', volcengine: '已经有 IAM 角色 TRN？直接验证' },
          backToRoleGuide: '返回创建角色引导',
          existingRoleLabel: { aliyun: 'RAM 角色 ARN', tencent: 'CAM 角色 QCS', volcengine: 'IAM 角色 TRN' },
          existingRolePlaceholder: { aliyun: 'acs:ram::123456789:role/example', tencent: 'qcs::cam::uin/123456789:roleName/example', volcengine: 'trn:iam::123456789:role/example' },
          errors: {
            CLOUD_ROLE_REF_INVALID: '请输入与当前云账号匹配的完整角色 ARN、QCS 或 TRN。',
            ASSUME_ROLE_FAILED: '未能验证云侧角色。请确认云端配置已成功完成，然后重新打开配置并再次验证。',
            STS_ROLE_NOT_FOUND: '未找到预期角色。请交给云管理员完成角色配置，或改用管理员已经创建的角色。',
            STS_DENIED: '角色已经存在，但信任策略不允许平台 AssumeRole。请让云管理员修正信任策略后重试。',
            STS_SERVICE_UNAVAILABLE: '暂时还没有验证到角色。请先完成下方角色配置；如果已经完成，请稍等片刻后重试。',
            STS_THROTTLED: '云厂商正在限制 STS 请求频率，请稍候片刻再重试。',
            STS_NETWORK_ERROR: '平台暂时无法访问云厂商 STS 地址，请保留当前授权并在网络恢复后重试。',
            PERMISSION_PROBE_FAILED: '角色信任关系已验证，但所需权限未通过检查。请确认已添加指引中的两项策略，然后重试。',
            TENCENT_TAT_POLICY_MISSING: '未检测到 QcloudTATFullAccess。请在腾讯云 CAM 为该角色关联此策略后重试。',
            CONNECTION_NAME_ALREADY_EXISTS: '云账号授权已经验证通过，但该连接器名称已被使用。请返回并更换名称。',
            AGENT_VERSION_UNSUPPORTED: '当前 Ontoxd 版本不支持云角色自动绑定，请升级到 v1.0.137 或更高版本后重试。',
            generic: '授权验证失败。请重新打开云端配置，确认角色与信任策略后再次尝试。',
          },
          changeConnectionName: '返回修改名称',
          continue: '继续授权',
          close: '关闭',
          done: '完成',
          openAgentSettings: '前往 Ontoxd 设置',
        },
        deviceAuth: {
          connecting: '连接中...',
          waiting: '等待授权...',
          expiresIn: '{{minutes}} 分钟后过期',
          userCode: '授权码',
          copy: '复制',
          copied: '已复制',
          openVerifyUrl: '打开授权页面',
          success: '连接成功！',
          successDesc: '现在可以关闭此对话框。',
          noConnections: '暂无可用连接',
          selectConnection: '选择一个连接',
          continue: '继续',
          cancel: '取消',
          done: '完成',
          expired: '授权已过期，请重试。',
          rejected: '授权被拒绝。',
        },
        oauthCallback: {
          loading: '正在完成授权...',
          success: '授权成功！即将跳转...',
          error: '授权失败，请重试。',
          back: '返回',
        },
        detailModal: {
          credentials: '需要凭证',
          credentialsHint: '此连接器需要身份验证。配置字段将在设置过程中显示。',
          capabilities: '包含能力',
          configure: '配置连接',
          secureCloudAuthorization: '安全授权云账号',
          cloudStorage: '云端存储',
          cloudStorageDesc: '凭证加密存储在平台，无需安装 Agent',
          knoxdLocal: '本地存储',
          knoxdLocalDesc: '仅在所选 {{daemon}} 节点保存角色引用；STS 临时凭证只驻留内存。',
          aliyun: {
            resourceDiscovery: '资源发现',
            resourceDiscoveryDesc: '查询 ECS 实例、磁盘、网络等阿里云资源',
            remoteExecution: '远程执行',
            remoteExecutionDesc: '通过云助手在 ECS 实例上执行命令',
          },
          aws: {
            resourceDiscovery: '资源发现',
            resourceDiscoveryDesc: '查询 EC2 实例、S3、RDS 等 AWS 资源',
            remoteExecution: '远程执行',
            remoteExecutionDesc: '通过 SSM Run Command 在 EC2 实例上执行命令',
          },
          tencent: {
            resourceDiscovery: '资源发现',
            resourceDiscoveryDesc: '查询 CVM 实例、VPC、COS 等腾讯云资源',
            remoteExecution: '远程执行',
            remoteExecutionDesc: '通过自动化助手 TAT 在 CVM 实例上执行命令',
          },
          volcengine: {
            resourceDiscovery: '资源发现',
            resourceDiscoveryDesc: '查询 ECS 实例、VPC 等火山引擎资源',
            remoteExecution: '远程执行',
            remoteExecutionDesc: '通过云助手在 ECS 实例上执行命令',
          },
        },
        editModal: {
          type: '类型',
          status: '状态',
          created: '创建时间',
          lastUsed: '最近使用',
          connectionName: '连接名称',
          credentials: '凭证',
          credentialMode: '凭证模式',
          knoxdLocal: '本地存储',
          credentialModeReadonly: '凭证模式创建后不可更改',
          cloudAuthorizationTitle: '云账号授权',
          cloudAuthorizationDescription: '重新执行角色引导与验证，验证通过后直接更新当前连接，不会重复创建连接。',
          reauthorizeCloudAccount: '重新授权并验证',
          reauthorizingCloudAccount: '正在准备授权…',
          reauthorizeLoading: '正在加载当前连接信息…',
          reauthorizeNeedsAccountId: '请先填写云账号 ID，再重新授权。',
          reauthorizeNeedsOnlineAgent: '请先选择在线 Ontoxd，再重新授权。',
		  replaceCloudRole: '重新配置角色',
		  replacingCloudRole: '正在准备角色配置…',
		  completeCloudAccount: '补充账号信息并授权',
		  agentUpgradeRequired: '当前 Ontoxd 版本不支持自动云授权，请升级到 v1.0.137 或更高版本。',
		  openAgentList: '前往 Ontoxd 列表',
		  completingCloudAccount: '正在准备账号配置…',
          cliBindCommand: 'CLI 绑定命令',
          credentialCommandHint: '请在已绑定的 {{daemon}} 节点运行此命令。首次执行会初始化凭证，重复执行会更新凭证。',
          bindAgent: '绑定 Agent',
          noAgent: '未绑定',
          agentOfflineUnavailable: '离线，无法选择',
          loadingCredentials: '加载凭证中...',
          shareWithTeam: '共享给团队',
          teamDescription: '组织内所有成员都可以使用此连接',
          personalDescription: '个人连接：仅你可以使用',
          save: '保存更改',
          saving: '保存中...',
          delete: '删除连接',
          deleting: '删除中...',
          confirmTitle: '确定删除此连接？',
          confirmMessage: '确定要删除 "{{name}}" 吗？',
          localRoleCleanupWarning: '还必须在已绑定的 {{daemon}} 节点清理此连接的本地 RoleArn 或 RoleTrn 绑定；删除云连接不会自动删除本地 Role 绑定。',
          bundleWarning: '这是组合连接 — 所有相关连接（发现 + 执行）都将被删除。',
          cancel: '取消',
          confirmDelete: '删除',
        },
        toggleStatus: '{{name}} — 切换状态',
      },
      security: {
        title: '命令策略',
        description: '管理允许和阻止的命令。未列出的命令需要审批。',
        nav: '安全策略',
        orgTab: '组织策略',
        orgTabDesc: '组织级命令允许/阻止规则。所有成员可查看，管理员可编辑。',
        myTab: '我的授权',
        myTabDesc: '你已永久授权的命令。移除后下次执行需要审批。',
        readonlyNotice: '仅管理员可编辑组织策略。',
        columnCommand: '命令',
        columnPolicy: '策略',
        columnAddedBy: '添加人',
        columnAuthorized: '授权时间',
        allow: '允许',
        block: '阻止',
        addPlaceholder: '例如 docker rm, systemctl stop',
        addCommand: '+ 添加命令',
        empty: '暂无策略配置。',
        searchPlaceholder: '过滤命令...',
        noResults: '没有匹配的命令。',
        tip: '`{cmd}` 匹配所有命令。`{sub}` 仅匹配该子命令。子命令规则优先于基础命令规则。',
        myTip: '在此添加命令可跳过审批提示。移除后下次执行需要审批。组织级策略始终优先。',
        addSuccess: '命令已添加',
        addError: '添加命令失败',
        removeSuccess: '命令已移除',
        removeError: '移除命令失败',
      },
      activity: {
        title: '活动日志',
        subtitle: '组织内的命令执行和文件操作记录',
        empty: '暂无活动记录',
        emptyHint: '通过连接器执行命令后，活动记录将显示在这里',
        error: '加载活动日志失败',
        retry: '重试',
        filter: {
          all: '全部',
          tool: '工具',
          status: '状态',
          allTools: '所有工具',
          allStatuses: '所有状态',
          allTime: '全部时间',
          lastHour: '最近 1 小时',
          last24h: '最近 24 小时',
          last7d: '最近 7 天',
          last30d: '最近 30 天',
          custom: '自定义范围',
          searchPlaceholder: '搜索命令、主机...',
        },
        col: {
          time: '时间',
          action: '操作',
          host: '主机',
          channel: '通道',
          status: '状态',
          duration: '耗时',
        },
        detail: {
          user: '用户',
          tool: '工具',
          channel: '通道',
          host: '主机',
          exitCode: '退出码',
          duration: '耗时',
          command: '命令',
          filePath: '文件路径',
          output: '输出',
          outputTruncated: '(已截断)',
          error: '错误',
        },
        batch: {
          tooltipSingle: '单次执行',
          tooltipBatch: '批量操作',
          filterLabel: '批次',
        },
        pagination: {
          showing: '显示 {{from}}-{{to}} / 共 {{total}} 条',
          total: '共 {{total}} 条',
          prev: '上一页',
          next: '下一页',
        },
      },
    },
    orgBinding: {
      title: '设置你的组织',
      subtitle: '创建新组织或加入已有组织以开始使用。',
      createLabel: '创建组织',
      joinLabel: '加入组织',
    },
    joinCode: {
      title: '加入组织',
      subtitle: '输入团队管理员分享的邀请码。',
      codeLabel: '邀请码',
      placeholder: '输入邀请码',
      submit: '继续',
      invalidOrExpired: '邀请码无效或已过期。',
      memberLimitReached: '该组织的成员数已达上限。',
      back: '← 返回选择',
      noCode: '没有邀请码？',
    },
    inviteCode: {
      title: '创建组织',
      subtitle: '输入邀请码并设置你的组织信息。',
      codeLabel: '邀请码',
      placeholder: '输入邀请码',
      orgNameLabel: '组织名称',
      orgNamePlaceholder: '例如：XX科技',
      submit: '创建组织',
      invalidOrExpired: '邀请码无效或已过期。',
      memberLimitReached: '该组织的成员数已达上限。',
      back: '← 返回选择',
      noCode: '没有邀请码？',
    },
    accessApplication: {
      trigger: '申请访问',
      title: '申请访问',
      subtitle: '填写信息，我们会审核你的申请。',
      workEmail: '工作邮箱',
      workEmailPlaceholder: 'you@company.com',
      fullName: '姓名',
      fullNamePlaceholder: '你的姓名',
      companyName: '公司或团队',
      companyNamePlaceholder: '你的公司或团队名称',
      role: '职位',
      rolePlaceholder: '选择你的职位',
      roleOptions: {
        itOperations: 'IT 运维',
        platformEngineering: '平台工程',
        sre: 'SRE',
        infrastructure: '基础设施',
        engineeringManagement: '工程管理',
        other: '其他',
      },
      problem: '你希望用 {{brand}} 解决什么问题？',
      problemPlaceholder: '告诉我们你面临的基础设施挑战或运维问题。',
      teamSize: '团队规模（选填）',
      teamSizePlaceholder: '选择团队规模',
      submit: '提交申请',
      submitting: '提交中...',
      successTitle: '申请已提交',
      successMessage: '我们会审核你的申请，通过后会将邀请码发送到你的邮箱。',
      checkInbox: '请在 24 小时内查看邮箱。',
      rateLimited: '请求过于频繁，请稍后再试。',
      genericError: '出了点问题，请重试。',
      required: '此项为必填。',
      invalidEmail: '请输入有效的工作邮箱。',
      problemMinLength: '至少需要 20 个字符。',
      close: '关闭',
    },
    invite: {
      loading: '加载邀请信息...',
      joining: '正在加入组织...',
      joinOrg: '加入 {{orgName}}',
      invitedBy: '{{name}} 邀请你加入',
      invitedToJoin: '你被邀请加入',
      asRole: '你将以 {{role}} 身份加入',
      expired: '此邀请已过期。',
      invalid: '此邀请链接无效。',
      used: '此邀请已被使用。',
      error: '加载邀请详情失败。',
      acceptError: '接受邀请失败，请重试。',
      loginToJoin: '登录以接受此邀请',
      email: '邮箱',
      password: '密码',
      name: '姓名',
      login: '登录并加入',
      noAccount: '还没有账号？',
      createAccount: '创建账号',
      haveAccount: '已有账号？',
      signIn: '登录',
      register: '注册并加入',
      registering: '正在创建账号...',
      loggingIn: '正在登录...',
      confirmJoin: '加入组织',
      backToLogin: '返回登录',
      passwordRules: '至少12个字符，字母、数字、符号至少包含两种',
      confirmPassword: '确认密码',
    },
    onboarding: {
      welcomeTitle: '欢迎加入 {{brand}}',
      welcomeSubtitle: '你的 AI 基础设施运维团队已就绪。',
      ctaButton: '开始设置',
      ctaLoading: '正在准备...',
      progressTitle: '引导设置',
      agentCaptain: '任务协调与团队调度',
      agentCollector: '从服务器采集基础设施数据',
      agentSupervisor: '校验数据完整性与一致性',
      agentArchitect: '分析架构与追踪依赖关系',
      agentChronicler: '将发现记录到知识图谱',
      agentCoordinator: '管理工作流与任务调度',
      step1Title: '认识你的 AI 团队',
      step2Title: '连接服务器',
      step2Desc: '在右侧 Chat 中告诉 Captain 你的服务器信息。',
      step3Title: '生成摸底报告',
      step3Desc: '你的 AI 团队将分析你的基础设施。',
      step3Analyzing: 'AI 团队正在分析你的基础设施...',
      step3Complete: '报告已就绪',
      viewReport: '查看完整报告',
      completed: 'Onboarding 完成！',
      skip: '跳过引导',
      continueGuide: '继续',
    },
    structured: {
      taskRun: {
        backgroundExecution: '后台任务',
        loadingLatest: '正在获取最新状态',
      },
      tool: {
        submitted: '已提交',
        running: '执行中...',
        debugRunning: '运行中',
        completed: '已完成',
        error: '出错',
        showOutput: '查看输出',
        hideOutput: '收起输出',
        loadingOutput: '加载输出中...',
        retry: '重试',
        noOutput: '无输出',
        noResults: '无结果',
        emptyResult: '结果为空',
        outputLargeSlow: '输出较大，加载可能较慢',
      },
      toolLibrary: {
        common: { yes: '是', no: '否' },
        status: {
          submitted: '已提交', running: '执行中', cancelling: '取消中', terminating: '终止确认中',
          succeeded: '执行成功', partial: '部分成功', failed: '执行失败', cancelled: '已取消', timedOut: '执行超时', unknown: '状态未知',
        },
        connector: {
          ssh: 'SSH', teleport: 'Teleport', aliyun: '阿里云助手', aws_ssm: 'AWS SSM', tencent: '腾讯云助手', volcengine: '火山引擎',
        },
        action: { read: '只读', change: '变更', delete: '删除' },
        target: { default: '目标', numbered: '目标 {{number}}', title: '目标' },
        duration: {
          label: '耗时 {{duration}}', milliseconds: '{{count}} 毫秒', seconds: '{{count}} 秒', minutes: '{{count}} 分钟', minutesSeconds: '{{minutes}} 分 {{seconds}} 秒',
        },
        array: { showRemaining: '查看其余 {{count}} 项' },
        arguments: { title: '输入参数', provided: '已提供' },
        logs: { title: '运行日志', stdout: '标准输出', stderr: '标准错误', errorCode: '错误码', errorStage: '错误阶段', diagnostic: '诊断信息', retryable: '重试建议', retryableYes: '可重试', retryableNo: '不可重试', truncated: '日志已截断' },
        schema: { input: '输入参数', output: '输出参数', required: '必填' },
        search: { title: '可用工具', empty: '没有匹配的工具' },
        describe: { title: '工具详情', connectors: '兼容连接器' },
        tool: { defaultTitle: '运维工具' },
        prepare: { title: '执行前确认' },
        execution: {
          running: '工具执行中', submitted: '工具执行已提交', title: '工具执行',
          counts: '共 {{targetCount}} 个目标 · 成功 {{successCount}} · 失败 {{failedCount}}',
          cancel: '取消执行', confirmCancel: '确认取消', keepRunning: '继续执行',
          cancelConfirmation: '已成功的目标会保留结果；运行中的目标将尽力终止；等待中的目标会取消。',
          prepareRetry: '重试失败目标', confirmPrepareRetry: '确认重试', cancelPrepareRetry: '暂不重试',
          retryConfirmation: '只会为失败目标准备重试；仍在终止确认中的目标不会重试。',
          retryPrepared: '重试已提交。',
          terminatingHint: '正在等待运行中的目标停止',
        },
        output: { title: '输出' },
        error: { title: '工具执行失败' },
        maintenance: {
          actionTitles: { createDraft: '工具草稿已创建', findDraft: '找到工具草稿', getDraft: '工具草稿', updateDraft: '工具草稿已更新', deleteDraft: '工具草稿已删除', publish: '工具已发布', enable: '工具已启用', disable: '工具已停用', listVersions: '版本历史' },
          runningActions: { createDraft: '正在创建工具草稿', findDraft: '正在查找工具草稿', getDraft: '正在读取工具草稿', updateDraft: '正在更新工具草稿', deleteDraft: '正在删除工具草稿', publish: '正在发布工具', enable: '正在启用工具', disable: '正在停用工具', listVersions: '正在读取版本历史', debug: '正在调试工具', default: '正在处理工具' },
          debug: { submitted: '调试已提交', running: '调试运行中', succeeded: '调试成功', failed: '调试失败', default: '工具调试' },
          schema: { parameter: '参数', type: '类型', required: '必填', defaultValue: '默认值', description: '说明' },
          runtime: { title: '运行配置', language: '语言', defaultInterpreter: '默认解释器', allowedInterpreters: '可用解释器', timeout: '超时', seconds: '{{count}} 秒' },
          errorTitle: '工具操作失败', completedTitle: '工具操作已完成', revision: '修订 {{revision}}', executionId: '执行 ID', status: '状态', viewPublishedDetails: '查看已发布详情', viewVersionHistory: '查看版本历史',
        },
      },
      error: {
        title: '错误',
        insufficientCredits: {
          title: '余额不足',
          adminDescription: '当前组织余额不足，充值后即可继续使用 AI。',
          memberDescription: '当前组织余额不足，请联系管理员充值。',
          recharge: '立即充值',
        },
      },
      askHuman: {
        waitingForInput: '等待输入...',
        submitted: '已提交回复',
        expired: '已超时',
        answer: '回答',
        question: '问题',
        other: '其他',
        typeYourAnswer: '输入你的回答...',
        submitting: '提交中...',
        submit: '提交',
        selectPlaceholder: '请选择...',
        previous: '上一步',
        next: '下一步',
      },
      approval: {
        approved: '已批准',
        rejected: '已拒绝',
        expired: '审批超时',
        expiredHint: '命令未执行',
        alwaysAllow: '始终允许',
        needsYourAuth: '需要你的授权',
        waitingFor: '等待 {{name}} 审批',
        processing: '处理中...',
        reject: '拒绝',
        allowOnce: '允许执行',
        approvalFailed: '审批失败',
        rejectionFailed: '拒绝操作失败',
        waitingForApproval: '等待审批中...',
        alwaysAllowScope: '始终允许将授权范围',
        complexCommandNoAlwaysAllow: '此命令使用了复杂 shell 特性（如命令替换），无法自动授权，每次执行都需要手动审批。',
      },
      dispatch: {
        dispatchedTo: '已派发至',
      },
      threadAck: {
        processing: '正在处理中...',
        completed: '已完成',
        error: '执行出错',
      },
      systemNotice: {
        sessionCleared: '对话已重置，重新开始。',
        sessionStopped: '已停止当前处理。',
        sessionStoppedIdle: '当前没有正在执行的任务。',
        processingInterrupted: '处理已中断。',
        allStopped: '已停止：{{names}}',
        allStoppedIdle: '当前没有正在执行的任务。',
        allCleared: '所有对话已重置。',
        personaCleared: '{{name}} 的对话已重置。',
      },
      botControl: {
        stopTooltip: '停止所有正在执行的任务',
        clearSession: '清除对话',
        clearAll: '清除全部',
        confirmClear: '确定重置 {{name}} 的上下文吗？所有对话记录将被清除。',
        confirmStop: '确定停止 {{name}} 吗？如果正在执行则会被取消。',
        confirmBtn: '确定',
        cancelBtn: '取消',
      },
      nameSeparator: '、',
      search: {
        scopeThisRoom: '当前房间',
        scopeAllRooms: '所有房间',
        searchMessages: '搜索消息',
        searchInRoom: '在此房间内搜索',
        searchAllRooms: '搜索所有房间',
        noResults: '没有找到 <b>{{term}}</b> 的结果',
        noResultsHint: '请尝试其他关键词或检查拼写',
        thread: 'Thread',
        replyTo: '回复',
      },
      profile: {
        roleAdmin: '管理员',
        roleModerator: '协管员',
        roleMember: '成员',
        mention: '提及',
        stop: '停止',
        clear: '清除',
        aiBadge: 'AI',
        confirmClear: '确定重置 {{name}} 的上下文吗？所有对话记录将被清除。',
        confirmStop: '确定停止 {{name}} 吗？如果正在执行则会被取消。',
        confirmBtn: '确认',
        cancelBtn: '取消',
      },
      typing: {
        thinking: '思考中...',
        oneTyping: '<bold>{{name}}</bold> 正在输入...',
        twoTyping: '<bold>{{name1}}</bold> 和 <bold>{{name2}}</bold> 正在输入...',
        manyTyping: '<bold>{{name}}</bold> 和其他 {{count}} 人正在输入...',
      },
      timeline: {
        newMessages: '新消息',
        jumpToLatest: '跳到最新',
        noMessagesYet: '暂无消息',
        loadingMessages: '消息加载中',
	        unreadSeparator: '新消息',
        relativeTime: {
          justNow: '刚刚',
          minutesAgo: '{{n}}分钟前',
          hoursAgo: '{{n}}小时前',
          daysAgo: '{{n}}天前',
        },
        agentToolGroup: {
          done: '完成',
          tool: '个工具',
          tools: '个工具',
          agentPrompt: 'Agent 提示',
          close: '关闭',
        },
        suggestions: {
          title: '{{brand}} Team 已就绪',
          subtitle: '选择一个快速开始，或直接输入你的需求',
          baseline: {
            title: '摸底报告',
            desc: '全面了解当前系统状态',
            prompt: '对我的基础设施做一次资源发现摸底',
          },
          troubleshoot: {
            title: '排查一个生产问题',
            desc: '调查并解决生产环境异常',
            prompt: '帮我排查一个问题：',
          },
          change: {
            title: '执行一个运维变更',
            desc: '运行操作与配置变更',
            prompt: '帮我执行一个变更：',
          },
          capabilities: {
            title: '我能做哪些事？',
            desc: '了解团队能力范围',
            prompt: '介绍一下你们团队的能力，我能让你们帮我做哪些事？',
          },
        },
        inboxWelcome: {
          greetingMorning: '早上好, {{name}}',
          greetingAfternoon: '下午好, {{name}}',
          greetingEvening: '晚上好, {{name}}',
          subtitle: '你的 AI SRE 团队 — 发现、监控、排障、运维，一站式处理',
          alertSummary: {
            label: '告警摘要',
            prompt: '帮我总结当前告警',
          },
          healthCheck: {
            label: '系统巡检',
            prompt: '做一次系统健康检查',
          },
          troubleshoot: {
            label: '问题排查',
            prompt: '帮我排查一个问题：',
          },
          capabilities: {
            label: '了解能力',
            prompt: '你能帮我做什么？',
          },
        },
        knowledgeWelcome: {
          greetingMorning: '早上好',
          greetingAfternoon: '下午好',
          greetingEvening: '晚上好',
          subtitle: '查找、浏览和管理你有权限使用的知识',
          searchKnowledge: {
            label: '搜索知识',
            prompt: '搜索知识库中的相关信息',
          },
          uploadKnowledge: {
            label: '添加知识',
            prompt: '介绍一下怎么添加知识，包括有哪些存储方式和各自的基本操作步骤。',
          },
          recentChanges: {
            label: '最近更新',
            prompt: '最近添加或更新了哪些知识？',
          },
          capabilities: {
            label: '了解知识库',
            prompt: '知识库可以怎么用？',
          },
        },
      },
    },
    messageActions: {
      reply: '回复',
      copy: '复制',
      copied: '已复制',
      pin: '置顶',
      unpin: '取消置顶',
      cancelReply: '取消回复',
      cancelReplyAria: '取消回复',
    },
    plugins: {
      title: '插件', description: '查看当前工作空间可用的技能、工具和连接器。',
      skills: '技能', tools: { title: '工具', description: '查看已发布工具。', searchPlaceholder: '搜索名称、用途或维护者', scopeLabel: '范围', statusLabel: '状态', viewDetail: '查看详情', continueDraft: '继续编辑', enableWithAi: '通过 AI 启用', disableWithAi: '通过 AI 停用', createWithAi: '创建工具', creatingWithAi: '创建中…', launchingAi: '打开中…', modifyWithAi: '修改工具', draftVersion: '草稿', loadMore: '加载更多工具', empty: '没有找到符合当前条件的工具。', searchEmpty: '没有找到匹配的工具', maintainedEmpty: '你还没有维护已发布工具', draftEmpty: '暂无工具草稿', disabledEmpty: '暂无已停用工具', clearSearch: '清除搜索', viewAllTools: '查看全部工具', viewEnabledTools: '查看可用工具', total: '共 {{count}} 个工具', column: { tool: '工具', source: '来源', version: '版本', action: '执行动作', updatedAt: '最后更新时间', status: '状态', operation: '操作' }, scope: { all: '全部工具', maintained: '我维护的', draft: '草稿' }, status: { all: '全部状态', enabled: '可用', disabled: '已停用' } },
      connectors: '连接器', connectorsDescription: '查看连接状态并进入连接器管理。',
      searchPlaceholder: '搜索技能、工具或连接器', searchLabel: '搜索技能、工具或连接器', viewAll: '查看全部', viewSkill: '查看详情', skillVersion: '版本 {{version}}', viewConnector: '查看连接器', clearFilters: '清除筛选', retry: '重试', back: '返回', backToWorkspace: '返回工作空间', skillsLoading: '正在加载技能', toolsLoading: '工具加载中', connectorsLoading: '连接器加载中', skillsLoadError: '技能加载失败', toolsLoadError: '工具加载失败', connectorsLoadError: '连接器加载失败', skillsEmpty: '暂无可用技能', toolsEmpty: '暂无可用工具', connectorsEmpty: '暂无可用连接器', createSkill: '创建技能', createTool: '创建工具', configureConnector: '配置连接器', creatingSkill: '创建中…', creatingTool: '创建中…', configuringConnector: '打开中…',
      skillsSummary: '可复用的 AI 运维流程。', skillsUnavailable: '技能管理暂不可用', skillsUnavailableDescription: '受保护的技能管理真实入口交付后将在此开放。', connectorSummary: '用于访问目标环境的能力。', connectorStatus: { active: '可用', inactive: '已停用', disabled: '已停用' }, searchEmpty: '没有找到符合条件的插件。', errorTitle: '页面加载失败', loading: '加载中', enable: '启用', disable: '停用', maintainers: '维护者', maintainerSummary: '{{name}} 等 {{count}} 人', maintainerSeparator: '、', closePanel: '关闭插件面板', workspaceUnavailable: '工作空间不可用', workspaceUnavailableDescription: '当前组织上下文无权访问此工作空间。',
      toggle: { confirmTitle: '确认变更状态', confirmDescription: '确认变更“{{name}}”的可用状态？', confirm: '确认', cancel: '取消' },
      status: { enabled: '可用', disabled: '已停用' }, officialTeam: 'Ontox 官方团队',
      source: { official: '官方', organization: '组织' },
      action: { read: '只读检查', write: '变更操作', change: '变更操作', execute: '执行命令', delete: '删除操作' },
      impact: { read: '不会修改目标配置。', write: '可能修改目标环境。', change: '可能修改目标环境。', execute: '会在选定目标上执行命令。', delete: '可能删除选定目标上的数据。' },
      detail: { useInAi: '在 AI 中使用', enableInAi: '通过 AI 启用', modifyInAi: '修改工具', toolInformation: '工具信息', purpose: '用途与适用场景', inputSchema: '输入参数', outputSchema: '输出参数', implementation: '实现脚本', copyScript: '复制脚本', scriptCopied: '脚本已复制', recentExecutions: '最近执行', viewAllExecutions: '查看全部执行历史', emptyExecutions: '暂无执行记录', versionHistory: '版本历史', emptyVersions: '暂无已发布版本', noReleaseNotes: '无版本说明', source: '来源', version: '版本', status: '状态', action: '执行动作', impact: '影响', scenarios: '适用场景', scenarioDescription: '当你需要{{summary}}时使用；执行类型为{{action}}。' },
      aiContext: { ready: '已带入工具，请发送开始', createTitle: '创建工具', dismiss: '关闭提示', backToDetail: '返回工具详情', backToList: '返回工具列表', prompt: { create: '请帮我创建一个运维工具。', use: '请使用工具“{{title}}”（工具 ID：{{id}}）。请先确认目标和参数，再生成执行计划。', enable: '请帮我启用工具“{{title}}”（工具 ID：{{id}}），先说明影响并等待我确认。', disable: '请帮我停用工具“{{title}}”（工具 ID：{{id}}），先说明影响并等待我确认。', maintain: '请继续维护工具“{{title}}”（工具 ID：{{id}}）的草稿，先读取当前草稿并说明可修改内容。' } },
      schema: { empty: '无参数', name: '参数名', type: '类型', requirement: '是否必填', description: '说明', default: '默认值', required: '必填', optional: '选填', enumSingle: '单选', enumMultiple: '多选' },
      history: { title: '我的执行历史', description: '仅展示你发起的执行记录。', toolDetail: '工具详情', succeeded: '成功', partially_succeeded: '部分成功', failed: '失败', running: '执行中', queued: '等待执行', submitted: '已提交', cancelling: '取消中', terminating: '取消中', cancelled: '已取消', timed_out: '执行超时', unknown: '状态未知', empty: '暂无执行历史。', time: '时间', version: '版本', targets: '目标数', result: '结果', duration: '耗时', action: '操作', viewResult: '查看结果', resultDetail: '执行结果', closeResult: '关闭结果', executionStatus: '状态', loadMore: '加载更多', cancelAction: '取消执行', cancellingAction: '取消中', compactCounts: '{{successCount}} 成功 · {{failedCount}} 失败', successCount: '成功 {{count}}', failedCount: '失败 {{count}}', retryFailed: '准备重试', retryPending: '准备中', retryPrepared: '已生成重试确认计划，包含 {{count}} 个失败目标。', targetResults: '目标结果', target: '目标', targetDuration: '目标耗时', stdoutPreview: '标准输出', stderrPreview: '标准错误', errorCode: '错误码', hiddenValue: '已隐藏' },
    },
    alerts: {
      rulesNewTitle: '新建过滤规则', rulesEditTitle: '编辑过滤规则', rulesCreateDescription: '创建一条 RCA 自动化过滤规则', rulesEditDescription: '修改现有的过滤匹配条件', rulesConditionSummary: '{{count}} 个条件（且）', rulesExtraConditions: '+{{count}} 个条件',
      title: '告警', subtitle: '告警中心', alertCount: '{{count}} 条告警', paginationSummary: '共 {{total}} 条 · 第 {{page}} / {{pages}} 页', pageSizeLabel: '每页条数', pageSize: '每页 {{count}} 条', page: '第', jumpToPage: '跳转页码', loading: '正在加载告警', empty: '暂无告警', filteredEmpty: '没有符合筛选条件的告警', error: '告警加载失败', unauthorized: '登录已失效，请重新登录', forbidden: '您没有查看告警的权限', retry: '重试', previous: '上一页', next: '下一页', unnamed: '未命名告警',
      filters: { keyword: '搜索告警名称、标签或注释', status: '告警状态', webhook: 'Webhook', searchSource: '搜索 Webhook 来源', clearSource: '清除 Webhook 来源', rcaStatus: 'RCA 状态', startsAfter: '开始', startsBefore: '结束', quickRange: '快捷时间', allTime: '全部时间', last1Hour: '近 1 小时', last24Hours: '近 24 小时', today: '今天', last7Days: '近 7 天', last30Days: '近 30 天', apply: '筛选', clear: '清除筛选', clearKeyword: '清除搜索', expand: '展开筛选', collapse: '收起筛选' },
      columns: { name: '告警名称', status: '状态', source: '来源', time: '时间', rca: 'RCA', actions: '操作' },
      status: { all: '全部状态', firing: '告警中', resolved: '已恢复' },
      rcaStatus: { all: '全部 RCA 状态', unevaluated: '未触发', unmatched: '未触发', triggering: '触发中', failed: '触发失败', triggered: '已触发' },
      webhooks: { all: '全部来源', deleted: '已删除 Webhook' },
      actions: { startRca: '开始 RCA', retryRca: '重试 RCA', triggering: '触发中', viewConversation: '查看对话', openingConversation: '正在打开对话', failed: 'RCA 启动失败', unauthorized: '登录已失效，请重新登录', forbidden: '您没有启动 RCA 的权限' },
      details: { loading: '正在加载告警详情', loadError: '告警详情加载失败', retry: '重试加载详情' },
      rules: { title: '过滤规则', description: '任一启用规则命中时触发 RCA', loading: '正在加载规则', empty: '暂无过滤规则', filteredEmpty: '没有匹配的过滤规则', loadError: '规则加载失败', unauthorized: '登录已失效，请重新登录', forbidden: '您没有管理过滤规则的权限', retry: '重试', new: '新建规则', edit: '编辑', delete: '删除', confirmDelete: '确认删除', cancelDelete: '取消', close: '关闭过滤规则', name: '规则名称（必填）', enabled: '启用规则', allConditions: '同时满足以下条件', addCondition: '添加条件', removeCondition: '删除条件', conditionPrefix: '条件 {{index}}', fieldLabel: '字段', labelKey: 'Label 名称', labelKeyPlaceholder: '例如 service', labelValueHint: 'Label 的值', labelValuePlaceholder: '例如 payments', conditionHint: '一条规则内的所有条件必须同时满足', operatorLabel: '运算符', value: '值', save: '保存规则', cancel: '取消编辑', saveError: '规则保存失败', updateError: '更新规则失败', deleteError: '删除规则失败', nameRequired: '请输入规则名称', labelKeyRequired: '请输入 Label 名称，例如 service', valueRequired: '请输入条件值', invalidRegex: '请输入有效的正则表达式', conditionSummary: '{{count}} 个条件', searchLabel: '搜索规则名称', statusFilterLabel: '启用状态', statusFilter: { all: '全部状态', enabled: '已启用', disabled: '已停用' }, paginationSummary: '共 {{total}} 条 · 第 {{page}} / {{pages}} 页', pageSizeLabel: '每页条数', pageSize: '每页 {{count}} 条', page: '第', jumpToPage: '跳转页码', previous: '上一页', next: '下一页', fields: { alertname: '告警名称', webhook_id: 'Webhook', status: '告警状态', starts_at: '开始时间', label: 'Label' }, operators: { equals: '等于', not_equals: '不等于', contains: '包含', matches: '正则匹配', exists: '存在', not_exists: '不存在' } },
    },
    aria: {
      loading: '加载中',
      connectors: '连接器',
      settings: '设置',
      helpCenter: '帮助中心',
      copy: '复制',
      close: '关闭',
      cancelReply: '取消回复',
      backToChat: '返回聊天',
      threadMessage: '话题消息',
    },
  },
};

/**
 * Knox i18n resource data for the `knox-aiops` namespace.
 * Exported so components can register them on the correct i18next instance.
 */
export const KNOX_RESOURCES = {
  en: KNOX_RESOURCES_EN['knox-aiops'],
  zh: KNOX_RESOURCES_ZH['knox-aiops'],
};

/**
 * Register Knox resources on a given i18next instance.
 */
export function addKnoxResourcesTo(i18n: { addResourceBundle: Function }) {
  i18n.addResourceBundle('en', 'knox-aiops', KNOX_RESOURCES_EN['knox-aiops'], true, true);
  i18n.addResourceBundle('zh-CN', 'knox-aiops', KNOX_RESOURCES_ZH['knox-aiops'], true, true);
  i18n.addResourceBundle('zh', 'knox-aiops', KNOX_RESOURCES_ZH['knox-aiops'], true, true);
}

/**
 * Module-level convenience: register on the imported i18next instance.
 */
export function addKnoxResources() {
  addKnoxResourcesTo(i18next);
}

// Initialize i18next with Knox resources.
i18next
  .use(initReactI18next)
  .init({
    lng: isCN() ? 'zh-CN' : 'en',
    fallbackLng: 'en',
    ns: ['knox-aiops'],
    defaultNS: 'knox-aiops',
    resources: {
      en: KNOX_RESOURCES_EN,
      'zh-CN': KNOX_RESOURCES_ZH,
      zh: KNOX_RESOURCES_ZH,
    },
    interpolation: {
      escapeValue: false,
      defaultVariables: { brand: isCN() ? 'Ontox' : 'Knox' },
    },
  })
  .then(() => restoreStoredLanguage());

/**
 * Restore language from localStorage if the user previously chose a non-default language.
 */
function restoreStoredLanguage() {
  const stored = localStorage.getItem('knox-language');
  if (stored && stored !== i18next.language) {
    i18next.changeLanguage(stored);
  }
}
